import React, { useRef } from 'react';
import styled, { keyframes, css, createGlobalStyle, ThemeProvider, StyleSheetManager } from 'styled-components';
import { compose, border, color, flexbox, grid, layout, position, shadow, space, typography, margin, maxHeight, system as system$g, width, height, padding, display, variant, maxWidth, fontSize as fontSize$1, borderBottom } from 'styled-system';
import { CheckBox, CheckCircleOutline, InfoOutline, ReportProblemOutlined, ErrorOutline, Rocket, Close, ChevronRight, ErrorIcon, Check, CheckBoxOutlineBlank, ChevronLeft, KeyboardArrowLeft, KeyboardArrowDown, MoreHoriz, ArrowForward, ArrowBack, RadioButtonUnchecked, RadioButtonChecked, Info, CheckCircle, Warning, ArrowUpward, ArrowDownward, KeyboardArrowUp } from '@sparkpost/matchbox-icons';
import { tokens } from '@sparkpost/design-tokens';
import { Transition } from 'react-transition-group';
import { omit as omit$1, pick as pick$1 } from '@styled-system/props';
import css$1 from '@styled-system/css';
import DayPicker from 'react-day-picker';
import FocusLock from 'react-focus-lock';
import ScrollLock, { TouchScrollable } from 'react-scrolllock';
import ReactDOM, { findDOMNode } from 'react-dom';
import copy from 'copy-to-clipboard';
import ResizeObserver from 'resize-observer-polyfill';
import PropTypes from 'prop-types';
import { createPropTypes } from '@styled-system/prop-types';
import { normalize } from 'styled-normalize';
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function _defineProperty$7(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toConsumableArray$3(arr) {
  return _arrayWithoutHoles$3(arr) || _iterableToArray$3(arr) || _unsupportedIterableToArray$t(arr) || _nonIterableSpread$3();
}
function _nonIterableSpread$3() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$t(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$t(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$t(o, minLen);
}
function _iterableToArray$3(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$3(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$t(arr);
}
function _arrayLikeToArray$t(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Group a list of items into sub groups based on the value of each item's groupingKey
 *
 * @param {Array} list List of items to group
 * @param {String} groupingKey Key to use for grouping each item by this value
 *
 * @example
 * const list = [{ name: 'a', section: 1 }, { name: 'b', section: 2 }, { name: 'c', section: 1 }]
 *
 * groupBy(list, 'section')
 *
 * // returns
 * {
 *   1: [ { name: 'a', section: 1 }, { name: 'c', section: 1 } ],
 *   2: [ { name: 'b', section: 2 } ]
 * }
 */

var groupBy = function groupBy(list, groupingKey) {
  return list.reduce(function (acc, item) {
    var index = item[groupingKey];
    return Object.assign(Object.assign({}, acc), _defineProperty$7({}, index, [].concat(_toConsumableArray$3(acc[index] || []), [item])));
  }, {});
};
var groupByValues = function groupByValues(list, groupingKey) {
  return Object.values(groupBy(list, groupingKey));
};
/**
 * Filters by an array of actions by the visible key
 * @param {Array} actions - Array of actions
 *
 * @example
 * filterVisible([{ name: 'a', visible: true }, { name: 'b', visible: false }, { name: 'c' }])
 *
 * // returns
 * [{ name: 'a' }, { name: 'c' }]
 */

var filterByVisible = function filterByVisible(actions) {
  if (!actions) {
    return [];
  }
  return actions.reduce(function (acc, _a) {
    var _a$visible = _a.visible,
      visible = _a$visible === void 0 ? true : _a$visible,
      action = __rest(_a, ["visible"]);
    if (visible) {
      acc.push(action);
    }
    return acc;
  }, []);
};

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Omits styled system props from an object
 * @param {Object} props
 * @param {Array} names
 *
 * @example
 *  import { margin } from 'styled-system';
 *
 * omit({ my: '100', className: 'classs' }, margin.propNames)
 *  > { className: 'class' }
 */
function omit(props, names) {
  var next = {};
  var regex = new RegExp("^(".concat(names.join('|'), ")$"));
  for (var key in props) {
    if (regex.test(key)) continue;
    next[key] = props[key];
  }
  return next;
}
/**
 * Picks styled system props from an object
 * @param {Object} props
 * @param {Array} names
 *
 * @example
 *  import { margin } from 'styled-system';
 *
 * pick({ my: '100', py: '100' }, margin.propNames)
 *  > { my: '100' }
 */

function pick(props, names) {
  var next = {};
  var regex = new RegExp("^(".concat(names.join('|'), ")$"));
  for (var key in props) {
    if (!regex.test(key)) continue;
    next[key] = props[key];
  }
  return next;
}
/**
 * Returns a configuration object for styled-components with omitted props, to be used with .withConfig
 * @param {Array} arr
 * @param {Object} config
 *
 * @see https://styled-components.com/docs/api#shouldforwardprop
 */

function clean() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return Object.assign({
    shouldForwardProp: function shouldForwardProp(prop, defaultFn) {
      return !arr.includes(prop) && defaultFn(prop);
    }
  }, config);
}
function _toConsumableArray$2(arr) {
  return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$s(arr) || _nonIterableSpread$2();
}
function _nonIterableSpread$2() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$s(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$s(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$s(o, minLen);
}
function _iterableToArray$2(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$2(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$s(arr);
}
function _arrayLikeToArray$s(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _templateObject$1p() {
  var data = _taggedTemplateLiteral$1p(["\n  ", "\n  ", "\n"]);
  _templateObject$1p = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1p(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$f = compose(border, color, flexbox, grid, layout, position, shadow, space, typography);
var truncate$1 = function truncate(props) {
  if (props.truncate) {
    return "\n      overflow: hidden;\n      text-overflow: ellipsis;\n      white-space: nowrap;\n    ";
  }
  return '';
};
var Box = styled.div.withConfig(clean([].concat(_toConsumableArray$2(system$f.propNames), ['truncate'])))(_templateObject$1p(), system$f, truncate$1);
Box.displayName = 'Box';
function _templateObject$1o() {
  var data = _taggedTemplateLiteral$1o(["\n  a,\n  a:visited {\n    color: ", ";\n  }\n"]);
  _templateObject$1o = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1o(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledText$1 = styled(Box)(_templateObject$1o(), tokens.color_gray_700);
function HelpText(props) {
  var id = props.id,
    children = props.children,
    _props$ml = props.ml,
    ml = _props$ml === void 0 ? '0' : _props$ml,
    _props$mt = props.mt,
    mt = _props$mt === void 0 ? '100' : _props$mt;
  return /*#__PURE__*/React.createElement(StyledText$1, {
    id: id,
    fontSize: "200",
    lineHeight: "200",
    m: "0",
    ml: ml,
    mt: mt,
    color: "gray.700"
  }, children);
}
HelpText.displayName = 'HelpText';
function _templateObject$1n() {
  var data = _taggedTemplateLiteral$1n(["\n  ", "\n  ", "\n"]);
  _templateObject$1n = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1n(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$e = compose(color, space, typography);
var Styledlink = styled.a(_templateObject$1n(), system$e, function (props) {
  if (props.$disabled) {
    return "\n        &, &:visited {\n          opacity: 0.6;\n          color: ".concat(props.theme.colors.gray['900'], ";\n          pointer-events: none;\n        }\n    ");
  }
});
/**
 * UnstyledLink component
 * @see https://design.sparkpost.com/components/unstyledlink/api
 */

var UnstyledLink = /*#__PURE__*/React.forwardRef(function UnstyledLink(props, ref) {
  var children = props.children,
    to = props.to,
    title = props.title,
    Component = props.Component,
    component = props.component,
    as = props.as,
    external = props.external,
    onClick = props.onClick,
    role = props.role,
    disabled = props.disabled,
    tabIndex = props.tabIndex,
    rest = __rest(props, ["children", "to", "title", "Component", "component", "as", "external", "onClick", "role", "disabled", "tabIndex"]);
  var WrapperComponent = as || component || Component;
  var linkTitle = external && !title ? 'Opens in a new tab' : title;
  var linkRole = role ? role : !!onClick ? 'button' : null;
  var disabledAttributes = {
    'aria-disabled': disabled,
    disabled: disabled,
    tabIndex: disabled ? -1 : tabIndex,
    onClick: disabled ? function () {
      return false;
    } : onClick
  };
  if (to && !WrapperComponent) {
    return /*#__PURE__*/React.createElement(Styledlink, Object.assign({
      href: typeof to === 'string' ? to : null,
      target: external ? '_blank' : '',
      rel: external ? 'noopener noreferrer' : '',
      title: linkTitle,
      onClick: onClick,
      role: role,
      ref: ref
    }, rest, disabledAttributes), children);
  }
  if (WrapperComponent) {
    return /*#__PURE__*/React.createElement(Styledlink, Object.assign({
      as: WrapperComponent,
      onClick: onClick,
      ref: ref,
      role: role,
      title: linkTitle,
      to: to
    }, rest, disabledAttributes), children);
  }
  return /*#__PURE__*/React.createElement(Styledlink, Object.assign({
    title: linkTitle,
    role: linkRole,
    onClick: onClick,
    ref: ref
  }, rest, disabledAttributes), children);
});
UnstyledLink.displayName = 'UnstyledLink';
function linkFrom(_a, key) {
  var content = _a.content,
    children = _a.children,
    action = __rest(_a, ["content", "children"]);
  return /*#__PURE__*/React.createElement(UnstyledLink, Object.assign({
    key: key
  }, action), content, children);
}
var buttonReset = "\n  border: none;\n  margin: 0;\n  padding: 0;\n  overflow: visible;\n  width: auto;\n  background: transparent;\n  color: inherit;\n  font: inherit;\n  line-height: normal;\n  -webkit-font-smoothing: inherit;\n  -moz-osx-font-smoothing: inherit;\n  -webkit-appearance: none;\n\n  &::-moz-focus-inner {\n    border: 0;\n    padding: 0;\n  }\n";
var visuallyHidden = "\n  border: 0 !important;\n  clip: rect(1px, 1px, 1px, 1px) !important;\n  clip-path: inset(50%) !important;\n  height: 1px !important;\n  margin: -1px !important;\n  overflow: hidden !important;\n  padding: 0 !important;\n  position: absolute !important;\n  width: 1px !important;\n  word-wrap: normal !important;\n";
/**
 * Creates focus styles on an :after pseudo-element.
 * @param string Color of the focus ring. Defaults to color_blue_700.
 * @param modifier CSS selector to target the pseudo-element with the ring, defaults to '&:focus'
 * @param offset Pixel offset of the focus ring. Defaults to '3px'.
 * @param radius Border radius of the focus ring. Defaults to borderRadius_200.
 *
 * @example
 * const Styled = styled.div`
 *   ${focusOutline({ color: 'gray', modifier: '&:focus-within' })}
 * `;
 */

var focusOutline = function focusOutline() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? tokens.color_blue_700 : _ref$color,
    _ref$modifier = _ref.modifier,
    modifier = _ref$modifier === void 0 ? '&:focus' : _ref$modifier,
    _ref$offset = _ref.offset,
    offset = _ref$offset === void 0 ? '3px' : _ref$offset,
    _ref$radius = _ref.radius,
    radius = _ref$radius === void 0 ? tokens.borderRadius_200 : _ref$radius;
  return "\n  position: relative;\n  outline: none;\n\n  &:after {\n    position: absolute;\n    content: \"\";\n    display: block;\n    top: -".concat(offset, ";\n    left: -").concat(offset, ";\n    right: -").concat(offset, ";\n    bottom: -").concat(offset, ";\n    transition: box-shadow ").concat(tokens.motionDuration_fast, ";\n    border-radius: ").concat(radius, ";\n    box-shadow: none;\n    pointer-events: none;\n  } \n\n  ").concat(modifier, ":after {\n    z-index: ").concat(tokens.zIndex_default, ";\n    box-shadow: 0 0 0 2px ").concat(color, ";\n  }\n");
};
function _templateObject3$n() {
  var data = _taggedTemplateLiteral$1m(["\n  ", "\n  ", "\n  &,\n  &:visited {\n    text-decoration: none;\n    color: ", ";\n    background: ", ";\n  }\n  &:disabled {\n    opacity: 0.6;\n    pointer-events: none;\n  }\n"]);
  _templateObject3$n = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$E() {
  var data = _taggedTemplateLiteral$1m(["\n  ", "\n  &,\n  &:visited {\n    text-decoration: none;\n    color: ", ";\n    background: ", ";\n  }\n  ", "\n"]);
  _templateObject2$E = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1m() {
  var data = _taggedTemplateLiteral$1m(["\n  border-top: ", ";\n  padding-bottom: ", ";\n  padding-top: ", ";\n\n  &:first-child {\n    border: none;\n    padding-top: ", ";\n  }\n\n  &:last-child {\n    padding-bottom: ", ";\n  }\n"]);
  _templateObject$1m = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1m(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledSection$1 = styled(Box)(_templateObject$1m(), function (props) {
  return props.theme.borders['300'];
}, tokens.spacing_200, tokens.spacing_200, tokens.spacing_0, tokens.spacing_0);
var shared = "\n  display: block;\n  width: 100%;\n  padding: ".concat(tokens.spacing_200, " ").concat(tokens.spacing_300, ";\n  transition: ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n  transition-property: color, background;\n  cursor: pointer;\n  font-size: ").concat(tokens.fontSize_300, ";\n  line-height: ").concat(tokens.lineHeight_300, ";\n  text-align: left;\n\n  &:hover,\n  &:focus {\n    outline: none;\n    color: ").concat(tokens.color_gray_900, ";\n    background: ").concat(tokens.color_blue_100, ";\n  }\n");
var StyledLink$4 = styled(UnstyledLink)(_templateObject2$E(), shared, function (props) {
  return props.$highlighted ? tokens.color_blue_700 : tokens.color_gray_900;
}, function (props) {
  return props.$highlighted ? tokens.color_blue_200 : 'none';
}, function (props) {
  return props.$disabled ? "\n      opacity: 0.6;\n      pointer-events: none;\n    " : '';
});
var StyledButton$4 = styled.button(_templateObject3$n(), buttonReset, shared, function (props) {
  return props.$highlighted ? tokens.color_blue_700 : tokens.color_gray_900;
}, function (props) {
  return props.$highlighted ? tokens.color_blue_200 : 'none';
});
function Content$4(props) {
  var content = props.content,
    children = props.children,
    selected = props.selected,
    helpText = props.helpText;
  return /*#__PURE__*/React.createElement(Box, {
    as: "span",
    alignItems: "flex-start",
    display: "flex"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    flex: "1",
    fontSize: "300",
    lineHeight: "300"
  }, content || children), selected && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    color: "blue.700"
  }, /*#__PURE__*/React.createElement(CheckBox, {
    size: 20
  })), helpText && /*#__PURE__*/React.createElement(HelpText, null, helpText));
} // TODO fix this any
// eslint-disable-next-line @typescript-eslint/no-explicit-any

var ButtonAction = /*#__PURE__*/React.forwardRef(function ButtonAction(props, userRef) {
  var content = props.content,
    children = props.children,
    disabled = props.disabled,
    helpText = props.helpText,
    selected = props.selected,
    highlighted = props.highlighted,
    action = __rest(props, ["content", "children", "disabled", "helpText", "selected", "highlighted"]);
  return /*#__PURE__*/React.createElement(StyledButton$4, Object.assign({
    type: "button",
    disabled: disabled,
    role: "menuitem",
    tabIndex: -1,
    "$highlighted": highlighted,
    ref: userRef
  }, action), /*#__PURE__*/React.createElement(Content$4, {
    content: content,
    helpText: helpText,
    selected: selected
  }, children));
}); // TODO fix this any
// eslint-disable-next-line @typescript-eslint/no-explicit-any

var LinkAction = /*#__PURE__*/React.forwardRef(function LinkAction(props, userRef) {
  var content = props.content,
    children = props.children,
    disabled = props.disabled,
    helpText = props.helpText,
    selected = props.selected,
    tabIndex = props.tabIndex,
    onClick = props.onClick,
    highlighted = props.highlighted,
    action = __rest(props, ["content", "children", "disabled", "helpText", "selected", "tabIndex", "onClick", "highlighted"]);
  var disabledAttributes = {
    'aria-disabled': disabled,
    disabled: disabled,
    tabIndex: disabled ? -1 : tabIndex,
    onClick: disabled ? function () {
      return false;
    } : onClick
  };
  return /*#__PURE__*/React.createElement(StyledLink$4, Object.assign({
    "$disabled": disabled,
    role: "menuitem",
    tabIndex: -1,
    "$highlighted": highlighted,
    ref: userRef
  }, action, disabledAttributes), /*#__PURE__*/React.createElement(Content$4, {
    content: content,
    helpText: helpText,
    selected: selected
  }, children));
});
var Action$3 = /*#__PURE__*/React.forwardRef(function Action(props, userRef) {
  var _props$is = props.is,
    is = _props$is === void 0 ? 'link' : _props$is,
    action = __rest(props, ["is"]);
  if (is === 'button') {
    return /*#__PURE__*/React.createElement(ButtonAction, Object.assign({
      ref: userRef
    }, action));
  }
  return /*#__PURE__*/React.createElement(LinkAction, Object.assign({
    ref: userRef
  }, action));
});
Action$3.displayName = 'ActionList.Action';
var Section$3 = /*#__PURE__*/React.forwardRef(function Section(props, userRef) {
  var section = props.section,
    children = props.children;
  if (children) {
    return /*#__PURE__*/React.createElement(StyledSection$1, {
      ref: userRef
    }, children);
  }
  var visibleActions = section ? filterByVisible(section).map(function (action, i) {
    return /*#__PURE__*/React.createElement(Action$3, Object.assign({
      key: i
    }, action));
  }) : [];
  if (!visibleActions.length) {
    return null;
  }
  return /*#__PURE__*/React.createElement(StyledSection$1, {
    ref: userRef
  }, visibleActions);
});
Section$3.displayName = 'ActionList.Section';
var keys$1 = {
  backspace: {
    key: 'Backspace',
    keyCode: 8,
    shiftKey: false
  },
  enter: {
    key: 'Enter',
    keyCode: 13,
    shiftKey: false
  },
  escape: {
    key: 'Escape',
    keyCode: 27,
    shiftKey: false
  },
  arrowRight: {
    key: 'ArrowRight',
    keyCode: 39,
    shiftKey: false
  },
  arrowLeft: {
    key: 'ArrowLeft',
    keyCode: 37,
    shiftKey: false
  },
  arrowUp: {
    key: 'ArrowUp',
    keyCode: 38,
    shiftKey: false
  },
  arrowDown: {
    key: 'ArrowDown',
    keyCode: 40,
    shiftKey: false
  },
  home: {
    key: 'Home',
    keyCode: 36,
    shiftKey: false
  },
  end: {
    key: 'End',
    keyCode: 35,
    shiftKey: false
  },
  space: {
    key: ' ',
    keyCode: 32,
    shiftKey: false
  },
  pageUp: {
    key: 'PageUp',
    keyCode: 33,
    shiftKey: false
  },
  pageDown: {
    key: 'PageDown',
    keyCode: 34,
    shiftKey: false
  },
  tab: {
    key: 'Tab',
    keyCode: 9,
    shiftKey: false
  }
};
function compareEvent(event, e) {
  return !!keys$1[event] && (e.key === keys$1[event].key || e.keyCode === keys$1[event].keyCode) && e.shiftKey === keys$1[event].shiftKey;
}
/**
 * Key down event handler
 * @example onKey('escape', () => foo())(e)
 */

function onKey(event, callback) {
  return function handleEvent(e) {
    if (compareEvent(event, e)) {
      return callback(e);
    }
  };
}
/**
 * Multiple key down event handler
 * @example onKeys(['escape', 'enter'], () => foo())(e)
 */

function onKeys(events, callback) {
  return function handleEvents(e) {
    events.forEach(function (event) {
      return onKey(event, callback)(e);
    });
  };
}

/**
 * Combines multiple refs
 */
function combineRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return function (value) {
    refs.forEach(function (ref) {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
}
function _slicedToArray$p(arr, i) {
  return _arrayWithHoles$p(arr) || _iterableToArrayLimit$p(arr, i) || _unsupportedIterableToArray$r(arr, i) || _nonIterableRest$p();
}
function _nonIterableRest$p() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$r(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$r(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$r(o, minLen);
}
function _arrayLikeToArray$r(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$p(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$p(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject$1l() {
  var data = _taggedTemplateLiteral$1l(["\n  ", "\n  overflow-y: auto;\n"]);
  _templateObject$1l = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1l(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$d = compose(margin, maxHeight, layout);
var Wrapper$8 = styled.div(_templateObject$1l(), system$d);
var ActionList = /*#__PURE__*/React.forwardRef(function ActionList(props, userRef) {
  var _props$actions = props.actions,
    actions = _props$actions === void 0 ? [] : _props$actions,
    labelledBy = props['aria-labelledby'],
    className = props.className,
    children = props.children,
    dataId = props['data-id'],
    id = props.id,
    _props$sections = props.sections,
    sections = _props$sections === void 0 ? [] : _props$sections,
    _props$maxHeight = props.maxHeight,
    maxHeight = _props$maxHeight === void 0 ? 'none' : _props$maxHeight,
    onClick = props.onClick,
    _props$groupByKey = props.groupByKey,
    groupByKey = _props$groupByKey === void 0 ? 'section' : _props$groupByKey,
    rest = __rest(props, ["actions", 'aria-labelledby', "className", "children", 'data-id', "id", "sections", "maxHeight", "onClick", "groupByKey"]); // TODO Remove `actions` and `sections` support in favor of composable components

  var list = actions && actions.length ? groupByValues(actions, groupByKey) : [];
  if (sections && sections.length) {
    list = list.concat(sections);
  }
  var listMarkup = list.map(function (section, index) {
    return /*#__PURE__*/React.createElement(Section$3, {
      section: section,
      key: index
    });
  });
  var wrapperRef = React.useRef(null);
  var _React$useState = React.useState([]),
    _React$useState2 = _slicedToArray$p(_React$useState, 2),
    focusableItemList = _React$useState2[0],
    setFocusableItemList = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray$p(_React$useState3, 2),
    focusIndex = _React$useState4[0],
    setFocusIndex = _React$useState4[1]; // Creates a list of focusable links or buttons inside the actionlist

  React.useEffect(function () {
    if (!!wrapperRef && wrapperRef.current) {
      setFocusableItemList(wrapperRef.current.querySelectorAll('[role="menuitem"]:not(:disabled):not([aria-disabled="true"])'));
    }
  }, []);
  React.useLayoutEffect(function () {
    if (focusableItemList[focusIndex]) {
      // Honestly not sure why this doesn't work without a timeout
      setTimeout(function () {
        focusableItemList[focusIndex].focus();
      }, 10);
    }
  }, [focusIndex, focusableItemList]);
  function handleKeyDown(e) {
    onKey('arrowDown', function () {
      // Stop arrow keys from scrolling the page
      e.preventDefault();
      if (focusIndex < focusableItemList.length - 1) {
        setFocusIndex(focusIndex + 1);
      } else {
        setFocusIndex(0);
      }
    })(e);
    onKey('arrowUp', function () {
      // Stop arrow keys from scrolling the page
      e.preventDefault();
      if (focusIndex <= 0) {
        setFocusIndex(focusableItemList.length - 1);
      } else {
        setFocusIndex(focusIndex - 1);
      }
    })(e);
  }
  return /*#__PURE__*/React.createElement(Wrapper$8, Object.assign({
    "aria-labelledby": labelledBy,
    className: className,
    "data-id": dataId,
    id: id,
    maxHeight: typeof maxHeight === 'number' ? "".concat(maxHeight, "px") : maxHeight,
    onClick: onClick,
    tabIndex: -1,
    role: "menu",
    ref: combineRefs(wrapperRef, userRef),
    onKeyDown: handleKeyDown
  }, rest), listMarkup, children);
});
ActionList.displayName = 'ActionList';
ActionList.Section = Section$3;
ActionList.Action = Action$3;
function _templateObject$1k() {
  var data = _taggedTemplateLiteral$1k(["\n  ", "\n  box-sizing: border-box;\n"]);
  _templateObject$1k = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1k(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Wrapper$7 = styled(Box)(_templateObject$1k(), margin);
var bgMap = {
  lightGray: 'gray.200',
  darkGray: 'gray.300',
  green: 'green.300',
  blue: 'blue.300',
  red: 'red.300',
  white: 'transparent',
  black: 'transparent'
};
var colorMap = {
  lightGray: 'gray.900',
  darkGray: 'gray.900',
  green: 'green.900',
  blue: 'blue.800',
  red: 'red.800',
  white: 'white',
  black: 'gray.900'
};
var borderMap = {
  lightGray: 'gray.400',
  darkGray: 'gray.400',
  green: 'green.400',
  blue: 'blue.400',
  red: 'red.400',
  white: 'white',
  black: 'gray.900'
};
var Badge = /*#__PURE__*/React.forwardRef(function Badge(props, userRef) {
  var children = props.children,
    _props$color = props.color,
    color = _props$color === void 0 ? 'lightGray' : _props$color,
    rest = __rest(props, ["children", "color"]);
  return /*#__PURE__*/React.createElement(Wrapper$7, Object.assign({
    display: "inline-block",
    borderRadius: "pill",
    px: "100",
    bg: bgMap[color],
    color: colorMap[color],
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: borderMap[color],
    fontSize: "50",
    fontWeight: "medium",
    lineHeight: "calc(1rem - 2px)",
    ref: userRef
  }, rest), children);
});
Badge.displayName = 'Badge';
var truncate = function truncate(props) {
  if (props.$truncate) {
    return "\n      overflow: hidden;\n      text-overflow: ellipsis;\n      white-space: nowrap;\n    ";
  }
  return '';
};
var lookslike = function lookslike(props) {
  var styles = '';
  switch (props.$looksLike) {
    case 'h1':
      styles = "\n        font-size: ".concat(tokens.fontSize_700, ";\n        line-height: ").concat(tokens.lineHeight_700, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'h2':
      styles = "\n        font-size: ".concat(tokens.fontSize_600, ";\n        line-height: ").concat(tokens.lineHeight_600, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'h3':
      styles = "\n        font-size: ".concat(tokens.fontSize_500, ";\n        line-height: ").concat(tokens.lineHeight_500, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'h4':
      styles = "\n        font-size: ".concat(tokens.fontSize_400, ";\n        line-height: ").concat(tokens.lineHeight_400, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'h5':
      styles = "\n        font-size: ".concat(tokens.fontSize_300, ";\n        line-height: ").concat(tokens.lineHeight_300, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'h6':
      styles = "\n        font-size: ".concat(tokens.fontSize_200, ";\n        line-height: ").concat(tokens.lineHeight_200, ";\n        font-weight: ").concat(tokens.fontWeight_semibold, ";\n      ");
      break;
    case 'p':
      styles = "\n        font-size: ".concat(tokens.fontSize_300, ";\n        line-height: ").concat(tokens.lineHeight_300, ";\n        font-weight: ").concat(tokens.fontWeight_normal, ";\n      ");
      break;
  }
  return styles;
};
function _toConsumableArray$1(arr) {
  return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$q(arr) || _nonIterableSpread$1();
}
function _nonIterableSpread$1() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$q(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$q(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$q(o, minLen);
}
function _iterableToArray$1(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$1(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$q(arr);
}
function _arrayLikeToArray$q(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _templateObject$1j() {
  var data = _taggedTemplateLiteral$1j(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$1j = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1j(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$c = compose(color, space, typography);
var StyledText = styled.p.withConfig(clean([].concat(_toConsumableArray$1(system$c.propNames), ['truncate'])))(_templateObject$1j(), system$c, truncate, lookslike);
var Text = /*#__PURE__*/React.forwardRef(function Text(props, ref) {
  var _props$as = props.as,
    as = _props$as === void 0 ? 'p' : _props$as,
    looksLike = props.looksLike,
    children = props.children,
    truncate = props.truncate,
    rest = __rest(props, ["as", "looksLike", "children", "truncate"]);
  return /*#__PURE__*/React.createElement(StyledText, Object.assign({
    as: as,
    "$looksLike": looksLike,
    "$truncate": truncate,
    ref: ref
  }, rest), children);
});
Text.displayName = 'Text';
var negativeTop = system$g({
  $gutter: {
    property: 'marginTop',
    scale: 'space',
    transform: function transform(value, scale) {
      return "calc(-".concat(scale[value] || value, " - 1px)");
    }
  }
});
var negativeLeft = system$g({
  $gutter: {
    property: 'marginLeft',
    scale: 'space',
    transform: function transform(value, scale) {
      return "-".concat(scale[value] || value);
    }
  }
});
var alignChildren = system$g({
  $align: {
    property: 'justifyContent',
    defaultScale: {
      center: 'center',
      left: 'flex-start',
      right: 'flex-end'
    }
  }
});
var alignYChildren = system$g({
  $alignY: {
    property: 'alignItems',
    defaultScale: {
      center: 'center',
      top: 'flex-start',
      bottom: 'flex-end'
    }
  }
});
function _templateObject2$D() {
  var data = _taggedTemplateLiteral$1i(["\n  display: flex;\n  flex-wrap: wrap;\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject2$D = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1i() {
  var data = _taggedTemplateLiteral$1i(["\n  padding-top: 1px;\n  &:before {\n    display: block;\n    content: '';\n    ", "\n  }\n"]);
  _templateObject$1i = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1i(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
// This is set here to prevent margin collapse

var OuterWrapper$1 = styled('div')(_templateObject$1i(), negativeTop); // Negates children padding left and aligns content

var InnerWrapper = styled('div')(_templateObject2$D(), negativeLeft, alignChildren, alignYChildren);
function Inline(props) {
  var children = props.children,
    align = props.align,
    _props$alignY = props.alignY,
    alignY = _props$alignY === void 0 ? 'center' : _props$alignY,
    _props$space = props.space,
    space = _props$space === void 0 ? '400' : _props$space;
  var items = React.Children.toArray(children);
  return /*#__PURE__*/React.createElement(OuterWrapper$1, {
    "$gutter": space,
    "data-id": props['data-id']
  }, /*#__PURE__*/React.createElement(InnerWrapper, {
    "$gutter": space,
    "$align": align,
    "$alignY": alignY
  }, items.map(function (child, i) {
    return /*#__PURE__*/React.createElement(Box, {
      key: i,
      pt: space,
      pl: space
    }, child);
  })));
}
Inline.displayName = 'Inline';
function _templateObject$1h() {
  var data = _taggedTemplateLiteral$1h(["\n  ", "\n"]);
  _templateObject$1h = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1h(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledScreenReaderOnly = styled('span')(_templateObject$1h(), visuallyHidden);
var ScreenReaderOnly = function ScreenReaderOnly(_ref) {
  var children = _ref.children,
    _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'span' : _ref$as,
    id = _ref.id;
  return /*#__PURE__*/React.createElement(StyledScreenReaderOnly, {
    id: id,
    as: as
  }, children);
};
ScreenReaderOnly.displayName = 'ScreenReaderOnly';
function container(props) {
  switch (props.$status) {
    case 'success':
      return "\n        background: ".concat(tokens.color_green_100, ";\n        border: 1px solid ").concat(tokens.color_green_600, ";\n      ");
    case 'warning':
      return "\n        background: ".concat(tokens.color_yellow_100, ";\n        border: 1px solid ").concat(tokens.color_yellow_500, ";\n      ");
    case 'danger':
      return "\n        background: ".concat(tokens.color_red_100, ";\n        border: 1px solid ").concat(tokens.color_red_700, ";\n      ");
    case 'muted':
    case 'promo':
      return "\n        background: ".concat(tokens.color_gray_100, ";\n        border: 1px solid ").concat(tokens.color_gray_400, ";\n      ");
    case 'default':
    default:
      return "\n        background: ".concat(tokens.color_blue_100, ";\n        border: 1px solid ").concat(tokens.color_blue_600, ";\n      ");
  }
} // Overwrites unstyled link colors within Banner content to be color-contrast accessible

function childLinks(props) {
  return "\n  p, ul, ol {\n    font-size: ".concat(props.theme.fontSizes[200], ";\n  a, a: visited {\n    color: ").concat(tokens.color_blue_800, ";\n      &: hover {\n      color: ").concat(tokens.color_blue_900, ";\n    }\n  }\n}\n");
}
var statusIcons = {
  success: {
    iconName: CheckCircleOutline,
    iconLabel: 'Success',
    bg: 'green.600',
    fill: 'green.700'
  },
  info: {
    iconName: InfoOutline,
    iconLabel: 'Info',
    bg: 'blue.700',
    fill: 'blue.700'
  },
  warning: {
    iconName: ReportProblemOutlined,
    iconLabel: 'Warning',
    bg: 'yellow.300',
    fill: 'yellow.700'
  },
  danger: {
    iconName: ErrorOutline,
    iconLabel: 'Error',
    bg: 'red.700',
    fill: 'red.700'
  },
  promo: {
    iconName: Rocket,
    bg: 'gray.900',
    fill: 'gray.900'
  }
};
function dismissBase() {
  return "\ntransition: background ".concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n    &: hover {\n  cursor: pointer;\n}\n");
}
function dismissColor(props) {
  switch (props.$status) {
    case 'success':
      return "\n  &: hover {\n  background: ".concat(tokens.color_green_300, ";\n}\n");
    case 'warning':
      return "\n  &: hover {\n  background: ".concat(tokens.color_yellow_300, ";\n}\n");
    case 'danger':
      return "\n  &: hover {\n  background: ".concat(tokens.color_red_300, ";\n}\n");
    // Gray banner no longer exists as of 4.0.0

    case 'default':
    default:
      return "\n  &: hover {\n  background: ".concat(tokens.color_blue_300, ";\n}\n");
  }
}

/**
 * Finds and clones a React component from display name
 * @param {string} name
 * @param {element} children
 * @param {object} props
 *
 * @example
 *  getChild('MyChildComponent', props.children, { extra: 'prop' })
 */

function getChild(name, children) {
  var props = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return React.Children.map(children, function (child, index) {
    if ( /*#__PURE__*/React.isValidElement(child) && typeof child.type !== 'string' && (child.type.displayName === name || child.type.name === name)) {
      props.index = index;
      return /*#__PURE__*/React.cloneElement(child, props);
    }
    return null;
  });
}
function excludeChild(names, children) {
  return React.Children.map(children, function (child) {
    if ( /*#__PURE__*/React.isValidElement(child) && names.includes(child.type.displayName)) {
      return null;
    }
    return child;
  });
}
function _templateObject10() {
  var data = _taggedTemplateLiteral$1g(["\n  0% {\n    transform: rotate(0);\n  }\n  100% {\n    transform: rotate(360deg);\n  }\n"]);
  _templateObject10 = function _templateObject10() {
    return data;
  };
  return data;
}
function _templateObject9() {
  var data = _taggedTemplateLiteral$1g(["\n  0% {\n    stroke-dasharray: 1 200;\n    stroke-dashoffset: 0;\n  }\n  50% {\n    stroke-dasharray: 120 200;\n    stroke-dashoffset: -25;\n  }\n  100% {\n    stroke-dasharray: 120 200;\n    stroke-dashoffset: -145;\n  }\n"]);
  _templateObject9 = function _templateObject9() {
    return data;
  };
  return data;
}
function _templateObject8() {
  var data = _taggedTemplateLiteral$1g(["\n  0% {\n    stroke-dasharray: 1 100;\n    stroke-dashoffset: 0;\n  }\n  50% {\n    stroke-dasharray: 60 100;\n    stroke-dashoffset: -15;\n  }\n  100% {\n    stroke-dasharray: 80 100;\n    stroke-dashoffset: -68;\n  }\n"]);
  _templateObject8 = function _templateObject8() {
    return data;
  };
  return data;
}
function _templateObject7$2() {
  var data = _taggedTemplateLiteral$1g(["\n  0% {\n    stroke-dasharray: 1 50;\n    stroke-dashoffset: 0;\n  }\n  50% {\n    stroke-dasharray: 30 50;\n    stroke-dashoffset: -10;\n  }\n  100% {\n    stroke-dasharray: 40 50;\n    stroke-dashoffset: -48;\n  }\n"]);
  _templateObject7$2 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$3() {
  var data = _taggedTemplateLiteral$1g(["\n          animation: ", " 1.5s ease-in-out infinite;\n        "]);
  _templateObject6$3 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$a() {
  var data = _taggedTemplateLiteral$1g(["\n    stroke: ", ";\n    stroke-width: ", ";\n    stroke-linecap: round;\n\n    ", "\n  "]);
  _templateObject5$a = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$e() {
  var data = _taggedTemplateLiteral$1g(["\n        stroke-dasharray: 55 100;\n        stroke-dashoffset: -15;\n      "]);
  _templateObject4$e = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$m() {
  var data = _taggedTemplateLiteral$1g(["\n        stroke-dasharray: 110 200;\n        stroke-dashoffset: -25;\n      "]);
  _templateObject3$m = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$C() {
  var data = _taggedTemplateLiteral$1g(["\n        stroke-dasharray: 35 50;\n        stroke-dashoffset: -10;\n      "]);
  _templateObject2$C = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1g() {
  var data = _taggedTemplateLiteral$1g(["\n    fill: none;\n    animation: ", " ", " linear infinite;\n  "]);
  _templateObject$1g = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1g(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var dimensions = function dimensions(props) {
  var size;
  switch (props.$size) {
    case 'small':
      size = '20px';
      break;
    case 'large':
      size = '60px';
      break;
    case 'medium':
    default:
      size = '28px';
  }
  return "\n    width: ".concat(size, ";\n    height: ").concat(size, ";\n  ");
};
var circleOuter = function circleOuter(props) {
  return css(_templateObject$1g(), rotateAnimation, props.$rotationOnly ? '1.2s' : '2s');
};
function getDefaultStrokes(size) {
  switch (size) {
    case 'small':
      return css(_templateObject2$C());
    case 'large':
      return css(_templateObject3$m());
    case 'medium':
    default:
      return css(_templateObject4$e());
  }
}
var circle = function circle(props) {
  var color, strokeWidth, animation;
  switch (props.color) {
    case 'gray':
      color = tokens.color_gray_800;
      break;
    case 'orange':
      color = tokens.color_brand_orange;
      break;
    case 'white':
      color = tokens.color_white;
      break;
    case 'blue':
    default:
      color = tokens.color_blue_700;
      break;
  }
  switch (props.$size) {
    case 'small':
      strokeWidth = '2px';
      animation = smallDashAnimation;
      break;
    case 'large':
      strokeWidth = '4px';
      animation = largeDashAnimation;
      break;
    case 'medium':
    default:
      strokeWidth = '3px';
      animation = dashAnimation;
  }
  return css(_templateObject5$a(), color, strokeWidth, props.$rotationOnly ? getDefaultStrokes(props.$size) : css(_templateObject6$3(), animation));
};
var smallDashAnimation = keyframes(_templateObject7$2());
var dashAnimation = keyframes(_templateObject8());
var largeDashAnimation = keyframes(_templateObject9());
var rotateAnimation = keyframes(_templateObject10());
function _templateObject3$l() {
  var data = _taggedTemplateLiteral$1f(["\n  ", "\n"]);
  _templateObject3$l = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$B() {
  var data = _taggedTemplateLiteral$1f(["\n  ", "\n  ", "\n"]);
  _templateObject2$B = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1f() {
  var data = _taggedTemplateLiteral$1f(["\n  ", "\n  ", "\n"]);
  _templateObject$1f = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1f(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$b = compose(margin, position, width, height);
var StyledSpinner = styled(Box)(_templateObject$1f(), system$b, dimensions);
var StyledSVG = styled.svg(_templateObject2$B(), circleOuter, dimensions);
var StyledCircle$1 = styled.circle(_templateObject3$l(), circle);
var Spinner = /*#__PURE__*/React.forwardRef(function Spinner(props, ref) {
  var _props$size = props.size,
    size = _props$size === void 0 ? 'medium' : _props$size,
    color = props.color,
    label = props.label,
    rotationOnly = props.rotationOnly,
    rest = __rest(props, ["size", "color", "label", "rotationOnly"]);
  var systemProps = pick(rest, system$b.propNames);
  return /*#__PURE__*/React.createElement(StyledSpinner, Object.assign({
    "$size": size
  }, systemProps, {
    ref: ref,
    "data-id": "loading-spinner"
  }), /*#__PURE__*/React.createElement(StyledSVG, {
    "$size": size,
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "25 25 50 50",
    "$rotationOnly": rotationOnly
  }, /*#__PURE__*/React.createElement(StyledCircle$1, {
    "$size": size,
    color: color,
    cx: "50",
    cy: "50",
    r: "20",
    vectorEffect: "non-scaling-stroke",
    "$rotationOnly": rotationOnly
  })), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, label));
});
Spinner.displayName = 'Spinner';
function _templateObject$1e() {
  var data = _taggedTemplateLiteral$1e(["\n  ", "\n"]);
  _templateObject$1e = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1e(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledIcon = styled.svg(_templateObject$1e(), margin);
var Icon = /*#__PURE__*/React.forwardRef(function Icon(props, userRef) {
  var as = props.as,
    size = props.size,
    width = props.width,
    height = props.height,
    label = props.label,
    rest = __rest(props, ["as", "size", "width", "height", "label"]);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(StyledIcon, Object.assign({
    as: as,
    size: size,
    width: width,
    height: height,
    label: label,
    ref: userRef,
    mt: "-2px"
  }, systemProps));
});
Icon.displayName = 'Button.Icon';
function focus(_ref) {
  var buttonColor = _ref.buttonColor;
  var color = tokens.color_gray_300;
  switch (buttonColor) {
    case 'orange': // To be deprecated

    case 'blue':
      color = tokens.color_blue_700;
      break;
    case 'red':
      color = tokens.color_red_700;
      break;
    case 'white':
      color = tokens.color_white;
      break;
    case 'gray':
    default:
      color = tokens.color_gray_900;
      break;
  }
  return focusOutline({
    color: color
  });
}
var base$2 = function base() {
  return "\n  display: inline-flex;\n  align-items: center;\n  border-radius: ".concat(tokens.borderRadius_100, ";\n  user-select: none;\n  font-weight: ").concat(tokens.fontWeight_medium, ";\n  justify-content: center;\n  text-decoration: none;\n  white-space: nowrap;\n  transition-property: background, color, border, outline;\n  transition-duration: ").concat(tokens.motionDuration_fast, ";\n  border: 1px solid transparent;\n  cursor: pointer;\n");
};
var childwrapper = function childwrapper(props) {
  return "\n  transition: ".concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n  transform: translateY(").concat(props.$loading ? '-50%' : '0%', ");\n  opacity: ").concat(props.$loading ? '0' : '1', ";\n");
};
var loader = function loader(props) {
  return "\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: 50%;\n  display: flex;\n  align-items: center;\n  pointer-events: none;\n  user-select: none\n  opacity: ".concat(props.$state === 'entered' ? '1' : '0', ";\n  transform: ").concat(props.$state === 'entered' ? 'translate(-50%, 0%)' : 'translate(-50%, 40%)', ";\n  transition: ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n");
};
var visualSize$1 = function visualSize(props) {
  switch (props.buttonSize) {
    case 'large':
      return "\n        height: ".concat(tokens.sizing_750, ";\n        font-size: ").concat(tokens.fontSize_300, ";\n        padding: 0 ").concat(tokens.spacing_500, ";\n      ");
    case 'small':
      return "\n        height: ".concat(tokens.sizing_600, ";\n        font-size: ").concat(tokens.fontSize_200, ";\n        padding: 0 ").concat(tokens.spacing_300, ";\n      ");
    case 'default':
    default:
      return "\n        height: ".concat(tokens.sizing_650, ";\n        font-size: ").concat(tokens.fontSize_200, ";\n        padding: 0 ").concat(tokens.spacing_400, ";\n      ");
  }
};
var colorVariant = function colorVariant(_ref2) {
  var buttonColor = _ref2.buttonColor,
    theme = _ref2.theme,
    buttonVariant = _ref2.buttonVariant,
    disabled = _ref2.disabled;
  var color;
  var darkHoverColor;
  var lightHoverColor;
  var lightActiveColor;
  switch (buttonColor) {
    case 'orange': // To be deprecated

    case 'blue':
      color = theme.colors.blue[700];
      darkHoverColor = theme.colors.blue[800];
      lightActiveColor = theme.colors.blue[300];
      lightHoverColor = theme.colors.blue[200];
      break;
    case 'red':
      color = theme.colors.red[700];
      darkHoverColor = theme.colors.red[800];
      lightActiveColor = theme.colors.red[300];
      lightHoverColor = theme.colors.red[200];
      break;
    case 'white':
      color = theme.colors.white;
      darkHoverColor = theme.colors.gray[200];
      lightActiveColor = theme.colors.gray[600];
      lightHoverColor = theme.colors.gray[700];
      break;
    case 'gray':
    default:
      color = theme.colors.gray[900];
      darkHoverColor = theme.colors.gray[1000];
      lightActiveColor = theme.colors.gray[400];
      lightHoverColor = theme.colors.gray[300];
      break;
  }
  switch (buttonVariant) {
    case 'filled':
      return "\n        &, &:visited {\n          background: ".concat(color, ";\n          color: ").concat(buttonColor === 'white' ? theme.colors.blue[700] : theme.colors.white, ";\n\n          &:hover {\n            ").concat(!disabled ? "background: ".concat(darkHoverColor, ";") : '', "\n          }\n          &:focus, &:hover {\n            color: ").concat(buttonColor === 'white' ? theme.colors.blue[700] : theme.colors.white, ";\n          }\n          &:active {\n            background: ").concat(color, ";\n          }\n        }\n      ");
    case 'text':
      return "\n        &, &:visited {\n          background: transparent;\n          color: ".concat(color, ";\n          &:hover {\n            ").concat(!disabled ? "background: ".concat(lightHoverColor, ";") : '', "\n          }\n          &:focus, &:hover {\n            color: ").concat(color, ";\n          }\n          &:active {\n            background: ").concat(lightActiveColor, ";\n          }\n        }\n      ");
    case 'outline':
    case 'mutedOutline':
    default:
      return "\n        &, &:visited {\n          border: 1px solid ".concat(buttonVariant == 'mutedOutline' ? buttonColor === 'white' ? theme.colors.gray[600] : theme.colors.gray[400] : color, ";\n          background: transparent;\n          color: ").concat(color, ";\n          &:hover {\n            ").concat(!disabled ? "background: ".concat(lightHoverColor, ";") : '', "\n          }\n          &:focus, &:hover {\n            color: ").concat(color, ";\n          }\n          &:active {\n            background: ").concat(lightActiveColor, ";\n          }\n        }\n      ");
  }
};
var disabled = function disabled(props) {
  if (props.disabled) {
    return "\n      opacity: 0.6;\n      &:hover {\n        cursor: not-allowed;\n      }\n    ";
  }
};
var fullWidth = function fullWidth(props) {
  if (props.fullWidth) {
    return "\n      display: block;\n      width: 100%;\n    ";
  }
}; // This selector is intentionally loose to handle buttons wrapped in other components such as tooltips

var group = function group() {
  return "\n  & > * {\n    margin-right: -1px;\n  }\n";
};
function _templateObject$1d() {
  var data = _taggedTemplateLiteral$1d(["\n  ", "\n"]);
  _templateObject$1d = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1d(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledGroup$2 = styled(Box)(_templateObject$1d(), group);
var Group$5 = function Group(_ref) {
  var children = _ref.children,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className;
  return /*#__PURE__*/React.createElement(StyledGroup$2, {
    display: "inline-flex",
    alignItems: "center",
    className: className
  }, children);
};
Group$5.displayName = 'Button.Group';
function _templateObject3$k() {
  var data = _taggedTemplateLiteral$1c(["\n  ", "\n"]);
  _templateObject3$k = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$A() {
  var data = _taggedTemplateLiteral$1c(["\n  ", "\n"]);
  _templateObject2$A = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1c() {
  var data = _taggedTemplateLiteral$1c(["\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$1c = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1c(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
function getLoaderColor() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'filled' : _ref$variant,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'gray' : _ref$color;
  if (variant === 'filled') {
    if (color === 'white') {
      return 'gray';
    }
    return 'white';
  }
  if (color === 'white') {
    return 'white';
  }
  if (color === 'blue') {
    return 'blue';
  }
  return 'gray';
} // TODO Categorize system props and abstract

var system$a = compose(margin, width, padding);
var StyledButton$3 = styled(Box)(_templateObject$1c(), base$2, focus, visualSize$1, colorVariant, disabled, fullWidth, system$a);
var StyledLoader = styled.div(_templateObject2$A(), loader);
var ChildWrapper = styled.span(_templateObject3$k(), childwrapper); // Handles deprecated button variant props
// TODO Remove in 6.0

function getVariant(_ref2) {
  var outline = _ref2.outline,
    outlineBorder = _ref2.outlineBorder,
    plain = _ref2.plain,
    flat = _ref2.flat,
    variant = _ref2.variant;
  if (variant) {
    return variant;
  }
  if (outlineBorder) {
    return 'outline';
  }
  if (outline) {
    return 'mutedOutline';
  }
  if (plain || flat) {
    return 'text';
  }
  return 'filled';
}
var Button$1 = /*#__PURE__*/React.forwardRef(function Button(props, ref) {
  var children = props.children,
    primary = props.primary,
    color = props.color,
    disabled = props.disabled,
    destructive = props.destructive,
    loading = props.loading,
    _props$loadingLabel = props.loadingLabel,
    loadingLabel = _props$loadingLabel === void 0 ? 'Loading' : _props$loadingLabel,
    plain = props.plain,
    flat = props.flat,
    outline = props.outline,
    outlineBorder = props.outlineBorder,
    variant = props.variant,
    _props$size = props.size,
    buttonSize = _props$size === void 0 ? 'default' : _props$size,
    fullWidth = props.fullWidth,
    submit = props.submit,
    to = props.to,
    Component = props.Component,
    component = props.component,
    as = props.as,
    external = props.external,
    title = props.title,
    onClick = props.onClick,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    rest = __rest(props, ["children", "primary", "color", "disabled", "destructive", "loading", "loadingLabel", "plain", "flat", "outline", "outlineBorder", "variant", "size", "fullWidth", "submit", "to", "Component", "component", "as", "external", "title", "onClick", "onFocus", "onBlur", "className"]);
  var transitionRef = React.useRef();
  var systemProps = pick(rest, system$a.propNames);
  var componentProps = omit$1(rest); // Polyfills deprecrated 'Component' prop — use as instead of component

  var WrapperComponent = as || component || Component; // Polyfills to be deprecrated 'primary' and 'destructive' prop

  var buttonColor = primary ? 'blue' : destructive ? 'red' : color;
  var buttonVariant = React.useMemo(function () {
    return getVariant({
      variant: variant,
      outline: outline,
      outlineBorder: outlineBorder,
      plain: plain,
      flat: flat
    });
  }, [variant, outline, outlineBorder, plain, flat, getVariant]);
  var loaderColor = React.useMemo(function () {
    return getLoaderColor({
      variant: buttonVariant,
      color: color
    });
  }, [buttonVariant, color]);
  var loadingIndicator = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(Transition, {
      mountOnEnter: true,
      unmountOnExit: true,
      "in": loading,
      timeout: 0,
      nodeRef: transitionRef
    }, function (state) {
      return /*#__PURE__*/React.createElement(StyledLoader, {
        "$state": state,
        ref: transitionRef
      }, /*#__PURE__*/React.createElement(Spinner, {
        color: loaderColor,
        size: "small",
        label: loadingLabel,
        rotationOnly: true
      }));
    });
  }, [loading]);
  var sharedProps = Object.assign(Object.assign({
    className: className,
    disabled: disabled || loading,
    fullWidth: fullWidth,
    onClick: onClick,
    onFocus: onFocus,
    onBlur: onBlur,
    buttonSize: buttonSize,
    buttonVariant: buttonVariant,
    buttonColor: buttonColor,
    ref: ref
  }, systemProps), componentProps);
  var childrenMarkup = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(ChildWrapper, {
      "aria-hidden": loading,
      "$loading": loading ? loading : undefined
    }, children);
  }, [loading, children]);
  if (to && !WrapperComponent) {
    return /*#__PURE__*/React.createElement(StyledButton$3, Object.assign({
      as: "a",
      href: to,
      target: external ? '_blank' : '',
      rel: external ? 'noopener noreferrer' : '',
      title: external && !title ? 'Opens in a new tab' : title
    }, sharedProps), childrenMarkup, loadingIndicator);
  }
  if (to && WrapperComponent) {
    return /*#__PURE__*/React.createElement(StyledButton$3, Object.assign({
      as: WrapperComponent,
      to: to
    }, sharedProps), childrenMarkup, loadingIndicator);
  }
  return /*#__PURE__*/React.createElement(StyledButton$3, Object.assign({
    as: WrapperComponent || 'button',
    type: submit ? 'submit' : 'button'
  }, sharedProps), childrenMarkup, loadingIndicator);
});
Button$1.displayName = 'Button';
Button$1.Group = Group$5;
Button$1.Icon = Icon;
function buttonsFrom(actions, overrides) {
  var filteredActions = filterByVisible(actions);
  if (filteredActions.length) {
    return /*#__PURE__*/React.createElement(Button$1.Group, null, filteredActions.map(function (action, key) {
      return buttonFrom(action, overrides, key);
    }));
  }
}
function buttonFrom(_a, overrides, key) {
  var content = _a.content,
    action = __rest(_a, ["content"]);
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    key: key,
    children: content
  }, action, overrides)); // eslint-disable-line
}

var Action$2 = /*#__PURE__*/React.forwardRef(function Action(props, userRef) {
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    mt: ['300', null, '500'],
    mr: "400"
  }, props, {
    ref: userRef
  }));
});
Action$2.displayName = 'Banner.Action';
function _templateObject$1b() {
  var data = _taggedTemplateLiteral$1b(["\n  pointer-events: none;\n  figure,\n  img,\n  video {\n    position: absolute;\n    height: 100%;\n    width: auto;\n    left: 50%;\n    transform: translateX(-50%);\n  }\n"]);
  _templateObject$1b = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1b(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledMedia$1 = styled(Box)(_templateObject$1b());
var Media$1 = /*#__PURE__*/React.forwardRef(function Media(_ref, userRef) {
  var children = _ref.children,
    className = _ref.className;
  return /*#__PURE__*/React.createElement(StyledMedia$1, {
    className: className,
    ref: userRef
  }, children);
});
Media$1.displayName = 'Banner.Media';
function _templateObject3$j() {
  var data = _taggedTemplateLiteral$1a(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject3$j = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$z() {
  var data = _taggedTemplateLiteral$1a(["\n  ", "\n"]);
  _templateObject2$z = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1a() {
  var data = _taggedTemplateLiteral$1a(["\n  ", "\n  ", "\n"]);
  _templateObject$1a = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1a(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
function IconSection(_ref) {
  var status = _ref.status,
    size = _ref.size;
  var statusIcon = React.useMemo(function () {
    return status === 'default' ? statusIcons.info : statusIcons[status];
  }, [statusIcons, status]);
  var Icon = statusIcon.iconName;
  var iconSize = '450';
  var fillColor = statusIcon.fill;
  var iconAttributes = statusIcon.iconLabel ? {
    label: statusIcon.iconLabel
  } : {
    'aria-hidden': true
  };
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: iconSize,
    height: iconSize,
    color: fillColor,
    mr: "300",
    mt: size === 'large' ? '4px' : '2px'
  }, /*#__PURE__*/React.createElement(Icon, Object.assign({
    size: size === 'large' ? 24 : 20
  }, iconAttributes)));
}
var StyledContainer = styled(Box)(_templateObject$1a(), container, margin);
var StyledChildren = styled.div(_templateObject2$z(), childLinks);
var StyledDismiss = styled(Box)(_templateObject3$j(), buttonReset, dismissBase, dismissColor);
var Banner = /*#__PURE__*/React.forwardRef(function Banner(props, userRef) {
  var children = props.children,
    dataId = props['data-id'],
    id = props.id,
    title = props.title,
    _props$status = props.status,
    status = _props$status === void 0 ? 'default' : _props$status,
    action = props.action,
    actions = props.actions,
    onDismiss = props.onDismiss,
    _props$size = props.size,
    size = _props$size === void 0 ? 'large' : _props$size,
    rest = __rest(props, ["children", 'data-id', "id", "title", "status", "action", "actions", "onDismiss", "size"]);
  var systemProps = pick(rest, margin.propNames);
  var titleMarkup = title ? /*#__PURE__*/React.createElement(Box, {
    mb: "200"
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: "400",
    lineHeight: "400",
    as: "h5"
  }, title)) : null;
  var actionMarkup = React.useMemo(function () {
    var result = action ? /*#__PURE__*/React.createElement(Action$2, Object.assign({}, omit(action, ['content'])), action.content) : null;
    if (actions) {
      result = /*#__PURE__*/React.createElement(Inline, null, actions.map(function (action, i) {
        return /*#__PURE__*/React.createElement(Action$2, Object.assign({
          key: i,
          mr: "0"
        }, omit(action, ['content'])), action.content);
      }));
    }
    return result;
  }, [action, actions]);
  var dismissMarkup = onDismiss ? /*#__PURE__*/React.createElement(Box, {
    flex: ['1', null, '0'],
    textAlign: "right",
    position: "relative"
  }, /*#__PURE__*/React.createElement(StyledDismiss, {
    as: "button",
    onClick: onDismiss,
    "$status": status,
    color: "gray.800",
    type: "button",
    p: size === 'large' ? '100' : 0
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Dismiss"), /*#__PURE__*/React.createElement(Close, {
    size: size === 'large' ? 24 : 20
  }))) : null;
  var hasMedia = React.useMemo(function () {
    return getChild('Banner.Media', children);
  }, [children]);
  var mediaMarkup = hasMedia && hasMedia.length ? /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    maxWidth: "600px",
    pl: "200",
    display: ['none', null, 'block'],
    position: "relative"
  }, getChild('Banner.Media', children)) : null;
  return /*#__PURE__*/React.createElement(StyledContainer, Object.assign({
    display: "flex",
    flexWrap: ['wrap', null, 'nowrap'],
    p: size === 'large' ? '450' : '300',
    py: size === 'large' ? null : '200',
    borderRadius: "100",
    "$status": status
  }, systemProps, {
    ref: userRef,
    tabIndex: -1,
    overflow: "hidden",
    "data-id": dataId,
    id: id
  }), status !== 'muted' && /*#__PURE__*/React.createElement(IconSection, {
    status: status,
    size: size
  }), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    order: [1, null, 0],
    flexBasis: ['100%', null, 'auto']
  }, titleMarkup, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(StyledChildren, null, excludeChild('Banner.Media', children))), actionMarkup), mediaMarkup, dismissMarkup);
});
Banner.displayName = 'Banner';
Banner.Action = Action$2;
Banner.Media = Media$1;
function _templateObject3$i() {
  var data = _taggedTemplateLiteral$19(["\n  text-decoration: none;\n  ", "\n  &:visited {\n    ", "\n  }\n  &:hover {\n    ", "\n  }\n"]);
  _templateObject3$i = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$y() {
  var data = _taggedTemplateLiteral$19(["\n  &:last-of-type {\n    ", " {\n      display: none;\n    }\n  }\n"]);
  _templateObject2$y = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$19() {
  var data = _taggedTemplateLiteral$19(["\n  ", "\n"]);
  _templateObject$19 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$19(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledChevron$3 = styled.div(_templateObject$19(), css$1({
  px: '100'
}));
var LinkWrapper$1 = styled(Box)(_templateObject2$y(), StyledChevron$3);
var StyledLink$3 = styled(UnstyledLink)(_templateObject3$i(), css$1({
  color: 'gray.900'
}), css$1({
  color: 'gray.900'
}), css$1({
  color: 'blue.700'
}));
var Link = /*#__PURE__*/React.forwardRef(function Link(props, userRef) {
  var to = props.to,
    _props$active = props.active,
    active = _props$active === void 0 ? false : _props$active,
    children = props.children;
  return /*#__PURE__*/React.createElement(LinkWrapper$1, {
    display: "inline-flex",
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(StyledLink$3, {
    to: to,
    fontSize: "200",
    lineHeight: "200",
    fontWeight: active ? 'semibold' : 'regular',
    ref: userRef
  }, children), /*#__PURE__*/React.createElement(StyledChevron$3, null, /*#__PURE__*/React.createElement(ChevronRight, {
    size: "18"
  })));
});
Link.displayName = 'Breadcrumb.Link';
function _templateObject$18() {
  var data = _taggedTemplateLiteral$18(["\n  ", "\n"]);
  _templateObject$18 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$18(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Wrapper$6 = styled(Box)(_templateObject$18(), margin);
var Breadcrumb$1 = /*#__PURE__*/React.forwardRef(function Breadcrumb(props, userRef) {
  var children = props.children,
    rest = __rest(props, ["children"]);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(Wrapper$6, Object.assign({
    ref: userRef
  }, systemProps), children);
});
Breadcrumb$1.displayName = 'Breadcrumb';
Breadcrumb$1.Link = Link;
function Label$1(props) {
  var label = props.label,
    id = props.id,
    htmlFor = props.htmlFor,
    className = props.className,
    children = props.children,
    labelHidden = props.labelHidden,
    _props$as = props.as,
    as = _props$as === void 0 ? 'label' : _props$as,
    _props$fontWeight = props.fontWeight,
    fontWeight = _props$fontWeight === void 0 ? '500' : _props$fontWeight,
    _props$mb = props.mb,
    mb = _props$mb === void 0 ? '100' : _props$mb;
  if (labelHidden) {
    return /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Box, {
      as: as,
      id: id && "".concat(id, "Label"),
      htmlFor: id
    }, label, children));
  }
  return /*#__PURE__*/React.createElement(Box, {
    display: "block",
    as: as,
    id: id && !htmlFor ? "".concat(id, "Label") : id,
    htmlFor: htmlFor ? htmlFor : id,
    fontWeight: fontWeight,
    className: className,
    mb: mb
  }, /*#__PURE__*/React.createElement(Box, {
    as: "div",
    lineHeight: "200",
    fontSize: "200"
  }, label ? /*#__PURE__*/React.createElement(Box, {
    as: "span",
    mr: "200"
  }, label) : null, children ? /*#__PURE__*/React.createElement("span", null, children) : null));
}
Label$1.displayName = 'Label';
function ErrorLabel(props) {
  var className = props.className,
    children = props.children,
    error = props.error,
    _props$wrapper = props.wrapper,
    WrapperComponent = _props$wrapper === void 0 ? 'div' : _props$wrapper,
    id = props.id,
    _props$ml = props.ml,
    ml = _props$ml === void 0 ? '0' : _props$ml;
  return /*#__PURE__*/React.createElement(Box, {
    id: id,
    as: WrapperComponent,
    className: className,
    ml: ml,
    "data-id": "error-message"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    color: "red.700",
    fontSize: "200",
    lineHeight: "200",
    display: "inline-flex",
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    display: "inline-block",
    mr: "100",
    pt: "2px",
    lineHeight: "0"
  }, /*#__PURE__*/React.createElement(ErrorIcon, {
    size: 14,
    label: "Error"
  })), /*#__PURE__*/React.createElement("span", null, error, children)));
}
ErrorLabel.displayName = 'Error';
function _templateObject$17() {
  var data = _taggedTemplateLiteral$17(["\n  display: flex;\n  flex-direction: column;\n  ", "\n  ", "\n"]);
  _templateObject$17 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$17(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledBox$3 = styled(Box)(_templateObject$17(), function () {
  return system$g({
    $gutter: {
      property: 'paddingBottom',
      scale: 'space'
    }
  });
}, function () {
  return system$g({
    $alignment: {
      property: 'alignItems',
      defaultScale: {
        center: 'center',
        left: 'flex-start',
        right: 'flex-end'
      }
    }
  });
});
function Stack(props) {
  var children = props.children,
    align = props.align,
    _props$space = props.space,
    space = _props$space === void 0 ? '500' : _props$space;
  var items = React.Children.toArray(children);
  if (items.length <= 1 && !align) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, items);
  }
  return /*#__PURE__*/React.createElement("div", {
    "data-id": props['data-id']
  }, items.map(function (child, i) {
    return /*#__PURE__*/React.createElement(StyledBox$3, {
      key: i,
      "$alignment": align,
      "$gutter": i < items.length - 1 ? space : null
    }, child);
  }));
}
Stack.displayName = 'Stack';
function _templateObject$16() {
  var data = _taggedTemplateLiteral$16(["\n  float: ", ";\n"]);
  _templateObject$16 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$16(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledBox$2 = styled(Box)(_templateObject$16(), function (props) {
  return props.$float ? 'right' : 'none';
});
function OptionalLabel(props) {
  var _float = props["float"];
  return /*#__PURE__*/React.createElement(StyledBox$2, {
    "$float": _float,
    as: "span",
    fontSize: "200",
    fontWeight: "400",
    lineHeight: "200",
    m: "0",
    color: "gray.700"
  }, "Optional");
}
OptionalLabel.displayName = 'OptionalLabel';
function _templateObject$15() {
  var data = _taggedTemplateLiteral$15(["\n  border: none;\n  padding: 0;\n  ", "\n"]);
  _templateObject$15 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$15(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledGroup$1 = styled('fieldset')(_templateObject$15(), margin);
function Group$4(props) {
  var dataId = props['data-id'],
    children = props.children,
    className = props.className,
    label = props.label,
    labelHidden = props.labelHidden,
    required = props.required,
    optional = props.optional,
    rest = __rest(props, ['data-id', "children", "className", "label", "labelHidden", "required", "optional"]);
  var systemProps = pick$1(rest);
  return /*#__PURE__*/React.createElement(StyledGroup$1, Object.assign({}, systemProps, {
    className: className,
    "data-id": dataId
  }), label && /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement(Label$1, {
    as: "legend",
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), required && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200",
    "aria-hidden": "true"
  }, "*"), optional && /*#__PURE__*/React.createElement(OptionalLabel, null))), /*#__PURE__*/React.createElement(Stack, {
    space: "100"
  }, children));
}
Group$4.displayName = 'Checkbox.Group';

/**
 * Generates an aria-describedby attribute and ids for HelpText and Error if id is present
 *
 * @example
 *  const { describedBy, errorId, helpTextId } = useInputDescribedBy({
 *    id: 'input-id',
 *    hasHelpText: true,
 *    hasError: true
 *  });
 *
 * // Usage
 *  <input {...describedBy} />
 *  <span id={errorId}>This is an error</span>
 *  <span id={helpTextId}>This is helptext</span>
 */

function useInputDescribedBy(_ref) {
  var id = _ref.id,
    hasHelpText = _ref.hasHelpText,
    hasError = _ref.hasError;
  return React.useMemo(function () {
    var errorId = null;
    var helpTextId = null;
    if (id && hasError) {
      errorId = "".concat(id, "-error");
    }
    if (id && hasHelpText) {
      helpTextId = "".concat(id, "-helptext");
    }
    return {
      describedBy: helpTextId || errorId ? {
        'aria-describedby': "".concat(helpTextId || '', " ").concat(errorId || '').trim()
      } : {},
      helpTextId: helpTextId,
      errorId: errorId
    };
  }, [hasHelpText, hasError, id]);
}
function _templateObject6$2() {
  var data = _taggedTemplateLiteral$14(["\n  ", "\n"]);
  _templateObject6$2 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$9() {
  var data = _taggedTemplateLiteral$14(["\n  border: 2px solid ", ";\n  transition: ", " ", ";\n  transition-property: border, background;\n\n  ", "\n\n  input:checked ~ span & {\n    border: 2px solid ", ";\n    background: ", ";\n  }\n\n  input:disabled ~ span & {\n    background: ", ";\n    border: 2px solid ", ";\n  }\n\n  input:disabled:checked ~ span & {\n    background: ", ";\n    border: 2px solid ", ";\n  }\n\n  ", ":hover input:not(:disabled) ~ span & {\n    ", "\n  }\n\n  ", ":hover input:checked:not(:disabled) ~ span & {\n    ", "\n  }\n\n  ", "\n"]);
  _templateObject5$9 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$d() {
  var data = _taggedTemplateLiteral$14(["\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  white-space: nowrap;\n  user-select: none;\n  cursor: ", ";\n"]);
  _templateObject4$d = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$h() {
  var data = _taggedTemplateLiteral$14(["\n  ", "\n"]);
  _templateObject3$h = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$x() {
  var data = _taggedTemplateLiteral$14(["\n  opacity: 0;\n  color: ", ";\n  transition: opacity ", " ", ";\n\n  input:checked ~ span & {\n    opacity: 1;\n  }\n"]);
  _templateObject2$x = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$14() {
  var data = _taggedTemplateLiteral$14(["\n  ", "\n"]);
  _templateObject$14 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$14(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Wrapper$5 = styled.div(_templateObject$14(), margin);
var StyledCheck = styled(Box)(_templateObject2$x(), tokens.color_white, tokens.motionDuration_fast, tokens.motionEase_in_out);
var StyledIndeterminate = styled(Box)(_templateObject3$h(), function (props) {
  return "\n    opacity: ".concat(props.$indeterminate ? '1' : '0', ";\n  ");
});
var StyledLabel$3 = styled('label')(_templateObject4$d(), function (props) {
  return props.$disabled ? 'not-allowed' : 'pointer';
});
var StyledBox$1 = styled(Box)(_templateObject5$9(), function (props) {
  return props.$error ? tokens.color_red_700 : tokens.color_gray_700;
}, tokens.motionDuration_fast, tokens.motionEase_in_out, focusOutline({
  modifier: 'input:focus ~ span &'
}), function (props) {
  return props.$error ? tokens.color_red_700 : tokens.color_blue_700;
}, function (props) {
  return props.$error ? tokens.color_red_700 : tokens.color_blue_700;
}, tokens.color_gray_300, tokens.color_gray_600, tokens.color_gray_600, tokens.color_gray_600, StyledLabel$3, function (props) {
  return !props.$disabled && !props.$indeterminate && !props.$error ? "border: 2px solid ".concat(tokens.color_gray_800, ";") : '';
}, StyledLabel$3, function (props) {
  return !props.$error ? "border: 2px solid ".concat(tokens.color_blue_700, ";") : '';
}, function (props) {
  return props.$indeterminate ? "\n        background: ".concat(tokens.color_blue_700, ";\n        border: 2px solid ").concat(tokens.color_blue_700, ";\n      ") : null;
});
var StyledInput$7 = styled.input(_templateObject6$2(), visuallyHidden);
var Checkbox = /*#__PURE__*/React.forwardRef(function Checkbox(props, userRef) {
  var id = props.id,
    checked = props.checked,
    defaultChecked = props.defaultChecked,
    disabled = props.disabled,
    error = props.error,
    helpText = props.helpText,
    label = props.label,
    labelHidden = props.labelHidden,
    value = props.value,
    rest = __rest(props, ["id", "checked", "defaultChecked", "disabled", "error", "helpText", "label", "labelHidden", "value"]);
  var componentProps = omit(rest, margin.propNames);
  var systemProps = pick$1(rest);
  var isControlled = checked !== undefined;
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId;
  var isIndeterminate = typeof checked === 'string' && checked === 'indeterminate';
  var indeterminateAttributes = isIndeterminate ? {
    'aria-checked': 'mixed',
    checked: false
  } : {
    'aria-checked': checked ? 'true' : 'false',
    checked: Boolean(checked)
  };
  return /*#__PURE__*/React.createElement(Wrapper$5, Object.assign({}, systemProps), /*#__PURE__*/React.createElement(StyledLabel$3, {
    htmlFor: id,
    "$disabled": disabled
  }, /*#__PURE__*/React.createElement(StyledInput$7, Object.assign({
    "aria-invalid": !!error,
    id: id,
    value: value,
    defaultChecked: defaultChecked,
    disabled: disabled,
    type: "checkbox",
    ref: userRef
  }, isControlled ? indeterminateAttributes : {}, describedBy, componentProps)), /*#__PURE__*/React.createElement(Box, {
    as: "span",
    position: "relative",
    flex: "0",
    display: "flex",
    alignItems: "center",
    height: "1.5rem" // Matches height of Label
  }, /*#__PURE__*/React.createElement(StyledBox$1, {
    as: "span",
    display: "inline-block",
    borderRadius: "200",
    size: "1rem",
    lineHeight: "200",
    "$error": !!error,
    "$indeterminate": isIndeterminate
  }), /*#__PURE__*/React.createElement(StyledCheck, {
    position: "absolute",
    width: "1rem",
    height: "1rem",
    as: Check
  }), /*#__PURE__*/React.createElement(StyledIndeterminate, {
    position: "absolute",
    left: "100",
    right: "100",
    height: "2px",
    bg: "white",
    borderRadius: "200",
    "$indeterminate": isIndeterminate
  })), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    pl: "200"
  }, label && /*#__PURE__*/React.createElement(Label$1, {
    as: "span" // Outer wrapper already includes a label
    ,

    id: id,
    labelHidden: labelHidden,
    fontWeight: "400",
    mb: "0" // TODO Remove once margin 0 is baked into Label
  }, label))), helpText && /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId,
    ml: "500",
    mt: "0"
  }, helpText), error && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    error: error,
    ml: "500"
  }));
});
Checkbox.displayName = 'Checkbox';
Checkbox.Group = Group$4;
var ColumnsContext = /*#__PURE__*/React.createContext({});
var verticalAlignment$1 = system$g({
  $alignY: {
    property: 'alignItems',
    defaultScale: {
      center: 'center',
      top: 'flex-start',
      bottom: 'flex-end'
    }
  }
});
var horizontalAlignment$1 = system$g({
  $align: {
    property: 'justifyContent',
    defaultScale: {
      center: 'center',
      left: 'flex-start',
      right: 'flex-end'
    }
  }
});
var reverseColumns = system$g({
  $reverse: {
    property: 'flexDirection',
    defaultScale: {
      "true": 'row-reverse',
      "false": 'row'
    }
  }
});
var negativeMargin = system$g({
  $gutter: {
    property: 'marginLeft',
    scale: 'space',
    transform: function transform(value, scale) {
      return "-".concat(scale[value] || value);
    }
  }
});
function noop$1() {
  return;
}

/**
 * Checks if operating in a browser-based environment
 */

function isBrowser() {
  return typeof window !== 'undefined';
}
/**
 * Checks if window is available to support SSG/SSR builds
 */

function getWindow() {
  if (isBrowser()) {
    return window;
  }
  return {
    matchMedia: noop$1,
    addEventListener: noop$1,
    removeEventListener: noop$1,
    setTimeout: noop$1,
    clearTimeout: noop$1
  };
}
/**
 * Returns true if window is an iframe
 * window.top is not accessible from an iframe
 * @see https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
 */

function isInIframe() {
  return window.self !== window.top;
}
function _slicedToArray$o(arr, i) {
  return _arrayWithHoles$o(arr) || _iterableToArrayLimit$o(arr, i) || _unsupportedIterableToArray$p(arr, i) || _nonIterableRest$o();
}
function _nonIterableRest$o() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$p(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$p(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$p(o, minLen);
}
function _arrayLikeToArray$p(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$o(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$o(arr) {
  if (Array.isArray(arr)) return arr;
}
var queries = ["(min-width: ".concat(tokens.mediaQuery_xl, ")"), "(min-width: ".concat(tokens.mediaQuery_lg, ")"), "(min-width: ".concat(tokens.mediaQuery_md, ")"), "(min-width: ".concat(tokens.mediaQuery_sm, ")"), "(min-width: ".concat(tokens.mediaQuery_xs, ")")]; // This order must be the same as above

var keys = ['xl', 'lg', 'md', 'sm', 'xs'];
/**
 * Hook that returns token breakpoint status based on window width
 *
 * @example
 * const breakpoint = useBreakpoint();
 *
 * Possible `breakpoint` values:
 * ['xl', 'lg', 'md', 'sm', 'xs', 'default'];
 *
 * @see https://usehooks.com/useMedia/
 */

function useBreakpoint() {
  if (!isBrowser()) {
    return 'default';
  }
  var environment = getWindow();
  var list = queries.map(function (q) {
    return environment.matchMedia(q);
  });
  function getValue() {
    // Get index of first media query that matches
    var index = list.findIndex(function (mql) {
      return mql.matches;
    }); // Return related value or 'default' if none (smaller than xs size)

    return typeof keys[index] !== 'undefined' ? keys[index] : 'default';
  } // State and setter for matched value

  var _React$useState = React.useState(getValue),
    _React$useState2 = _slicedToArray$o(_React$useState, 2),
    value = _React$useState2[0],
    setValue = _React$useState2[1];
  React.useEffect(function () {
    // Event listener callback
    // Note: By defining getValue outside of useEffect we ensure that it has
    // current values of hook args (as this hook callback is created once on mount).
    var handler = function handler() {
      return setValue(getValue);
    }; // Set a listener for each media query with above handler as callback.

    list.forEach(function (mql) {
      return mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
    }); // Remove listeners on cleanup

    return function () {
      return list.forEach(function (mql) {
        return mql.addEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
      });
    };
  }, []);
  return value;
}
function _templateObject$13() {
  var data = _taggedTemplateLiteral$13(["\n  ", "\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$13 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$13(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var breakpoints$1 = ['default', 'xs', 'sm', 'md', 'lg', 'xl'];
var StyledColumns = styled(Box)(_templateObject$13(), verticalAlignment$1, horizontalAlignment$1, reverseColumns, negativeMargin);
var Columns = /*#__PURE__*/React.forwardRef(function Columns(props, userRef) {
  var children = props.children,
    reverse = props.reverse,
    _props$space = props.space,
    space = _props$space === void 0 ? '500' : _props$space,
    alignY = props.alignY,
    align = props.align,
    collapseBelow = props.collapseBelow,
    rest = __rest(props, ["children", "reverse", "space", "alignY", "align", "collapseBelow"]);
  var systemProps = pick(rest, margin.propNames);
  var breakpoint = useBreakpoint();
  var collapsed = React.useMemo(function () {
    var arr = breakpoints$1.slice(breakpoints$1.findIndex(function (bp) {
      return bp === breakpoint;
    }));
    return arr.includes(collapseBelow);
  }, [breakpoint, collapseBelow]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({}, systemProps, {
    "data-id": props['data-id']
  }), /*#__PURE__*/React.createElement(StyledColumns, {
    display: "flex",
    "$alignY": alignY,
    "$align": align,
    ref: userRef,
    "$reverse": reverse,
    "$gutter": space,
    flexWrap: collapsed ? 'wrap' : 'nowrap',
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(ColumnsContext.Provider, {
    value: {
      space: space,
      collapsed: collapsed
    }
  }, children)));
});
Columns.displayName = 'Columns';
var gutter = system$g({
  $gutter: {
    property: 'marginLeft',
    scale: 'space'
  }
});
system$g({
  width: {
    property: 'width',
    scale: 'layout'
  }
});
function _templateObject$12() {
  var data = _taggedTemplateLiteral$12(["\n  ", "\n  ", "\n\n  &:first-child {\n    padding-top: 0;\n  }\n"]);
  _templateObject$12 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$12(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledColumn = styled(Box)(_templateObject$12(), display, gutter);
var Column$1 = /*#__PURE__*/React.forwardRef(function Column(props, ref) {
  var width = props.width,
    children = props.children,
    className = props.className,
    display = props.display;
  var _React$useContext = React.useContext(ColumnsContext),
    space = _React$useContext.space,
    collapsed = _React$useContext.collapsed;
  var widthOverride = null;
  if (collapsed) {
    widthOverride = '100%';
  }
  if (width === 'content') {
    widthOverride = 'auto';
  }
  return /*#__PURE__*/React.createElement(StyledColumn, {
    display: display,
    "data-id": props['data-id'],
    className: className,
    width: widthOverride || width,
    flex: !width && !collapsed ? '1' : '',
    pt: collapsed ? space : null,
    "$gutter": space,
    ref: ref,
    tabIndex: -1
  }, children);
});
Column$1.displayName = 'Column';
function _templateObject2$w() {
  var data = _taggedTemplateLiteral$11(["\n  border: none;\n  padding: 0;\n  ", "\n"]);
  _templateObject2$w = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$11() {
  var data = _taggedTemplateLiteral$11(["\n  ", "\n"]);
  _templateObject$11 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$11(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var CardWrapper$1 = styled.div(_templateObject$11(), function (props) {
  if (props.$space === 'compact') {
    return "\n        & > div:not(:first-child) label {\n          margin-top: -1px;\n          border-top-right-radius: 0;\n          border-top-left-radius: 0;\n        }\n        & > div:not(:last-child) label {\n          border-bottom-right-radius: 0;\n          border-bottom-left-radius: 0;\n        }\n      ";
  }
  return "";
});
var Fieldset$1 = styled.fieldset(_templateObject2$w(), margin);
var Group$3 = /*#__PURE__*/React.forwardRef(function Group(props, userRef) {
  var children = props.children,
    collapseBelow = props.collapseBelow,
    helpText = props.helpText,
    id = props.id,
    label = props.label,
    labelHidden = props.labelHidden,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'vertical' : _props$orientation,
    optional = props.optional,
    _props$space = props.space,
    space = _props$space === void 0 ? 'default' : _props$space,
    rest = __rest(props, ["children", "collapseBelow", "helpText", "id", "label", "labelHidden", "orientation", "optional", "space"]);
  var items = React.Children.toArray(children);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(Fieldset$1, Object.assign({
    "data-id": rest['data-id'],
    id: id,
    ref: userRef
  }, systemProps), /*#__PURE__*/React.createElement(Label$1, {
    as: "legend",
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), optional && /*#__PURE__*/React.createElement(OptionalLabel, null)), helpText && /*#__PURE__*/React.createElement(Box, {
    mb: "200"
  }, /*#__PURE__*/React.createElement(HelpText, {
    mt: "0"
  }, helpText)), orientation === 'horizontal' && /*#__PURE__*/React.createElement(Columns, {
    space: "300",
    collapseBelow: collapseBelow
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement(Column$1, {
      key: i
    }, item);
  })), orientation === 'vertical' && /*#__PURE__*/React.createElement(CardWrapper$1, {
    "$space": space
  }, space !== 'compact' ? /*#__PURE__*/React.createElement(Stack, {
    space: "300"
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  }))), orientation === 'grid' && /*#__PURE__*/React.createElement(Box, {
    display: "grid",
    gridTemplateColumns: ['1fr', null, '1fr 1fr'],
    gridGap: "300"
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  })));
});
Group$3.displayName = 'CheckboxCard.Group';
function _templateObject5$8() {
  var data = _taggedTemplateLiteral$10(["\n  input:checked ~ ", " & {\n    color: ", ";\n  }\n\n  input:disabled ~ ", " & {\n    color: ", ";\n  }\n"]);
  _templateObject5$8 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$c() {
  var data = _taggedTemplateLiteral$10(["\n  ", "\n  opacity: 0;\n  left: -1.25rem;\n  fill: ", ";\n\n  input:checked ~ ", " & {\n    opacity: 1;\n  }\n"]);
  _templateObject4$c = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$g() {
  var data = _taggedTemplateLiteral$10(["\n  ", "\n  opacity: 1;\n  fill: ", ";\n\n  input:checked ~ ", " & {\n    opacity: 0;\n  }\n"]);
  _templateObject3$g = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$v() {
  var data = _taggedTemplateLiteral$10(["\n  ", "\n  &:hover {\n    & + ", " {\n      z-index: ", ";\n    }\n  }\n  &:checked {\n    & + ", " {\n      z-index: ", ";\n    }\n  }\n  &:focus {\n    & + ", " {\n      z-index: ", ";\n    }\n  }\n"]);
  _templateObject2$v = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$10() {
  var data = _taggedTemplateLiteral$10(["\n  display: block;\n  position: relative;\n  background: ", ";\n  padding: ", ";\n  border: ", ";\n  border-radius: ", ";\n  cursor: pointer;\n  transition: ", ";\n  transition-property: box-shadow, border;\n  ", "\n\n  &:hover {\n    border: ", " solid\n      ", ";\n  }\n\n  input:checked ~ & {\n    border: ", " solid\n      ", ";\n  }\n\n  input:disabled ~ & {\n    cursor: not-allowed;\n    background: ", ";\n    border: ", " solid\n      ", ";\n    color: ", ";\n  }\n"]);
  _templateObject$10 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$10(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledLabel$2 = styled(Box)(_templateObject$10(), function (props) {
  return props.theme.colors.white;
}, function (props) {
  return props.$size === 'small' ? props.theme.space['200'] : props.theme.space['500'];
}, function (props) {
  return props.theme.borders['400'];
}, function (props) {
  return props.theme.radii['200'];
}, tokens.motionDuration_medium, focusOutline({
  offset: '2px',
  modifier: 'input:focus ~ &'
}), function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['400'] : props.theme.colors.gray['600'];
}, function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['400'] : props.theme.colors.blue['700'];
}, function (props) {
  return props.theme.colors.gray['100'];
}, function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.theme.colors.gray['300'];
}, function (props) {
  return props.theme.colors.gray['800'];
});
var StyledInput$6 = styled.input(_templateObject2$v(), visuallyHidden, StyledLabel$2, tokens.zIndex_default, StyledLabel$2, tokens.zIndex_above, StyledLabel$2, tokens.zIndex_above + 1);
var commonCheckStyle$1 = function commonCheckStyle(props) {
  return "\n  position: relative;\n  transition: opacity ".concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", fill ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", box-shadow ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n  \n  input:disabled ~ ").concat(StyledLabel$2, " & {\n    fill: ").concat(props.theme.colors.gray['700'], ";\n  }\n");
};
var StyledUnchecked$2 = styled.svg(_templateObject3$g(), function (props) {
  return commonCheckStyle$1(props);
}, function (props) {
  return props.theme.colors.gray['700'];
}, StyledLabel$2);
var StyledChecked$2 = styled.svg(_templateObject4$c(), function (props) {
  return commonCheckStyle$1(props);
}, function (props) {
  return props.theme.colors.blue['700'];
}, StyledLabel$2);
var StyledHeader$2 = styled(Box)(_templateObject5$8(), StyledLabel$2, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['800'] : props.theme.colors.blue['700'];
}, StyledLabel$2, function (props) {
  return props.theme.colors.gray['800'];
});
var CheckboxCard = /*#__PURE__*/React.forwardRef(function CheckboxCard(props, userRef) {
  var id = props.id,
    checked = props.checked,
    children = props.children,
    defaultChecked = props.defaultChecked,
    disabled = props.disabled,
    label = props.label,
    name = props.name,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    _props$size = props.size,
    size = _props$size === void 0 ? 'default' : _props$size,
    value = props.value,
    dataId = props['data-id'],
    dataTrack = props['data-track'],
    rest = __rest(props, ["id", "checked", "children", "defaultChecked", "disabled", "label", "name", "onChange", "onFocus", "onBlur", "size", "value", 'data-id', 'data-track']);
  var isSmall = size === 'small';
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "radio-card"
  }, /*#__PURE__*/React.createElement(StyledInput$6, Object.assign({
    checked: checked,
    "data-id": dataId,
    "data-track": dataTrack,
    disabled: disabled,
    defaultChecked: defaultChecked,
    id: id,
    name: name,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: userRef,
    type: "checkbox",
    value: value
  }, rest)), /*#__PURE__*/React.createElement(StyledLabel$2, {
    as: "label",
    htmlFor: id,
    "$size": size
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 auto",
    width: isSmall ? '200' : '400'
  }, /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    top: isSmall ? '100' : '500',
    left: isSmall ? '200' : '500'
  }, /*#__PURE__*/React.createElement(StyledUnchecked$2, {
    size: "1.25rem",
    as: CheckBoxOutlineBlank
  }), /*#__PURE__*/React.createElement(StyledChecked$2, {
    size: "1.25rem",
    as: CheckBox
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    pl: isSmall ? '400' : '300'
  }, /*#__PURE__*/React.createElement(StyledHeader$2, {
    "data-id": "radio-card-header",
    fontSize: isSmall ? '200' : '300',
    lineHeight: isSmall ? '200' : '400',
    fontWeight: isSmall ? 'medium' : 'semibold',
    "$size": size
  }, label))), children && /*#__PURE__*/React.createElement(Box, {
    "data-id": "checkbox-card-content",
    pt: isSmall ? '0' : '200',
    pl: isSmall ? '500' : '0',
    fontSize: isSmall ? '200' : '300',
    lineHeight: isSmall ? '200' : '300',
    color: isSmall ? 'gray.700' : 'gray.900'
  }, children)));
});
CheckboxCard.displayName = 'CheckboxCard';
CheckboxCard.Group = Group$3;
var lineHeight = '25px';
var fontSize = tokens.fontSize_100;
var pre = function pre(props) {
  return "\n  position: relative;\n  display: grid;\n  grid-template-columns: ".concat(tokens.spacing_700, " auto;\n  font-family: ").concat(tokens.fontFamily_monospace, ";\n  border-radius: ").concat(tokens.borderRadius_100, ";\n  background-color: ").concat(props.$dark ? tokens.color_gray_900 : tokens.color_gray_100, ";\n  border: 1px solid ").concat(tokens.color_gray_400, ";\n  overflow: auto;\n  padding: ").concat(tokens.spacing_600, " ").concat(tokens.spacing_400, " ").concat(tokens.spacing_600, " 0;\n");
};
var code = function code(props) {
  return "\n  position: absolute;\n  padding: ".concat(tokens.spacing_600, " ").concat(tokens.spacing_600, " ").concat(tokens.spacing_600, " ").concat(tokens.spacing_800, ";\n  color: ").concat(props.$dark ? tokens.color_white : tokens.color_gray_800, ";\n  font-size: ").concat(fontSize, ";\n  line-height: ").concat(lineHeight, ";\n");
};
var line = function line(props) {
  return "\n  display: block;\n  text-align: right;\n  line-height: ".concat(lineHeight, ";\n  font-size: ").concat(fontSize, ";\n  color: ").concat(props.$dark ? tokens.color_white : tokens.color_gray_700, ";\n  user-select: none;\n");
};
var prefix = function prefix() {
  return "\n  padding-right: ".concat(tokens.spacing_200, ";\n");
};
var chevron$2 = function chevron() {
  return "\n  width: ".concat(tokens.fontSize_400, ";\n  height: ").concat(tokens.fontSize_400, ";\n  fill: ").concat(tokens.color_gray_500, ";\n");
};
function _templateObject5$7() {
  var data = _taggedTemplateLiteral$$(["\n  ", "\n"]);
  _templateObject5$7 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$b() {
  var data = _taggedTemplateLiteral$$(["\n  ", "\n"]);
  _templateObject4$b = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$f() {
  var data = _taggedTemplateLiteral$$(["\n  ", "\n"]);
  _templateObject3$f = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$u() {
  var data = _taggedTemplateLiteral$$(["\n  ", "\n"]);
  _templateObject2$u = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$$() {
  var data = _taggedTemplateLiteral$$(["\n  ", "\n"]);
  _templateObject$$ = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$$(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledPre = styled('pre')(_templateObject$$(), pre);
var StyledCode = styled('code')(_templateObject2$u(), code);
var StyledCodePrefix = styled('div')(_templateObject3$f(), prefix);
var StyledLineNumber = styled('span')(_templateObject4$b(), line);
var StyledChevron$2 = styled(ChevronRight)(_templateObject5$7(), chevron$2);
function CodeBlock(props) {
  var children = props.children,
    code = props.code,
    height = props.height,
    className = props.className,
    _props$numbered = props.numbered,
    numbered = _props$numbered === void 0 ? false : _props$numbered,
    _props$dark = props.dark,
    dark = _props$dark === void 0 ? false : _props$dark;
  return /*#__PURE__*/React.createElement(StyledPre, {
    className: className,
    style: {
      height: height ? "".concat(height, "px") : 'auto'
    },
    "$dark": dark
  }, /*#__PURE__*/React.createElement(CodePrefix, {
    dark: dark,
    code: code,
    numbered: numbered
  }), /*#__PURE__*/React.createElement(StyledCode, {
    "$dark": dark
  }, children ? React.Children.map(children, function (child) {
    if ( /*#__PURE__*/React.isValidElement(child)) {
      return /*#__PURE__*/React.cloneElement(child, {
        children: code
      });
    }
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, code)));
}
function CodePrefix(_ref) {
  var code = _ref.code,
    numbered = _ref.numbered,
    dark = _ref.dark;
  var rows = code.split(/\r\n|\r|\n/);
  return /*#__PURE__*/React.createElement(StyledCodePrefix, {
    "aria-hidden": "true"
  }, rows.map(function (_row, rowIndex) {
    var key = "code-block-row-".concat(rowIndex);
    if (numbered) {
      return /*#__PURE__*/React.createElement(StyledLineNumber, {
        "$dark": dark,
        key: key
      }, rowIndex + 1, " ");
    }
    return /*#__PURE__*/React.createElement(StyledLineNumber, {
      "$dark": dark,
      key: key
    }, /*#__PURE__*/React.createElement(StyledChevron$2, null));
  }));
}
function ComboBox(props) {
  var children = props.children,
    style = props.style,
    rootRef = props.rootRef,
    rest = __rest(props, ["children", "style", "rootRef"]); // Clones children in order to pass the menu into the textfield
  // Textfield inserts the menu in the DOM directly after the input, before helptext and error

  var textfieldWithMenu = React.useMemo(function () {
    return getChild('ComboBoxTextField', children, {
      children: getChild('ComboBoxMenu', children)
    });
  }, [children]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    position: "relative",
    ref: rootRef,
    style: style
  }, rest), textfieldWithMenu);
}
function secondsToMS(secondsStr) {
  return Number(secondsStr.replace(/\D/g, '')) * 100;
}
var content$2 = function content(props) {
  return "\n  position: absolute;\n  background: ".concat(tokens.color_white, ";\n  border: ").concat(props.theme.borders['300'], ";\n  border-radius: ").concat(tokens.borderRadius_100, ";\n  box-shadow: ").concat(tokens.boxShadow_100, ";\n");
};
var transition = function transition(props) {
  var visible = "\n    opacity: 1;\n    transform: translateY(0);\n    transition: opacity ".concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ",\n                transform ").concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ";\n  ");
  var hidden = "\n    opacity: 0;\n    pointer-events: none;\n    transform: translateY(".concat(props.isTop ? tokens.spacing_100 : "-".concat(tokens.spacing_100), ");\n    transition: opacity ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ",\n                transform ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ";\n  ");
  switch (props.state) {
    case 'entered':
      return "\n        ".concat(visible, "\n        pointer-events: auto;\n        visibility: visible;\n      ");
    case 'exiting':
      return "\n        ".concat(hidden, "\n        visibility: visible;\n      ");
    case 'exited':
    case 'entering':
    default:
      return "\n        ".concat(hidden, "\n        visibility: hidden;\n      ");
  }
};
function _templateObject$_() {
  var data = _taggedTemplateLiteral$_(["\n  ", "\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$_ = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$_(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$9 = compose(layout, padding);
var StyledContent$5 = styled('div')(_templateObject$_(), system$9, content$2, transition, variant({
  variants: {
    bottomLeft: {
      right: '0',
      left: 'auto',
      top: '100%',
      bottom: 'auto',
      mt: '100'
    },
    bottomRight: {
      left: '0',
      right: 'auto',
      top: '100%',
      bottom: 'auto',
      mt: '100'
    },
    topRight: {
      left: '0',
      right: 'auto',
      top: 'auto',
      bottom: '100%',
      mb: '100'
    },
    topLeft: {
      right: '0',
      left: 'auto',
      top: 'auto',
      bottom: '100%',
      mb: '100'
    }
  }
}));
function getPositionFromDeprecatedProps(_ref) {
  var top = _ref.top,
    left = _ref.left;
  if (left && !top) {
    return 'bottomLeft';
  }
  if (!left && !top) {
    return 'bottomRight';
  }
  if (left && top) {
    return 'topLeft';
  }
  if (!left && top) {
    return 'topRight';
  }
}
var Content$3 = /*#__PURE__*/React.forwardRef(function Content(props, userRef) {
  var children = props.children,
    open = props.open,
    _props$top = props.top,
    top = _props$top === void 0 ? false : _props$top,
    _props$left = props.left,
    left = _props$left === void 0 ? false : _props$left,
    position = props.position,
    sectioned = props.sectioned,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    style = props.style,
    rest = __rest(props, ["children", "open", "top", "left", "position", "sectioned", "className", "style"]);
  var transitionRef = React.useRef();
  var systemProps = pick$1(rest); // TODO: Remove in future

  var variant = position || getPositionFromDeprecatedProps({
    top: top,
    left: left
  });
  return /*#__PURE__*/React.createElement(Transition, {
    mountOnEnter: true,
    unmountOnExit: true,
    "in": open,
    timeout: {
      enter: 0,
      exit: secondsToMS(tokens.motionDuration_fast)
    },
    nodeRef: transitionRef
  }, function (state) {
    return /*#__PURE__*/React.createElement(Box, {
      position: "relative",
      height: "100%",
      ref: userRef,
      tabIndex: -1
    }, /*#__PURE__*/React.createElement(StyledContent$5, Object.assign({
      "data-id": "popover-content",
      className: className,
      p: sectioned ? '400' : null,
      minWidth: "13rem" // 208px
      ,

      variant: variant,
      // TODO: remove in future, isTop is still needed for animation
      isTop: variant.toString().includes('top'),
      state: state
    }, style, systemProps, {
      ref: transitionRef
    }), /*#__PURE__*/React.createElement("div", null, children)));
  });
});
Content$3.displayName = 'Popover.Content';
function ComboBoxMenu(props) {
  var _props$items = props.items,
    items = _props$items === void 0 ? [] : _props$items,
    menuRef = props.menuRef,
    isOpen = props.isOpen,
    _props$maxHeight = props.maxHeight,
    maxHeight = _props$maxHeight === void 0 ? '20rem' : _props$maxHeight,
    _props$emptyMessage = props.emptyMessage,
    emptyMessage = _props$emptyMessage === void 0 ? 'No Results' : _props$emptyMessage,
    rest = __rest(props, ["items", "menuRef", "isOpen", "maxHeight", "emptyMessage"]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    ref: menuRef
  }, rest, {
    position: "relative",
    zIndex: "overlay"
  }), /*#__PURE__*/React.createElement(Content$3, {
    open: isOpen,
    width: "100%"
  }, items.length > 0 ? /*#__PURE__*/React.createElement(ActionList, {
    maxHeight: maxHeight
  }, items.map(function (action, i) {
    return /*#__PURE__*/React.createElement(ActionList.Action, Object.assign({
      key: i
    }, action));
  })) : /*#__PURE__*/React.createElement(Box, {
    p: "200",
    color: "gray.700"
  }, emptyMessage)));
}
var tagBase = function tagBase(props) {
  return "\n  display: inline-flex;\n  align-items: center;\n  white-space: nowrap;\n  padding: 0 ".concat(tokens.spacing_200, ";\n  padding-right: ").concat(props.$hasRemove ? '1px' : tokens.spacing_200, ";\n  border-radius: ").concat(tokens.borderRadius_pill, ";\n  height: 1.5rem;\n  font-size: ").concat(tokens.fontSize_200, ";\n  font-weight: ").concat(tokens.fontWeight_normal, ";\n");
}; // Includes button reset styles
// Key events (space, enter) inherit the onClick handler through <button>

var closeBase = function closeBase() {
  return "\n  border: none;\n  background: none;\n  padding: 0;\n  display: inline-flex;\n  width: 1.25rem;\n  height: 1.25rem;\n  align-items: center;\n  justify-content: center;\n  margin-left: ".concat(tokens.spacing_100, ";\n  border-radius: ").concat(tokens.borderRadius_circle, ";\n  transition-duration: 0.15s;\n  transition-property: background;\n");
}; // This margin is a little funky, however, appears to be the best route to take to add
// spacing between icons and other tag content. Ideally, if Tag instances do not have bare
// strings inside, then the sibling combinator could be used (i.e., `> * + svg { margin-left: 2px; }`),
// however, that's not possible as bare strings do not count as sibling elements.

var content$1 = function content() {
  return "\n  display: inline-flex;\n  align-items: center;\n  & > svg {\n    margin: 0 2px;\n  }\n";
};
var tagColor = function tagColor(props) {
  var bg = '';
  var color = '';
  switch (props.$tagColor) {
    case 'orange':
    case 'blue':
    case 'navy':
      bg = tokens.color_blue_400;
      color = tokens.color_blue_1000;
      break;
    case 'green':
      bg = tokens.color_green_400;
      color = tokens.color_green_1000;
      break;
    case 'yellow':
      bg = tokens.color_yellow_300;
      color = tokens.color_yellow_1000;
      break;
    case 'red':
      bg = tokens.color_red_400;
      color = tokens.color_red_1000;
      break;
    case 'magenta':
      bg = tokens.color_magenta_400;
      color = tokens.color_magenta_1000;
      break;
    case 'purple':
      bg = tokens.color_purple_400;
      color = tokens.color_purple_1000;
      break;
    case 'teal':
      bg = tokens.color_teal_500;
      color = tokens.color_teal_1000;
      break;
    case 'darkGray':
      bg = tokens.color_gray_500;
      color = tokens.color_gray_1000;
      break;
    case 'lightGray':
    default:
      bg = tokens.color_gray_300;
      color = tokens.color_gray_1000;
  }
  return "\n    background: ".concat(bg, ";\n    color: ").concat(color, ";\n  ");
};
var closeColor = function closeColor(props) {
  var bg = '';
  var color = '';
  switch (props.$tagColor) {
    case 'orange':
    case 'blue':
    case 'navy':
      bg = tokens.color_blue_500;
      color = tokens.color_blue_800;
      break;
    case 'green':
      bg = tokens.color_green_500;
      color = tokens.color_green_900;
      break;
    case 'yellow':
      bg = tokens.color_yellow_500;
      color = tokens.color_yellow_700;
      break;
    case 'red':
      bg = tokens.color_red_500;
      color = tokens.color_red_800;
      break;
    case 'magenta':
      bg = tokens.color_magenta_500;
      color = tokens.color_magenta_800;
      break;
    case 'purple':
      bg = tokens.color_purple_500;
      color = tokens.color_purple_800;
      break;
    case 'teal':
      bg = tokens.color_teal_600;
      color = tokens.color_teal_800;
      break;
    case 'darkGray':
      bg = tokens.color_gray_600;
      color = tokens.color_gray_900;
      break;
    case 'lightGray':
    default:
      bg = tokens.color_gray_400;
      color = tokens.color_gray_900;
      break;
  }
  return "\n    color: ".concat(color, ";\n    &:hover {\n      color: ").concat(color, ";\n      background: ").concat(bg, ";\n      cursor: pointer;\n    }\n  ");
};
function _templateObject3$e() {
  var data = _taggedTemplateLiteral$Z(["\n  ", "\n"]);
  _templateObject3$e = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$t() {
  var data = _taggedTemplateLiteral$Z(["\n  ", "\n  ", "\n"]);
  _templateObject2$t = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$Z() {
  var data = _taggedTemplateLiteral$Z(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$Z = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$Z(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledTag = styled(Box)(_templateObject$Z(), tagBase, tagColor, margin);
var StyledClose$1 = styled.button(_templateObject2$t(), closeBase, closeColor);
var StyledContent$4 = styled.span(_templateObject3$e(), content$1);
var Tag = /*#__PURE__*/React.forwardRef(function Tag(props, userRef) {
  var color = props.color,
    children = props.children,
    onRemove = props.onRemove,
    className = props.className,
    dataId = props['data-id'],
    id = props.id,
    rest = __rest(props, ["color", "children", "onRemove", "className", 'data-id', "id"]);
  var systemProps = pick(rest, margin.propNames);
  var closeMarkup = onRemove ? /*#__PURE__*/React.createElement(StyledClose$1, {
    onClick: onRemove,
    "$tagColor": color,
    type: "button"
  }, /*#__PURE__*/React.createElement(Close, {
    size: 16
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Remove")) : null;
  return /*#__PURE__*/React.createElement(StyledTag, Object.assign({
    as: "span",
    className: className,
    "data-id": dataId,
    id: id,
    "$tagColor": color,
    "$hasRemove": !!onRemove
  }, systemProps, {
    ref: userRef
  }), /*#__PURE__*/React.createElement(StyledContent$4, null, children), closeMarkup);
});
Tag.displayName = 'Tag';
function noop() {
  return undefined;
}
function identity(a) {
  return a;
}
function isNotTouchEvent(e) {
  return e.touches.length > 1 || e.type.toLowerCase() === 'touchend' && e.touches.length > 0;
}
function _templateObject2$s() {
  var data = _taggedTemplateLiteral$Y(["\n  display: inline-block;\n  flex: 1;\n  background: none;\n  border: none;\n  outline: none;\n  padding-left: ", ";\n  padding-right: ", ";\n  line-height: calc(", " - 2px);\n  height: calc(", " - 2px);\n  font-weight: ", ";\n  color: ", ";\n  &:disabled {\n    cursor: not-allowed;\n  }\n"]);
  _templateObject2$s = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$Y() {
  var data = _taggedTemplateLiteral$Y(["\n  display: flex;\n  flex-wrap: wrap;\n  background: ", ";\n  border: ", ";\n  border-radius: ", ";\n  min-height: ", ";\n\n  ", "\n"]);
  _templateObject$Y = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$Y(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var inputHeight = '2.5rem';
var StyledInputWrapper$2 = styled(Box)(_templateObject$Y(), function (props) {
  return props.$isDisabled ? tokens.color_gray_200 : tokens.color_white;
}, function (props) {
  return props.$hasError ? "".concat(tokens.borderWidth_100, " solid ").concat(tokens.color_red_700) : "".concat(tokens.borderWidth_100, " solid ").concat(tokens.color_gray_400);
}, tokens.borderRadius_100, inputHeight, focusOutline({
  modifier: '&:focus-within'
}));
var StyledInput$5 = styled('input')(_templateObject2$s(), tokens.spacing_300, tokens.spacing_300, inputHeight, inputHeight, tokens.fontWeight_normal, tokens.color_gray_900);
function _templateObject$X() {
  var data = _taggedTemplateLiteral$X(["\n  ", "\n"]);
  _templateObject$X = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$X(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledWrapper$8 = styled('div')(_templateObject$X(), margin);
function ComboBoxTextField(props) {
  var autoFocus = props.autoFocus,
    disabled = props.disabled,
    delimiter = props.delimiter,
    children = props.children,
    error = props.error,
    errorInLabel = props.errorInLabel,
    helpText = props.helpText,
    id = props.id,
    _props$itemToString = props.itemToString,
    itemToString = _props$itemToString === void 0 ? identity : _props$itemToString,
    label = props.label,
    labelHidden = props.labelHidden,
    name = props.name,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onKeyDown = props.onKeyDown,
    placeholder = props.placeholder,
    readOnly = props.readOnly,
    _props$removeItem = props.removeItem,
    removeItem = _props$removeItem === void 0 ? noop : _props$removeItem,
    required = props.required,
    style = props.style,
    _props$selectedItems = props.selectedItems,
    selectedItems = _props$selectedItems === void 0 ? [] : _props$selectedItems,
    value = props.value,
    rest = __rest(props, ["autoFocus", "disabled", "delimiter", "children", "error", "errorInLabel", "helpText", "id", "itemToString", "label", "labelHidden", "name", "onChange", "onFocus", "onBlur", "onKeyDown", "placeholder", "readOnly", "removeItem", "required", "style", "selectedItems", "value"]);
  var inputProps = omit(rest, margin.propNames);
  var systemProps = pick$1(rest);
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId; // Removes last item with a backspace and and empty input value

  function handleKeyDown(e) {
    if (!value && selectedItems.length) {
      onKey('backspace', function () {
        removeItem(selectedItems[selectedItems.length - 1]);
      })(e);
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  }
  return /*#__PURE__*/React.createElement(StyledWrapper$8, Object.assign({}, systemProps), label && /*#__PURE__*/React.createElement(Label$1, {
    id: id,
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), required && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200",
    "aria-hidden": "true"
  }, "*"), error && errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    wrapper: "span"
  }, error)), /*#__PURE__*/React.createElement(StyledInputWrapper$2, {
    "$hasError": !!error,
    "$isDisabled": disabled
  }, selectedItems.length > 0 && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    pl: "200",
    pt: "0.375rem"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "100"
  }, selectedItems.map(function (item, i) {
    return /*#__PURE__*/React.createElement("span", {
      key: i
    }, /*#__PURE__*/React.createElement(Tag, {
      onRemove: !disabled ? function () {
        return removeItem(item);
      } : null
    }, itemToString(item)), delimiter && i < selectedItems.length - 1 ? /*#__PURE__*/React.createElement(Box, {
      as: "span",
      ml: "100",
      fontWeight: "medium",
      fontStyle: "italic",
      style: {
        textTransform: 'uppercase'
      }
    }, delimiter) : null);
  }))), /*#__PURE__*/React.createElement(StyledInput$5, Object.assign({}, describedBy, {
    autoFocus: autoFocus,
    disabled: disabled,
    id: id,
    name: name,
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: onChange,
    onKeyDown: handleKeyDown,
    placeholder: placeholder,
    readOnly: readOnly,
    required: required,
    style: style,
    type: "text",
    value: value
  }, inputProps))), children, helpText && /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId
  }, helpText), error && !errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId
  }, error));
}
function Navbar(props) {
  var onPreviousClick = props.onPreviousClick,
    onNextClick = props.onNextClick,
    showNextButton = props.showNextButton,
    showPreviousButton = props.showPreviousButton;
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "space-between",
    position: "absolute",
    left: "0",
    right: "0"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "0"
  }, /*#__PURE__*/React.createElement(Button$1, {
    variant: "text",
    color: "blue",
    "data-id": "datepicker-previous",
    disabled: !showPreviousButton,
    onClick: function onClick() {
      return onPreviousClick();
    },
    px: "100",
    size: "small"
  }, /*#__PURE__*/React.createElement(ChevronLeft, {
    size: 24
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Previous Month"))), /*#__PURE__*/React.createElement(Box, {
    flex: "0"
  }, /*#__PURE__*/React.createElement(Button$1, {
    variant: "text",
    color: "blue",
    "data-id": "datepicker-next",
    disabled: !showNextButton,
    onClick: function onClick() {
      return onNextClick();
    },
    px: "100",
    size: "small"
  }, /*#__PURE__*/React.createElement(ChevronRight, {
    size: 24
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Next Month"))));
}
Navbar.displayName = 'Navbar';
function Caption(_ref) {
  var classNames = _ref.classNames,
    onClick = _ref.onClick,
    date = _ref.date,
    localeUtils = _ref.localeUtils;
  var month = localeUtils.getMonths()[date.getMonth()];
  var year = date.getFullYear();
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "datepicker-caption",
    className: classNames.caption,
    mx: "200",
    onClick: onClick,
    role: "heading",
    textAlign: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    fontSize: "400",
    lineHeight: "400",
    fontWeight: "semibold"
  }, month), ' ', /*#__PURE__*/React.createElement(Text, {
    as: "span",
    color: "gray.700",
    fontSize: "400",
    lineHeight: "400",
    fontWeight: "normal"
  }, year));
}
Caption.displayName = 'Caption';
var shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function Weekday(_ref) {
  var className = _ref.className,
    weekday = _ref.weekday,
    localeUtils = _ref.localeUtils;
  return /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 0",
    minWidth: "650",
    textAlign: "center",
    role: "columnheader",
    className: className,
    py: "300"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "abbr",
    title: localeUtils.formatWeekdayLong(weekday),
    fontWeight: "medium",
    fontSize: "200",
    lineHeight: "200",
    color: "gray.700",
    textAlign: "center",
    style: {
      textDecoration: 'none'
    }
  }, shortDays[weekday]));
}
Weekday.displayName = 'Weekday';
function _templateObject$W() {
  var data = _taggedTemplateLiteral$W(["\n  background: ", ";\n  color: ", ";\n  cursor: pointer;\n\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n"]);
  _templateObject$W = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$W(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledDay = styled(Box)(_templateObject$W(), tokens.color_white, tokens.color_gray_800, function (props) {
  // Disabled styles
  if (props.$modifiers.disabled) {
    return "\n        opacity: 0.3;\n        cursor: not-allowed;\n      ";
  }
}, function (props) {
  // Today modifier styles
  if (props.$modifiers.today) {
    if (props.$modifiers.disabled) {
      return "\n          box-shadow: inset 0 0 0 1px ".concat(tokens.color_gray_900, ";\n        ");
    }
    return "\n        color: ".concat(tokens.color_blue_800, ";\n        box-shadow: inset 0 0 0 1px ").concat(tokens.color_blue_700, ";\n      ");
  }
}, function (props) {
  // Selected modifier styles
  if (props.$modifiers.selected) {
    return "\n        color: ".concat(tokens.color_blue_800, ";\n        background: ").concat(tokens.color_blue_300, ";\n      ");
  }
}, function (props) {
  // In Between selected modifier styles
  if (props.$modifiers.inBetween) {
    return "\n        color: ".concat(tokens.color_gray_900, ";\n        background: ").concat(tokens.color_blue_200, ";\n      ");
  }
}, function (props) {
  // First and Last selected modifier styles
  if (props.$modifiers.lastSelected || props.$modifiers.firstSelected) {
    return "\n        color: ".concat(tokens.color_blue_800, ";\n        background: ").concat(tokens.color_blue_300, ";\n      ");
  }
}, function (props) {
  // Hover styles
  if (!props.$modifiers.disabled && !props.$modifiers.selected && !props.$modifiers.inBetween && !props.$modifiers.firstSelected && !props.$modifiers.lastSelected) {
    return "\n        &:hover {\n          background: ".concat(tokens.color_gray_200, ";\n        }\n      ");
  }
}); // Daypicker global styles where component overrides are not possible

var wrapper$2 = function wrapper(props) {
  return "\n  position: relative;\n  max-width: ".concat(props.numberOfMonths === 1 ? '17.5rem' : '36.5rem', ";\n\n  .DayPicker-wrapper {\n    position: relative;\n    padding: 0;\n    outline: none;\n\n    *:focus {\n      outline: none;\n      box-shadow: 0 0 0 2px ").concat(tokens.color_blue_700, ";\n      z-index: 1;\n    }\n  }\n\n  .DayPicker-Months {\n    display: flex;\n    flex-wrap: wrap;\n    justify-content: center;\n    flex-direction: row;\n  }\n\n  .DayPicker-Month {\n    flex: 0 0 auto;\n\n    & + .DayPicker-Month {\n      margin-left: ").concat(tokens.spacing_500, ";\n    }\n  }\n\n  .DayPicker-WeekdaysRow,\n  .DayPicker-Week {\n    display: flex;\n  }\n\n  .DayPicker-Day {\n    flex: 1 0;\n  }\n\n  .DayPicker-Day--disabled {\n    pointer-events: none;\n  }\n");
};

/** Supported modifiers:
 * firstSelected
 * lastSelected
 * inBetween
 * today - provided by react-day-picker
 * disabled - provided by react-day-picker
 * outside - provided by react-day-picker
 * */

function renderDay(date, modifiers) {
  if (modifiers.outside) {
    return '';
  }
  return /*#__PURE__*/React.createElement(StyledDay, {
    alignItems: "center",
    display: "inline-flex",
    fontSize: "200",
    height: "600",
    justifyContent: "center",
    lineHeight: "200",
    minWidth: "650",
    "$modifiers": modifiers
  }, date.getDate());
}
function _templateObject$V() {
  var data = _taggedTemplateLiteral$V(["\n  ", "\n  ", "\n"]);
  _templateObject$V = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$V(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Wrapper$4 = styled.div(_templateObject$V(), margin, wrapper$2);
var DatePicker = /*#__PURE__*/React.forwardRef(function DatePicker(props, ref) {
  var _props$fixedWeeks = props.fixedWeeks,
    fixedWeeks = _props$fixedWeeks === void 0 ? false : _props$fixedWeeks,
    _props$enableOutsideD = props.enableOutsideDaysClick,
    enableOutsideDaysClick = _props$enableOutsideD === void 0 ? false : _props$enableOutsideD,
    _props$numberOfMonths = props.numberOfMonths,
    numberOfMonths = _props$numberOfMonths === void 0 ? 2 : _props$numberOfMonths,
    _props$showOutsideDay = props.showOutsideDays,
    showOutsideDays = _props$showOutsideDay === void 0 ? false : _props$showOutsideDay,
    rest = __rest(props, ["fixedWeeks", "enableOutsideDaysClick", "numberOfMonths", "showOutsideDays"]);
  var systemProps = pick$1(props);
  var componentProps = omit(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(Wrapper$4, Object.assign({
    ref: ref
  }, systemProps, {
    "data-id": "datepicker",
    numberOfMonths: numberOfMonths
  }), /*#__PURE__*/React.createElement(DayPicker, Object.assign({
    captionElement: Caption,
    weekdayElement: Weekday,
    numberOfMonths: numberOfMonths,
    enableOutsideDaysClick: enableOutsideDaysClick,
    showOutsideDays: showOutsideDays,
    fixedWeeks: fixedWeeks,
    renderDay: renderDay,
    navbarElement: Navbar
  }, componentProps)));
});
DatePicker.displayName = 'DatePicker';
function Portal(props) {
  var containerId = props.containerId,
    children = props.children;
  if (typeof document !== 'undefined') {
    var container = containerId ? document.getElementById(containerId) : document.body;
    if (container) {
      return /*#__PURE__*/ReactDOM.createPortal(children, container);
    }
  }
  return null;
}
Portal.displayName = 'Portal';
var DrawerContext = /*#__PURE__*/React.createContext({
  footerRef: null,
  onClose: null
});
var Header$4 = /*#__PURE__*/React.forwardRef(function Header(props, ref) {
  var children = props.children,
    _props$showCloseButto = props.showCloseButton,
    showCloseButton = _props$showCloseButto === void 0 ? true : _props$showCloseButto;
  var context = React.useContext(DrawerContext);
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "drawer-header",
    bg: "white",
    p: "500",
    ref: ref
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "h2",
    fontSize: "400",
    lineHeight: "400"
  }, children)), showCloseButton && /*#__PURE__*/React.createElement(Button$1, {
    variant: "text",
    size: "small",
    width: tokens.spacing_600,
    px: "0",
    onClick: context.onClose
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Close"), /*#__PURE__*/React.createElement(Close, {
    size: 25
  }))));
});
Header$4.displayName = 'Drawer.Header';
function _templateObject$U() {
  var data = _taggedTemplateLiteral$U(["\n  ", "\n"]);
  _templateObject$U = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$U(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Container$5 = styled.div(_templateObject$U(), padding);
var Content$2 = /*#__PURE__*/React.forwardRef(function Content(props, ref) {
  var systemProps = pick$1(props);
  return /*#__PURE__*/React.createElement(Container$5, Object.assign({
    "data-id": "drawer-content",
    p: "500"
  }, systemProps, {
    ref: ref
  }), props.children);
});
Content$2.displayName = 'Drawer.Content';
function _templateObject$T() {
  var data = _taggedTemplateLiteral$T(["\n  ", "\n"]);
  _templateObject$T = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$T(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Container$4 = styled.div(_templateObject$T(), padding);
var Footer$2 = /*#__PURE__*/React.forwardRef(function Footer(_a, userRef) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var systemProps = pick$1(rest);
  var context = React.useContext(DrawerContext);
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "drawer-footer",
    bg: "white",
    borderTop: "400",
    bottom: "0",
    position: "fixed",
    left: "0",
    right: "0",
    ref: context.footerRef
  }, /*#__PURE__*/React.createElement(Container$4, Object.assign({
    p: "500"
  }, systemProps, {
    ref: userRef
  }), children));
});
Footer$2.displayName = 'Drawer.Footer';
function _slicedToArray$n(arr, i) {
  return _arrayWithHoles$n(arr) || _iterableToArrayLimit$n(arr, i) || _unsupportedIterableToArray$o(arr, i) || _nonIterableRest$n();
}
function _nonIterableRest$n() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$o(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$o(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$o(o, minLen);
}
function _arrayLikeToArray$o(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$n(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$n(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * SSR friendly hook that copies a string to the clipboard and provides copied state
 *
 * @example
 * const { copy, copied } = useCopyToClipboard({ timeout: 2000 });
 *
 * <Button onClick={() => copy('string')}>
 *  {copied ? 'String has been copied' : 'Copy to clipboard'}
 * </Button>
 */

function useCopyToClipboard() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$timeout = _ref.timeout,
    timeout = _ref$timeout === void 0 ? 1000 : _ref$timeout;
  var environment = getWindow();
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray$n(_React$useState, 2),
    copied = _React$useState2[0],
    setCopied = _React$useState2[1];
  function handleCopy(string) {
    copy(string);
    setCopied(true);
  }
  React.useEffect(function () {
    var timer;
    if (copied) {
      timer = environment.setTimeout(function () {
        return setCopied(false);
      }, timeout);
    }
    return function () {
      environment.clearTimeout(timer);
    };
  }, [copied, timeout]);
  return {
    copy: handleCopy,
    copied: copied
  };
}
function _slicedToArray$m(arr, i) {
  return _arrayWithHoles$m(arr) || _iterableToArrayLimit$m(arr, i) || _unsupportedIterableToArray$n(arr, i) || _nonIterableRest$m();
}
function _nonIterableRest$m() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$n(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$n(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$n(o, minLen);
}
function _arrayLikeToArray$n(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$m(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$m(arr) {
  if (Array.isArray(arr)) return arr;
}
function useDrawer() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$initialOpen = _ref.initialOpen,
    initialOpen = _ref$initialOpen === void 0 ? false : _ref$initialOpen,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? 'matchbox-drawer' : _ref$id;
  var _React$useState = React.useState(initialOpen),
    _React$useState2 = _slicedToArray$m(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  function toggle() {
    setIsOpen(!isOpen);
  }
  function open() {
    setIsOpen(true);
  }
  function close() {
    setIsOpen(false);
  }
  function getActivatorProps() {
    var additionalProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return Object.assign({
      'aria-controls': id,
      onClick: open
    }, additionalProps);
  }
  function getDrawerProps() {
    var additionalProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return Object.assign({
      id: id,
      onClose: close,
      open: isOpen
    }, additionalProps);
  }
  return {
    isOpen: isOpen,
    toggleDrawer: toggle,
    openDrawer: open,
    closeDrawer: close,
    getActivatorProps: getActivatorProps,
    getDrawerProps: getDrawerProps
  };
}
function _slicedToArray$l(arr, i) {
  return _arrayWithHoles$l(arr) || _iterableToArrayLimit$l(arr, i) || _unsupportedIterableToArray$m(arr, i) || _nonIterableRest$l();
}
function _nonIterableRest$l() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$m(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$m(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$m(o, minLen);
}
function _arrayLikeToArray$m(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$l(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$l(arr) {
  if (Array.isArray(arr)) return arr;
}
function useModal() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$initialOpen = _ref.initialOpen,
    initialOpen = _ref$initialOpen === void 0 ? false : _ref$initialOpen,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? 'matchbox-modal' : _ref$id;
  var _React$useState = React.useState(initialOpen),
    _React$useState2 = _slicedToArray$l(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  function toggle() {
    setIsOpen(!isOpen);
  }
  function open() {
    setIsOpen(true);
  }
  function close() {
    setIsOpen(false);
  }
  function getActivatorProps() {
    var additionalProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return Object.assign({
      'aria-controls': id,
      onClick: open
    }, additionalProps);
  }
  function getModalProps() {
    var additionalProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return Object.assign({
      id: id,
      onClose: close,
      open: isOpen
    }, additionalProps);
  }
  return {
    isOpen: isOpen,
    toggleModal: toggle,
    openModal: open,
    closeModal: close,
    getActivatorProps: getActivatorProps,
    getModalProps: getModalProps
  };
}
function _slicedToArray$k(arr, i) {
  return _arrayWithHoles$k(arr) || _iterableToArrayLimit$k(arr, i) || _unsupportedIterableToArray$l(arr, i) || _nonIterableRest$k();
}
function _nonIterableRest$k() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$l(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$l(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$l(o, minLen);
}
function _arrayLikeToArray$l(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$k(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$k(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * @see https://tobbelindstrom.com/blog/resize-observer-hook/
 * @see https://github.com/que-etc/resize-observer-polyfill
 *
 * @returns
 * target: DOM Element,
 * contentRect: {
 *   x: number
 *   y: number
 *   width: number
 *   height: number
 *   top: number
 *   right: number
 *   bottom: number
 *   left: number
 * }
 * @example
 * const Component = () => {
 *  const [ref, entry] = useResizeObserver();
 *  return <div ref={ref} />;
 * };
 */

function useResizeObserver() {
  var _React$useState = React.useState({
      contentRect: {}
    }),
    _React$useState2 = _slicedToArray$k(_React$useState, 2),
    entry = _React$useState2[0],
    setEntry = _React$useState2[1];
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray$k(_React$useState3, 2),
    node = _React$useState4[0],
    setNode = _React$useState4[1];
  var observer = React.useRef(null);
  var disconnect = React.useCallback(function () {
    var current = observer.current;
    current && current.disconnect();
  }, []);
  var observe = React.useCallback(function () {
    observer.current = new ResizeObserver(function (_ref) {
      var _ref2 = _slicedToArray$k(_ref, 1),
        entry = _ref2[0];
      return setEntry(entry);
    });
    node && observer.current.observe(node);
  }, [node]);
  React.useLayoutEffect(function () {
    observe();
    return function () {
      return disconnect();
    };
  }, [disconnect, observe]);
  return [setNode, entry];
}
function _slicedToArray$j(arr, i) {
  return _arrayWithHoles$j(arr) || _iterableToArrayLimit$j(arr, i) || _unsupportedIterableToArray$k(arr, i) || _nonIterableRest$j();
}
function _nonIterableRest$j() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$k(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$k(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$k(o, minLen);
}
function _arrayLikeToArray$k(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$j(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$j(arr) {
  if (Array.isArray(arr)) return arr;
}
function useTabs(_ref) {
  var _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
    _ref$initialSelected = _ref.initialSelected,
    initialSelected = _ref$initialSelected === void 0 ? 0 : _ref$initialSelected;
  var _React$useState = React.useState(initialSelected),
    _React$useState2 = _slicedToArray$j(_React$useState, 2),
    tabIndex = _React$useState2[0],
    setTabIndex = _React$useState2[1];
  var tabsWithOnClick = React.useMemo(function () {
    return tabs.map(function (tab, i) {
      return Object.assign(Object.assign({}, tab), {
        onClick: function onClick() {
          return setTabIndex(i);
        }
      });
    });
  }, [tabs]);
  var onSelect = React.useCallback(function (index) {
    setTabIndex(index);
  }, [tabs]);
  var getTabsProps = function getTabsProps(userProps) {
    return Object.assign({
      tabs: tabsWithOnClick,
      selected: tabIndex,
      onSelect: onSelect
    }, userProps);
  };
  return {
    getTabsProps: getTabsProps,
    tabIndex: tabIndex,
    tabs: tabs,
    setTabIndex: setTabIndex
  };
}

/**
 * Handles global window event listeners in a reusable hook
 * @param {String} event
 * @param {Func} callback
 *
 * @example
 *  function Component() {
 *    useWindowEvent('resize', handleWindowResize);
 *    ...
 *  }
 */

function useWindowEvent(event, callback) {
  if (!isBrowser()) {
    return;
  }
  var environment = getWindow();
  React.useEffect(function () {
    environment.addEventListener(event, callback);
    return function () {
      return environment.removeEventListener(event, callback);
    };
  }, [event, callback]);
}

/**
 * Rounds `number` to the nearest `places` number of places
 * @param number
 * @param places
 * @returns Rounded number
 */
function roundToPlaces(number, places) {
  var multiplier = Math.pow(10, places);
  return Math.round(number * multiplier) / multiplier;
}
/**
 * Clamps `number` between the `lower` and `upper` limits
 * @param number Number to clamp
 * @param lower Lower bounds
 * @param upper Uppwer bounds
 * @returns Clamped number
 */

function clamp(number, lower, upper) {
  if (number < lower) {
    return lower;
  }
  if (number > upper) {
    return upper;
  }
  return number;
}
function getWindowRect() {
  var environment = getWindow();
  return {
    top: environment.scrollY,
    left: environment.scrollX,
    height: environment.innerHeight,
    width: environment.innerWidth
  };
}
function getRectFor(node) {
  // Need to update this soon since findDomNode is deprecated
  var rect = findDOMNode(node); //eslint-disable-line

  if (!rect) {
    return {};
  }
  return rect.getBoundingClientRect();
}
/**
 * Gets preferred direction for the provided react component
 * @param  {React Node} node - a react component
 * @return {Shape}
 * Returns directional booleans for where the component should render. Eg:
 * top: true if component is in the bottom half of the screen
 * right: true if component in the left half of the screen
 */

function getPreferredDirectionFor(node) {
  var windowRect = getWindowRect();
  var elementRect = getRectFor(node);
  var rightOffset = windowRect.width - elementRect.right;
  var bottomOffset = windowRect.height - elementRect.bottom;
  return {
    top: bottomOffset < elementRect.top,
    left: rightOffset < elementRect.left,
    right: rightOffset >= elementRect.left,
    bottom: bottomOffset >= elementRect.top
  };
}
/**
 * Gets coordinates and dimensions in pixels for the provided react component
 * @param  {React Node} node
 * @return {Shape}
 */

function getPositionFor(node) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$fixed = _ref.fixed,
    fixed = _ref$fixed === void 0 ? false : _ref$fixed;
  var windowRect = getWindowRect();
  var elementRect = getRectFor(node);
  return {
    top: elementRect.top + (fixed ? 0 : windowRect.top),
    left: elementRect.left + (fixed ? 0 : windowRect.left),
    width: elementRect.width,
    height: elementRect.height
  };
}
/**
 * Linearly interpolates and clamps between two values
 * @param  {number} min
 * @param  {number} max
 * @param  {number} n
 * @return {number}
 * @example
 *   lerp(10, 20, 0.5)
 *   > 15
 */

function lerp(min, max, n) {
  var value = (max - min) * n + min;
  return clamp(value, min, max);
}
/**
 * Rounds a number to the nearest baseline ceiling
 */

function roundToBaseline(n) {
  var base = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 4;
  return Math.ceil(n / base) * base;
}
function _slicedToArray$i(arr, i) {
  return _arrayWithHoles$i(arr) || _iterableToArrayLimit$i(arr, i) || _unsupportedIterableToArray$j(arr, i) || _nonIterableRest$i();
}
function _nonIterableRest$i() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$j(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$j(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$j(o, minLen);
}
function _arrayLikeToArray$j(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$i(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$i(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * Reusable hook that returns true when element is scrolled into view
 *
 * @param {Boolean} once defaults to true. If set to false the inView param will change when the element is scrolled into and out of view
 *
 * @example
 * const [ref, inView] = useInView({ once: false });
 * return <div>{inView ? 'In View' : 'Not In View'}</div>
 *
 */

function useInView() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$offset = _ref.offset,
    offset = _ref$offset === void 0 ? 0 : _ref$offset,
    _ref$once = _ref.once,
    once = _ref$once === void 0 ? true : _ref$once;
  if (!isBrowser()) {
    return [noop$1, false];
  }
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray$i(_React$useState, 2),
    node = _React$useState2[0],
    setNode = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray$i(_React$useState3, 2),
    inView = _React$useState4[0],
    setInView = _React$useState4[1];
  var _React$useState5 = React.useState(0),
    _React$useState6 = _slicedToArray$i(_React$useState5, 2),
    scroll = _React$useState6[0],
    setScroll = _React$useState6[1];
  var _React$useState7 = React.useState({
      top: 0,
      height: 0
    }),
    _React$useState8 = _slicedToArray$i(_React$useState7, 2),
    rect = _React$useState8[0],
    setRect = _React$useState8[1];
  var environment = getWindow();
  useWindowEvent('scroll', function () {
    setScroll(environment.scrollY);
  });
  React.useEffect(function () {
    setRect(getRectFor(node));
  }, [node]);
  React.useLayoutEffect(function () {
    var top = rect.top,
      height = rect.height;
    var innerHeight = environment.innerHeight;
    if (top) {
      if (scroll >= top + offset - innerHeight) {
        setInView(true);
      }
      if (scroll > top + height || scroll < top - innerHeight) {
        if (!once) {
          setInView(false);
        }
      }
    }
  }, [scroll]);
  return [setNode, inView];
}
function _slicedToArray$h(arr, i) {
  return _arrayWithHoles$h(arr) || _iterableToArrayLimit$h(arr, i) || _unsupportedIterableToArray$i(arr, i) || _nonIterableRest$h();
}
function _nonIterableRest$h() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$i(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$i(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$i(o, minLen);
}
function _arrayLikeToArray$i(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$h(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$h(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * SSR friendly hook that returns prefers-reduced-state status to disable animations
 *
 * @returns
 * 'no-preference' OR 'reduce'
 *
 * @example
 * const prefersReducedMotion = usePrefersReducedMotion();
 * return <div>{prefersReducedMotion}</div>
 */

var QUERY$1 = '(prefers-reduced-motion: reduce)';
function usePrefersReducedMotion() {
  if (!isBrowser()) {
    return 'no-preference';
  }
  var environment = getWindow();
  var matches = environment.matchMedia(QUERY$1) ? environment.matchMedia(QUERY$1).matches : false;
  var _React$useState = React.useState(matches),
    _React$useState2 = _slicedToArray$h(_React$useState, 2),
    prefersReducedMotion = _React$useState2[0],
    setPrefersReducedMotion = _React$useState2[1];
  React.useEffect(function () {
    var mql = environment.matchMedia(QUERY$1);
    var listener = function listener(event) {
      setPrefersReducedMotion(event.matches);
    };
    if (mql && mql.addListener) {
      mql.addListener(listener);
    }
    return function () {
      if (mql && mql.removeListener) {
        mql.removeListener(listener);
      }
    };
  });
  return prefersReducedMotion ? 'reduce' : 'no-preference';
}
function _slicedToArray$g(arr, i) {
  return _arrayWithHoles$g(arr) || _iterableToArrayLimit$g(arr, i) || _unsupportedIterableToArray$h(arr, i) || _nonIterableRest$g();
}
function _nonIterableRest$g() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$h(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$h(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$h(o, minLen);
}
function _arrayLikeToArray$h(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$g(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$g(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * SSR friendly hook that returns prefers-color-scheme status
 *
 * @returns
 * 'dark' OR 'light'
 *
 * @example
 * const prefersColorScheme = usePrefersColorScheme();
 * return <div>{prefersColorScheme}</div>
 */

var QUERY = '(prefers-color-scheme: dark)';
function usePrefersColorScheme() {
  if (!isBrowser()) {
    return 'light';
  }
  var environment = getWindow();
  var matches = environment.matchMedia(QUERY) ? environment.matchMedia(QUERY).matches : false;
  var _React$useState = React.useState(matches),
    _React$useState2 = _slicedToArray$g(_React$useState, 2),
    prefersColorScheme = _React$useState2[0],
    setPrefersColorScheme = _React$useState2[1];
  React.useEffect(function () {
    var mql = environment.matchMedia(QUERY);
    var listener = function listener(event) {
      setPrefersColorScheme(event.matches);
    };
    if (mql && mql.addListener) {
      mql.addListener(listener);
    }
    return function () {
      if (mql && mql.removeListener) {
        mql.removeListener(listener);
      }
    };
  });
  return prefersColorScheme ? 'dark' : 'light';
}
function _templateObject2$r() {
  var data = _taggedTemplateLiteral$S(["\n  ", "\n"]);
  _templateObject2$r = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$S() {
  var data = _taggedTemplateLiteral$S(["\n  opacity: 0;\n\n  ", "\n"]);
  _templateObject$S = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$S(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Overlay = styled(Box)(_templateObject$S(), function (props) {
  var visible = "\n      opacity: 0.7;\n      transition: opacity ".concat(tokens.motionDuration_slow, " ").concat(tokens.motionEase_out, ";\n    ");
  var hidden = "\n      opacity: 0;\n      pointer-events: none;\n      transition: opacity ".concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_in, ";\n    ");
  switch (props.$state) {
    case 'entered':
      return "\n          ".concat(visible, "\n          pointer-events: auto;\n          visibility: visible;\n        ");
    case 'exiting':
      return "\n          ".concat(hidden, "\n          visibility: visible;\n        ");
    case 'exited':
    case 'entering':
    default:
      return "\n          ".concat(hidden, "\n          visibility: hidden;\n        ");
  }
});
var Container$3 = styled(Box)(_templateObject2$r(), function (props) {
  var visible = "\n      transform: translateX(0);\n      transition: transform ".concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ";\n    ");
  var hidden = "\n      transform: translateX(".concat(props.$viewportPosition === 'left' ? '-100%' : '100%', ");\n      pointer-events: none;\n      transition: transform ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ";\n    ");
  switch (props.$state) {
    case 'entered':
      return "\n          ".concat(visible, "\n          pointer-events: auto;\n          visibility: visible;\n        ");
    case 'entering':
    case 'exiting':
      /**
       * Visibility needs to be set here on entering to ensure
       * FocusLock can focus on Drawer content when opening.
       */
      return "\n          ".concat(hidden, "\n          visibility: visible;\n        ");
    case 'exited':
      return "\n          ".concat(hidden, "\n          visibility: hidden;\n        ");
  }
});
function _slicedToArray$f(arr, i) {
  return _arrayWithHoles$f(arr) || _iterableToArrayLimit$f(arr, i) || _unsupportedIterableToArray$g(arr, i) || _nonIterableRest$f();
}
function _nonIterableRest$f() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$g(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$g(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$g(o, minLen);
}
function _arrayLikeToArray$g(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$f(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$f(arr) {
  if (Array.isArray(arr)) return arr;
}
var Drawer = /*#__PURE__*/React.forwardRef(function Drawer(props, userRef) {
  var children = props.children,
    _props$closeOnEscape = props.closeOnEscape,
    closeOnEscape = _props$closeOnEscape === void 0 ? true : _props$closeOnEscape,
    _props$closeOnOutside = props.closeOnOutsideClick,
    closeOnOutsideClick = _props$closeOnOutside === void 0 ? true : _props$closeOnOutside,
    id = props.id,
    onChange = props.onChange,
    onClose = props.onClose,
    open = props.open,
    portalId = props.portalId,
    _props$position = props.position,
    position = _props$position === void 0 ? 'right' : _props$position;
  var overlayRef = React.useRef();
  var childrenRef = React.useRef();
  var footerRef = React.useRef();
  var _React$useState = React.useState('0px'),
    _React$useState2 = _slicedToArray$f(_React$useState, 2),
    footerHeight = _React$useState2[0],
    setFooterHeight = _React$useState2[1]; // Calls onClose when clicking outside drawer content

  function handleOutsideClick(e) {
    if (!closeOnOutsideClick) {
      return;
    }
    var isOutside = overlayRef.current && overlayRef.current.contains(e.target) && childrenRef && childrenRef.current && !childrenRef.current.contains(e.target);
    if (isOutside && open && onClose) {
      onClose();
    }
  } // Calls onClose when receiving a escape keydown event

  function handleEscape(e) {
    if (closeOnEscape && open && onClose) {
      onKey('escape', onClose)(e);
    }
  }
  useWindowEvent('keydown', handleEscape);
  useWindowEvent('click', handleOutsideClick);
  React.useEffect(function () {
    if (onChange) {
      onChange(open);
    }
  }, [open]);
  /**
   * Drawer.Footer is a fixed element. This effect measures height of the footer and uses
   * it as padding to offset Drawer.Content so its children isn't rendered underneath
   * the footer
   */

  React.useLayoutEffect(function () {
    if (!footerRef.current) {
      setFooterHeight('0px');
    } else {
      setFooterHeight("".concat(getRectFor(footerRef.current).height, "px"));
    }
  }, [footerRef.current, open]);
  /**
   * On drawer open, moves focus to the container
   * Without this, focus gets moved to the close button first
   */

  React.useLayoutEffect(function () {
    var timer;
    if (open) {
      timer = getWindow().setTimeout(function () {
        childrenRef.current.focus();
      }, secondsToMS(tokens.motionDuration_medium));
    }
    return function () {
      getWindow().clearTimeout(timer);
    };
  }, [open]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScrollLock, {
    isActive: open
  }), /*#__PURE__*/React.createElement(Portal, {
    containerId: portalId
  }, /*#__PURE__*/React.createElement(Transition, {
    mountOnEnter: true,
    unmountOnExit: true,
    "in": open,
    timeout: {
      enter: 0,
      exit: secondsToMS(tokens.motionDuration_medium)
    },
    nodeRef: overlayRef
  }, function (state) {
    return /*#__PURE__*/React.createElement(FocusLock, {
      returnFocus: true,
      disabled: !open || isInIframe(),
      autoFocus: false
    }, /*#__PURE__*/React.createElement(Box, {
      "data-id": "drawer-wrapper",
      height: "100vh",
      left: "0",
      position: "fixed",
      ref: userRef,
      style: {
        pointerEvents: 'none'
      },
      tabIndex: -1,
      top: "0",
      width: "100vw",
      zIndex: "overlay"
    }, /*#__PURE__*/React.createElement(Overlay, {
      "data-id": "drawer-overlay",
      ref: overlayRef,
      "$state": state,
      position: "absolute",
      top: "0",
      left: "0",
      height: "100%",
      width: "100%",
      bg: "gray.900"
    }), /*#__PURE__*/React.createElement(Container$3, {
      "aria-modal": "true",
      "data-id": "drawer-container",
      id: id,
      ref: childrenRef,
      role: "dialog",
      "$state": state,
      "$viewportPosition": position,
      tabIndex: -1,
      bg: "white",
      position: "absolute",
      top: "0",
      bottom: "0",
      maxWidth: "32rem" // TODO Make this configurable and reference sizing theme
      ,

      width: "80vw",
      right: position === 'right' ? '0' : 'auto',
      left: position === 'left' ? '0' : 'auto'
    }, /*#__PURE__*/React.createElement(TouchScrollable, null, /*#__PURE__*/React.createElement(Box, {
      overflowY: "scroll",
      position: "relative",
      height: "calc(100% - ".concat(footerHeight, ")")
    }, /*#__PURE__*/React.createElement(DrawerContext.Provider, {
      value: {
        onClose: onClose,
        footerRef: footerRef
      }
    }, children))))));
  })));
});
Drawer.displayName = 'Drawer';
Drawer.Footer = Footer$2;
Drawer.Content = Content$2;
Drawer.Header = Header$4;
var Action$1 = /*#__PURE__*/React.forwardRef(function Action(props, userRef) {
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    color: "blue",
    size: "large"
  }, props, {
    ref: userRef
  }));
});
Action$1.displayName = 'EmptyState.Action';
var Header$3 = /*#__PURE__*/React.forwardRef(function Header(props, userRef) {
  var _props$as = props.as,
    as = _props$as === void 0 ? 'h1' : _props$as,
    looksLike = props.looksLike,
    children = props.children;
  return /*#__PURE__*/React.createElement(Text, {
    as: as,
    looksLike: looksLike,
    fontSize: !looksLike ? ['600', null, null, '700'] : null,
    lineHeight: !looksLike ? ['600', null, null, '700'] : null,
    mb: 300,
    ref: userRef
  }, children);
});
Header$3.displayName = 'EmptyState.Header';
function _templateObject4$a() {
  var data = _taggedTemplateLiteral$R(["\n  list-style: none;\n  padding-left: 0;\n  & > li {\n    position: relative;\n    padding-left: 24px;\n    padding-bottom: ", ";\n    color: ", ";\n\n    &:last-of-type {\n      padding-bottom: 0;\n      &:before {\n        content: none;\n      }\n    }\n\n    &:after {\n      content: '';\n      position: absolute;\n      left: 0;\n      top: ", ";\n      transform: translateY(-50%);\n      width: ", ";\n      height: ", ";\n      background: ", ";\n      border-radius: 50%;\n    }\n  }\n"]);
  _templateObject4$a = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$d() {
  var data = _taggedTemplateLiteral$R(["\n  transform: translate(0, -50%);\n  & > svg {\n    height: 100%;\n    width: 100%;\n  }\n"]);
  _templateObject3$d = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$q() {
  var data = _taggedTemplateLiteral$R(["\n  & > img {\n    width: 100%;\n  }\n  & > svg {\n    height: 100%;\n    width: 100%;\n  }\n"]);
  _templateObject2$q = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$R() {
  var data = _taggedTemplateLiteral$R(["\n  p {\n    color: ", ";\n    ", ";\n  }\n"]);
  _templateObject$R = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$R(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledContent$3 = styled(Box)(_templateObject$R(), function (props) {
  return props.theme.colors.gray['900'];
}, css$1({
  fontSize: '300',
  lineHeight: '300'
}));
var StyledImage = styled(Box)(_templateObject2$q());
var StyledImageLegacy = styled(Box)(_templateObject3$d());
var StyledList$1 = styled(Box)(_templateObject4$a(), function (props) {
  return props.theme.space['500'];
}, function (props) {
  return props.theme.colors.gray['900'];
}, function (props) {
  return props.theme.space['300'];
}, function (props) {
  return props.theme.space['200'];
}, function (props) {
  return props.theme.space['200'];
}, function (props) {
  return props.theme.colors.blue['700'];
});
var Content$1 = /*#__PURE__*/React.forwardRef(function Content(props, userRef) {
  var children = props.children;
  return /*#__PURE__*/React.createElement(StyledContent$3, {
    ref: userRef
  }, children);
});
Content$1.displayName = 'EmptyState.Content';
function _templateObject$Q() {
  var data = _taggedTemplateLiteral$Q(["\n  position: absolute;\n  left: 3px;\n  top: 18px;\n  height: calc(100% - 3px);\n  pointer-events: none;\n"]);
  _templateObject$Q = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$Q(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledSvg = styled.svg(_templateObject$Q());
var List = /*#__PURE__*/React.forwardRef(function List(props, userRef) {
  var children = props.children;
  return /*#__PURE__*/React.createElement(StyledList$1, {
    as: "ul",
    mt: "500",
    position: "relative",
    ref: userRef
  }, React.Children.map(children, function (child, index) {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      children: /*#__PURE__*/React.createElement("div", null, child.props.children, index < React.Children.toArray(children).length - 1 && /*#__PURE__*/React.createElement(StyledSvg, null, /*#__PURE__*/React.createElement("g", {
        stroke: tokens.color_blue_700,
        strokeWidth: "2"
      }, /*#__PURE__*/React.createElement("path", {
        strokeDasharray: "3,7",
        d: "M1 2 l0 200",
        strokeWidth: "2",
        strokeLinecap: "round"
      }))))
    });
  }));
});
List.displayName = 'EmptyState.List';
function _templateObject$P() {
  var data = _taggedTemplateLiteral$P(["\n  ", ";\n  ", ";\n"]);
  _templateObject$P = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$P(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Image$1 = /*#__PURE__*/React.forwardRef(function Image(props, userRef) {
  var _props$alt = props.alt,
    alt = _props$alt === void 0 ? '' : _props$alt,
    className = props.className,
    src = props.src;
  return /*#__PURE__*/React.createElement(Box, {
    as: "img",
    alt: alt,
    className: className,
    src: src,
    ref: userRef,
    width: "100%"
  });
});
Image$1.displayName = 'Picture.Image';
var system$8 = compose(layout, margin);
var StyledFigure$1 = styled.figure(_templateObject$P(), system$8, function (_ref) {
  var $seeThrough = _ref.$seeThrough;
  return $seeThrough ? 'mix-blend-mode: multiply;' : '';
}); // Picture Component

var Picture = /*#__PURE__*/React.forwardRef(function Picture(props, userRef) {
  var children = props.children,
    seeThrough = props.seeThrough,
    role = props.role,
    rest = __rest(props, ["children", "seeThrough", "role"]);
  var systemProps = pick(rest, system$8.propNames);
  return /*#__PURE__*/React.createElement(StyledFigure$1, Object.assign({
    m: "0",
    role: role,
    ref: userRef,
    "$seeThrough": seeThrough
  }, systemProps), /*#__PURE__*/React.createElement("picture", null, children));
});
Picture.displayName = 'Picture';
Picture.Image = Image$1;
var Image = /*#__PURE__*/React.forwardRef(function Image(props, userRef) {
  var src = props.src,
    className = props.className,
    children = props.children,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? '1100' : _props$maxWidth;
  return /*#__PURE__*/React.createElement(StyledImage, {
    display: "block",
    mx: "auto",
    mb: "400",
    width: ['100%', '80%', '60%', '100%'],
    maxWidth: maxWidth,
    height: "auto",
    ref: userRef
  }, /*#__PURE__*/React.createElement(Picture, null, children, /*#__PURE__*/React.createElement(Picture.Image, {
    src: src,
    className: className
  })));
});
Image.displayName = 'EmptyState.Image';
function _templateObject$O() {
  var data = _taggedTemplateLiteral$O(["\n  pointer-events: none;\n"]);
  _templateObject$O = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$O(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledMedia = styled(Box)(_templateObject$O());
var Media = /*#__PURE__*/React.forwardRef(function Media(props, userRef) {
  var children = props.children,
    className = props.className,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? '1100' : _props$maxWidth;
  return /*#__PURE__*/React.createElement(StyledMedia, {
    display: "block",
    mx: "auto",
    mb: "400",
    width: ['100%', '80%', '60%', '100%'],
    maxWidth: maxWidth,
    height: "auto",
    className: className,
    ref: userRef
  }, children);
});
Media.displayName = 'EmptyState.Media';
function ownKeys$4(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$4(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$4(Object(source), !0).forEach(function (key) {
      _defineProperty$6(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$4(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$6(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function EmptyState$1(props) {
  var title = props.title,
    primaryAction = props.primaryAction,
    secondaryAction = props.secondaryAction,
    Image = props.image,
    children = props.children;
  var primaryActionMarkup = primaryAction ? buttonFrom(primaryAction, _objectSpread$4({
    size: 'large'
  }, !primaryAction.color ? {
    color: 'blue'
  } : {})) : null;
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    height: "100vh",
    py: ['500', null, '33vh'],
    px: "500"
  }, /*#__PURE__*/React.createElement(Stack, {
    space: ['400', null, null, '600']
  }, /*#__PURE__*/React.createElement(Box, {
    as: "h1",
    width: ['auto', null, '45%'],
    fontSize: ['600', null, null, '700'],
    lineHeight: ['600', null, null, '700']
  }, title), /*#__PURE__*/React.createElement(StyledContent$3, {
    width: ['auto', null, '45%']
  }, children), /*#__PURE__*/React.createElement(Box, {
    width: ['auto', null, '45%']
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "500"
  }, primaryActionMarkup, secondaryAction && /*#__PURE__*/React.createElement("span", null, linkFrom(secondaryAction))))), Image && /*#__PURE__*/React.createElement(StyledImageLegacy, {
    display: ['none', null, 'block'],
    top: "45%",
    left: "48%",
    width: "50%",
    maxWidth: "600px",
    height: "auto",
    position: "absolute"
  }, /*#__PURE__*/React.createElement(Image, null)));
}
EmptyState$1.displayName = 'EmptyState.LEGACY';
EmptyState$1.propTypes = {
  /**
   * The display title
   */
  title: PropTypes.string,
  /**
   * Main cta. Most button props will work in here.
   * e.g. { content: 'button label', onClick: callback() }
   */
  primaryAction: PropTypes.shape({
    content: PropTypes.node.isRequired
  }),
  /**
   * Secondary Action - appear as a link next to the primary action
   */
  secondaryAction: PropTypes.shape({
    content: PropTypes.node.isRequired
  }),
  /**
   * Image to display
   */
  image: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
  /**
   * Content below the CTA
   */
  children: PropTypes.node
};
var EmptyState = /*#__PURE__*/React.forwardRef(function EmptyState(props, userRef) {
  var children = props.children,
    rest = __rest(props, ["children"]);
  var breakpoint = useBreakpoint();
  var isMobile = ['default', 'xs', 'sm'].includes(breakpoint);
  var systemProps = pick(rest, margin.propNames); // Allow fallback use of `EmptyState.Image` for now
  // We should remove in the future in favor of `EmptyState.Media`

  var Media = getChild('EmptyState.Media', children);
  var Image = getChild('EmptyState.Image', children);
  return /*#__PURE__*/React.createElement(Columns, Object.assign({
    collapseBelow: "sm",
    space: "500",
    alignY: "center"
  }, systemProps, {
    ref: userRef
  }), isMobile ? /*#__PURE__*/React.createElement(Column$1, {
    width: 1 / 2
  }, Image && Image.length >= 1 ? Image : Media) : null, /*#__PURE__*/React.createElement(Column$1, {
    width: 1 / 2
  }, getChild('EmptyState.Header', children), getChild('EmptyState.Content', children), /*#__PURE__*/React.createElement(Box, {
    mt: 650
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "500"
  }, getChild('EmptyState.Action', children)))), !isMobile ? /*#__PURE__*/React.createElement(Column$1, {
    width: 1 / 2
  }, Image && Image.length >= 1 ? Image : Media) : null);
});
EmptyState.displayName = 'EmptyState';
EmptyState.Action = Action$1;
EmptyState.Header = Header$3;
EmptyState.Content = Content$1;
EmptyState.Image = Image;
EmptyState.Media = Media;
EmptyState.List = List;
EmptyState.LEGACY = EmptyState$1;
function _templateObject2$p() {
  var data = _taggedTemplateLiteral$N(["\n  ", "\n  ", "\n"]);
  _templateObject2$p = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$N() {
  var data = _taggedTemplateLiteral$N(["\n  ", "\n  user-select: none;\n  outline: none;\n  ", "\n\n  display: flex;\n  align-items: center;\n  text-align: left;\n  width: 100%;\n\n  transition: background ", " ", ";\n\n  &:hover {\n    cursor: pointer;\n  }\n"]);
  _templateObject$N = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$N(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledHeader$1 = styled('button')(_templateObject$N(), buttonReset, function (props) {
  return css$1({
    px: '450',
    py: props.$variant === 'borderless' ? '200' : '300'
  });
}, tokens.motionDuration_fast, tokens.motionEase_in_out);
var StyledContentWrapper = styled('div')(_templateObject2$p(), function (props) {
  var visibility = 'hidden';
  var display = 'none';
  if (props.$isOpen) {
    visibility = 'visible';
    display = 'flex';
  }
  return "\n      visibility: ".concat(visibility, ";\n      display: ").concat(display, ";\n    ");
}, function () {
  return css$1({
    px: '450',
    pb: '450'
  });
});
var expandable = function expandable(props) {
  var borderRadius = tokens.borderRadius_100;
  if (props.$variant === 'borderless') {
    return "";
  }
  if (props.$accent) {
    borderRadius = "0 0 ".concat(tokens.borderRadius_100, " ").concat(tokens.borderRadius_100);
  }
  return "\n    border: ".concat(props.theme.borders['300'], ";\n    border-radius: ").concat(borderRadius, ";\n    border-top-width: ").concat(props.accent ? tokens.borderWidth_0 : tokens.borderWidth_100, "\n  ");
};
var arrow = function arrow(props) {
  var rotate = 'rotate(-90deg)';
  if (props.$isOpen) {
    rotate = 'rotate(90deg)';
  }
  return "\n    border-radius: ".concat(tokens.borderRadius_circle, ";\n    padding: ").concat(tokens.spacing_100, ";\n    transition: ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n    transform-origin: center;\n    transform: ").concat(rotate, ";\n\n    ").concat(StyledHeader$1, ":hover &, ").concat(StyledHeader$1, ":focus & {\t\n      background: ").concat(tokens.color_blue_200, ";\t\n      color: ").concat(tokens.color_blue_800, ";\n      ").concat(focusOutline({
    modifier: '&',
    radius: tokens.borderRadius_circle
  }), "\n    }\n  ");
};
var title = function title(props) {
  return "\n  font-size: ".concat(props.$variant === 'borderless' ? tokens.fontSize_300 : tokens.fontSize_400, ";\n  font-weight: ").concat(props.$variant === 'borderless' ? tokens.fontWeight_normal : tokens.fontWeight_semibold, ";\n  color: ").concat(props.$variant === 'borderless' ? tokens.color_gray_700 : tokens.color_gray_900, ";\n  line-height: ").concat(tokens.lineHeight_400, ";\n  transition: color ").concat(tokens.motionDuration_fast, ";\n\n  ").concat(StyledHeader$1, ":hover &, ").concat(StyledHeader$1, ":focus & {\t\n    color: ").concat(tokens.color_blue_700, ";\n  }\n");
};
var subtitle = function subtitle() {
  return "\n  font-weight: ".concat(tokens.fontWeight_normal, ";\n");
};
var accent$1 = function accent(props) {
  var color;
  switch (props.$accentColor) {
    case 'orange':
      color = tokens.color_brand_orange;
      break;
    case 'green':
      color = tokens.color_green_700;
      break;
    case 'yellow':
      color = tokens.color_yellow_400;
      break;
    case 'red':
      color = tokens.color_red_700;
      break;
    case 'gray':
      color = tokens.color_gray_600;
      break;
    case 'blue':
    case 'navy':
    case 'purple':
    default:
      color = tokens.color_blue_700;
  }
  return "\n    height: ".concat(tokens.spacing_100, ";\n    border-radius: ").concat(tokens.borderRadius_100, " ").concat(tokens.borderRadius_100, " 0 0;\n    background-color: ").concat(color, ";\n  ");
};
function _templateObject$M() {
  var data = _taggedTemplateLiteral$M(["\n  ", "\n"]);
  _templateObject$M = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$M(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var AccentOuter$1 = styled('div')(_templateObject$M(), accent$1);
var Accent$2 = function Accent(_ref) {
  var accentColor = _ref.accentColor;
  return /*#__PURE__*/React.createElement(AccentOuter$1, {
    "$accentColor": accentColor
  });
};
Accent$2.displayName = 'Expandable.Accent';
function _slicedToArray$e(arr, i) {
  return _arrayWithHoles$e(arr) || _iterableToArrayLimit$e(arr, i) || _unsupportedIterableToArray$f(arr, i) || _nonIterableRest$e();
}
function _nonIterableRest$e() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$f(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$f(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$f(o, minLen);
}
function _arrayLikeToArray$f(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$e(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$e(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject4$9() {
  var data = _taggedTemplateLiteral$L(["\n  ", "\n"]);
  _templateObject4$9 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$c() {
  var data = _taggedTemplateLiteral$L(["\n  ", "\n"]);
  _templateObject3$c = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$o() {
  var data = _taggedTemplateLiteral$L(["\n  ", "\n"]);
  _templateObject2$o = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$L() {
  var data = _taggedTemplateLiteral$L(["\n  ", "\n  ", "\n"]);
  _templateObject$L = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$L(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledExpandable = styled('div')(_templateObject$L(), expandable, margin);
var StyledArrow = styled.div(_templateObject2$o(), arrow);
var StyledTitle = styled('h3')(_templateObject3$c(), title);
var StyledSubtitle = styled(Box)(_templateObject4$9(), subtitle);
function Expandable(props) {
  var children = props.children,
    _props$defaultOpen = props.defaultOpen,
    defaultOpen = _props$defaultOpen === void 0 ? false : _props$defaultOpen,
    dataId = props['data-id'],
    icon = props.icon,
    id = props.id,
    open = props.open,
    onToggle = props.onToggle,
    subtitle = props.subtitle,
    title = props.title,
    accent = props.accent,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'bordered' : _props$variant,
    rest = __rest(props, ["children", "defaultOpen", 'data-id', "icon", "id", "open", "onToggle", "subtitle", "title", "accent", "variant"]);
  var systemProps = pick(rest, margin.propNames);
  var header = React.useRef();
  var _React$useState = React.useState(open || defaultOpen),
    _React$useState2 = _slicedToArray$e(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  var controlled = typeof open === 'boolean'; // Sets internal open state when controlled externally

  React.useEffect(function () {
    if (controlled) {
      setIsOpen(open);
    }
  }, [open]);
  function handleToggle() {
    if (controlled) {
      onToggle();
    } else {
      setIsOpen(!isOpen);
    }
  }
  function handleClick() {
    header.current.blur();
    handleToggle();
  }
  function handleKeyDown(e) {
    onKeys(['space', 'enter'], function () {
      e.preventDefault();
      handleToggle();
    })(e);
  }
  var accentColor = accent === true ? 'blue' : accent;
  var accentMarkup = accentColor ? /*#__PURE__*/React.createElement(Accent$2, {
    accentColor: accentColor
  }) : null;
  var iconMarkup = icon ? /*#__PURE__*/React.createElement(Box, {
    flex: "0",
    minWidth: "40px",
    maxWidth: "40px",
    mr: "500"
  }, icon) : null;
  var subtitleMarkup = subtitle ? /*#__PURE__*/React.createElement(StyledSubtitle, {
    as: "h6",
    mb: "100"
  }, subtitle) : null;
  var contentSpacer = icon ? /*#__PURE__*/React.createElement(Box, {
    flex: "0",
    minWidth: "40px",
    maxWidth: "40px",
    mr: "500"
  }) : null;
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    "data-id": dataId
  }, systemProps), accentMarkup, /*#__PURE__*/React.createElement(StyledExpandable, {
    "$accent": accent,
    "$variant": variant
  }, /*#__PURE__*/React.createElement(StyledHeader$1, {
    "aria-controls": id,
    "aria-expanded": isOpen,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    ref: header,
    "data-id": "expandable-toggle",
    type: "button",
    "$variant": variant
  }, iconMarkup, /*#__PURE__*/React.createElement(Box, {
    display: "inline-block",
    flex: "1"
  }, /*#__PURE__*/React.createElement(StyledTitle, {
    "$variant": variant
  }, title), subtitleMarkup), /*#__PURE__*/React.createElement(Box, {
    display: "inline-block",
    flex: "0"
  }, /*#__PURE__*/React.createElement(StyledArrow, {
    "$isOpen": isOpen
  }, /*#__PURE__*/React.createElement(KeyboardArrowLeft, {
    size: 26
  })))), /*#__PURE__*/React.createElement(StyledContentWrapper, {
    "aria-hidden": !isOpen,
    "$isOpen": isOpen,
    id: id,
    "data-id": "expandable-content",
    "$variant": variant
  }, contentSpacer, /*#__PURE__*/React.createElement(Box, {
    flex: "1"
  }, children))));
}
Expandable.Accent = Accent$2;
Expandable.Title = StyledTitle;
Expandable.Subtitle = StyledSubtitle;
Expandable.ContentWrapper = StyledContentWrapper;
Expandable.Arrow = StyledArrow;
Expandable.Header = StyledHeader$1;
function _defineProperty$5(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
var gutterWidth = tokens.spacing_400;
var breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
var alignmentStyleMap = {
  $start: {
    justifyContent: 'flex-start',
    textAlign: 'start'
  },
  $center: {
    justifyContent: 'center',
    textAlign: 'center'
  },
  $end: {
    justifyContent: 'flex-end',
    textAlign: 'end'
  },
  $top: {
    alignItems: 'flex-start'
  },
  $middle: {
    alignItems: 'center'
  },
  $bottom: {
    alignItems: 'flex-end'
  },
  $around: {
    justifyContent: 'space-around'
  },
  $between: {
    justifyContent: 'space-between'
  }
};
var wrapStyleWithMediaQuery = function wrapStyleWithMediaQuery(breakpoint, style) {
  var mediaQueryWidth = tokens["mediaQuery_".concat(breakpoint)]; // do not wrap if no style for breakpoint or breakpoint is extra small

  if (!Object.keys(style).length || breakpoint === 'xs') {
    return style;
  }
  return _defineProperty$5({}, "@media only screen and (min-width: ".concat(mediaQueryWidth, ")"), style);
};
var gridStyle = function gridStyle(props) {
  var breakpointStyle = breakpoints.reduce(function (style, breakpoint) {
    // match props with alignment style and merge for breakpoint
    var alignmentStyle = Object.keys(props).reduce(function (obj, key) {
      return props[key] === breakpoint ? Object.assign(Object.assign({}, obj), alignmentStyleMap[key]) : obj;
    }, {});
    return Object.assign(Object.assign({}, style), wrapStyleWithMediaQuery(breakpoint, alignmentStyle));
  }, {});
  return Object.assign({
    boxSizing: 'border-box',
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: "-".concat(gutterWidth)
  }, breakpointStyle);
};
var calculateGridColumnWidth = function calculateGridColumnWidth(index) {
  return 100 / 12 * index;
};
var gridColumnStyle = function gridColumnStyle(props) {
  var breakpointStyle = breakpoints.reduce(function (style, breakpoint) {
    var breakpointIndex = props[breakpoint];
    var breakpointOffsetIndex = props["".concat(breakpoint, "Offset")];
    var columnStyle = Object.assign(Object.assign({}, breakpointIndex ? {
      flex: "0 0 ".concat(calculateGridColumnWidth(breakpointIndex), "%"),
      maxWidth: "".concat(calculateGridColumnWidth(breakpointIndex), "%"),
      paddingLeft: gutterWidth,
      textAlign: 'left'
    } : {}), breakpointOffsetIndex ? {
      marginLeft: "".concat(calculateGridColumnWidth(breakpointOffsetIndex), "%")
    } : {});
    return Object.assign(Object.assign({}, style), wrapStyleWithMediaQuery(breakpoint, columnStyle));
  }, {});
  return Object.assign({
    boxSizing: 'border-box',
    flex: '1 0 0',
    maxWidth: '100%',
    paddingLeft: gutterWidth,
    textAlign: 'left'
  }, breakpointStyle);
};
function _templateObject$K() {
  var data = _taggedTemplateLiteral$K(["\n  ", "\n"]);
  _templateObject$K = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$K(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledGridColumn = styled('div')(_templateObject$K(), gridColumnStyle);
function Column(_a) {
  var children = _a.children,
    props = __rest(_a, ["children"]);
  return /*#__PURE__*/React.createElement(StyledGridColumn, Object.assign({}, props), children);
}
Column.displayName = 'Grid.Column';
function _templateObject$J() {
  var data = _taggedTemplateLiteral$J(["\n  ", "\n"]);
  _templateObject$J = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$J(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledGrid = styled('div')(_templateObject$J(), gridStyle);
function Grid(props) {
  var children = props.children,
    start = props.start,
    center = props.center,
    end = props.end,
    top = props.top,
    middle = props.middle,
    bottom = props.bottom,
    around = props.around,
    between = props.between;
  return /*#__PURE__*/React.createElement(StyledGrid, {
    "$start": start,
    "$center": center,
    "$end": end,
    "$top": top,
    "$middle": middle,
    "$bottom": bottom,
    "$around": around,
    "$between": between
  }, children);
}
Grid.displayName = 'Grid';
Grid.Column = Column;
function InlineCode(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement(Box, {
    as: "code",
    borderRadius: "100",
    fontFamily: "monospace",
    display: "inline",
    bg: "blue.200",
    color: "blue.700",
    px: "100",
    lineHeight: "inherit",
    fontSize: "75%"
  }, children);
}
InlineCode.displayName = 'InlineCode';
function _templateObject$I() {
  var data = _taggedTemplateLiteral$I(["\n  position: relative;\n  display: inline-block;\n  padding: 0 ", ";\n  height: ", ";\n\n  background: ", ";\n  border-radius: 4px;\n  border: ", " solid ", ";\n\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n\n  box-shadow: 0px 3px 0px -1px ", ", 0px 3px 0px ", ";\n"]);
  _templateObject$I = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$I(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledKey = styled.kbd(_templateObject$I(), tokens.spacing_100, tokens.sizing_450, tokens.color_gray_100, tokens.borderWidth_100, tokens.color_gray_400, tokens.color_gray_900, tokens.fontSize_100, tokens.lineHeight_100, tokens.color_gray_200, tokens.color_gray_400);
var KeyboardKey = /*#__PURE__*/React.forwardRef(function KeyboardKey(_ref, ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement(StyledKey, {
    ref: ref
  }, children);
});
KeyboardKey.displayName = 'KeyboardKey';
function _templateObject$H() {
  var data = _taggedTemplateLiteral$H(["\n  ", "\n"]);
  _templateObject$H = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$H(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledWrapper$7 = styled.div(_templateObject$H(), margin);
var Label = function Label(_ref) {
  var children = _ref.children,
    orientation = _ref.orientation,
    appearance = _ref.appearance;
  return /*#__PURE__*/React.createElement(Box, {
    fontSize: "200",
    fontWeight: "semibold",
    mb: orientation === 'vertical' ? '100' : '',
    color: appearance == 'inverted' ? 'gray.500' : ''
  }, children);
};
Label.displayName = 'LabelValue.Label';
var Value = function Value(_ref2) {
  var children = _ref2.children,
    appearance = _ref2.appearance;
  return /*#__PURE__*/React.createElement(Box, {
    color: appearance == 'inverted' ? 'white' : ''
  }, children);
};
Value.displayName = 'LabelValue.Value';
var LabelValue = /*#__PURE__*/React.forwardRef(function LabelValue(props, userRef) {
  var children = props.children,
    className = props.className,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'vertical' : _props$orientation,
    _props$appearance = props.appearance,
    appearance = _props$appearance === void 0 ? 'default' : _props$appearance,
    rest = __rest(props, ["children", "className", "orientation", "appearance"]);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(StyledWrapper$7, Object.assign({
    className: className
  }, systemProps, {
    ref: userRef,
    tabIndex: -1
  }), /*#__PURE__*/React.createElement(Box, {
    display: orientation === 'horizontal' ? 'grid' : 'block',
    gridGap: orientation === 'horizontal' ? '300' : '',
    gridTemplateColumns: orientation === 'horizontal' ? "".concat(tokens.spacing_900, " 1fr") : ''
  }, getChild('LabelValue.Label', children, {
    orientation: orientation,
    appearance: appearance
  }), getChild('LabelValue.Value', children, {
    appearance: appearance
  })));
});
LabelValue.displayName = 'LabelValue';
LabelValue.Label = Label;
LabelValue.Value = Value;
function Section$2(_ref) {
  var annotated = _ref.annotated,
    children = _ref.children,
    dataId = _ref['data-id'];
  return /*#__PURE__*/React.createElement(Column$1, {
    width: annotated ? 1 / 3 : 1,
    "data-id": dataId
  }, children);
}
Section$2.displayName = 'Layout.Section';
var SectionTitle = function SectionTitle(props) {
  var children = props.children,
    _props$as = props.as,
    as = _props$as === void 0 ? 'h2' : _props$as;
  return /*#__PURE__*/React.createElement(Text, {
    as: as,
    fontSize: ['300', null, '400'],
    lineHeight: ['300', null, '400'],
    pb: "300",
    "data-id": props['data-id']
  }, children);
};
SectionTitle.displayName = 'Layout.SectionTitle';
var Layout = /*#__PURE__*/React.forwardRef(function Layout(props, ref) {
  var children = props.children,
    _props$collapseBelow = props.collapseBelow,
    collapseBelow = _props$collapseBelow === void 0 ? 'md' : _props$collapseBelow;
  return /*#__PURE__*/React.createElement(Columns, {
    collapseBelow: collapseBelow,
    space: ['400', null, '500'],
    mb: "600",
    ref: ref,
    "data-id": props['data-id']
  }, children);
});
Layout.displayName = 'Layout';
Layout.Section = Section$2;
Layout.SectionTitle = SectionTitle;
function _templateObject$G() {
  var data = _taggedTemplateLiteral$G(["\n  ", "\n  text-decoration: none;\n  display: block;\n  width: 100%;\n  padding: ", " ", ";\n  transition: ", " ", ";\n  transition-property: color, background;\n  font-size: ", ";\n  line-height: ", ";\n  text-align: left;\n  cursor: ", ";\n  background: ", ";\n  color: ", ";\n\n  &:focus {\n    border: none;\n    outline: none;\n    box-shadow: 0;\n    background: ", ";\n  }\n\n  &:hover {\n    background: ", ";\n  }\n"]);
  _templateObject$G = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$G(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var chevron$1 = function chevron(props) {
  return "\n  position: absolute;\n  z-index: ".concat(props.theme.zIndex_default, ";\n  top: 3%;\n  right: ").concat(props.theme.space['300'], ";\n  height: 100%;\n  fill: ").concat(props.theme.colors.blue['700'], ";\n  user-select: none;\n  pointer-events: none;\n  ").concat(props.$disabled ? 'display: none' : '', ";\n");
};
var StyledLink$2 = styled(UnstyledLink)(_templateObject$G(), buttonReset, tokens.spacing_200, tokens.spacing_300, tokens.motionDuration_fast, tokens.motionEase_in_out, tokens.fontSize_300, tokens.lineHeight_300, function (props) {
  return props.$disabled ? 'not-allowed' : 'pointer';
}, function (props) {
  return props.$active ? tokens.color_blue_200 : '';
}, function (props) {
  return props.$disabled ? tokens.color_gray_600 : props.$active ? tokens.color_blue_700 : '';
}, function (props) {
  return props.$disabled ? '' : props.$active ? tokens.color_blue_200 : tokens.color_blue_100;
}, function (props) {
  return props.$disabled ? '' : props.$active ? tokens.color_blue_200 : tokens.color_blue_100;
});
var Option$1 = /*#__PURE__*/React.forwardRef(function Option(props, ref) {
  var value = props.value,
    index = props.index,
    disabled = props.disabled,
    setSize = props.setSize,
    selected = props.selected,
    children = props.children,
    onSelect = props.onSelect;
  var isActive = React.useMemo(function () {
    return selected === value;
  }, [selected, value]);
  return /*#__PURE__*/React.createElement(Box, {
    as: "li",
    role: "option",
    id: value === null || value === void 0 ? void 0 : value.toString(),
    "aria-setsize": setSize,
    "aria-posinset": index,
    "aria-selected": isActive,
    onClick: function onClick() {
      return onSelect(value);
    }
  }, /*#__PURE__*/React.createElement(StyledLink$2, {
    "$active": isActive,
    as: "button",
    type: "button",
    "$disabled": disabled,
    tabIndex: -1,
    ref: ref
  }, children));
});
Option$1.displayName = 'ListBox.Option';
function _slicedToArray$d(arr, i) {
  return _arrayWithHoles$d(arr) || _iterableToArrayLimit$d(arr, i) || _unsupportedIterableToArray$e(arr, i) || _nonIterableRest$d();
}
function _nonIterableRest$d() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$e(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$e(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$e(o, minLen);
}
function _arrayLikeToArray$e(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$d(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$d(arr) {
  if (Array.isArray(arr)) return arr;
}
function useOptionConstructor(props) {
  var options = props.options,
    value = props.value,
    onSelect = props.onSelect,
    open = props.open,
    placeholder = props.placeholder;
  var _React$useState = React.useState(value ? options.findIndex(function (option) {
      return option.props.value === value;
    }) : 0),
    _React$useState2 = _slicedToArray$d(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1];
  var _React$useState3 = React.useState(''),
    _React$useState4 = _slicedToArray$d(_React$useState3, 2),
    keysSoFar = _React$useState4[0],
    setKeysSoFar = _React$useState4[1];
  var _React$useState5 = React.useState(),
    _React$useState6 = _slicedToArray$d(_React$useState5, 2),
    keyClear = _React$useState6[0],
    setKeyClear = _React$useState6[1];
  var optionRefs = React.useRef(new Array(options.length));
  function onFocusContainerKeyDown(e) {
    if (open) {
      onKey('arrowDown', function () {
        e.preventDefault();
        if (focused === options.length - 1) {
          setFocused(0);
        } else {
          setFocused(focused + 1);
        }
      })(e);
      onKey('arrowUp', function () {
        e.preventDefault();
        if (focused === 0) {
          setFocused(options.length - 1);
        } else {
          setFocused(focused - 1);
        }
      })(e);
      setKeysSoFar(keysSoFar + e.key.toLowerCase());
      clearKeysSoFarAfterDelay();
    }
  }
  function clearKeysSoFarAfterDelay() {
    var timeout = setTimeout(function () {
      setKeysSoFar('');
      setKeyClear(false);
    }, 700);
    if (keyClear) {
      clearTimeout(timeout);
      setKeyClear(null);
    }
    setKeyClear(true);
  } // Preps array of option refs

  React.useEffect(function () {
    optionRefs.current = new Array(options.length);
    return function () {
      optionRefs.current = [];
    };
  }, [options]); // Focuses on option when focused index changes

  React.useLayoutEffect(function () {
    if (optionRefs.current[focused]) {
      optionRefs.current[focused].focus();
    }
  }, [focused]);
  React.useLayoutEffect(function () {
    if (keysSoFar && options) {
      var index = options.findIndex(function (option) {
        if (typeof option.props.children === 'string') {
          return option.props.children.toLowerCase().startsWith(keysSoFar);
        }
        return -1;
      });
      if (index >= 0) {
        setFocused(index);
      }
    }
  }, [keysSoFar, options]);
  var optionsMarkup = /*#__PURE__*/React.createElement(React.Fragment, null, placeholder && /*#__PURE__*/React.createElement(Option$1, {
    disabled: true,
    value: ""
  }, placeholder), options.map(function (option, i) {
    return /*#__PURE__*/React.cloneElement(option, {
      selected: value,
      setSize: options.length,
      onSelect: onSelect,
      ref: function ref(n) {
        optionRefs.current[i] = n;
      }
    });
  }));
  return {
    optionsMarkup: optionsMarkup,
    focusContainerProps: {
      onKeyDown: onFocusContainerKeyDown
    }
  };
}

/**
 * Adds and removes events for you
 * @example <WindowEvent event='keydown' handler={handleKeyDown} />
 */

function WindowEvent(props) {
  var event = props.event,
    handler = props.handler;
  if (!isBrowser()) {
    return null;
  }
  var environment = getWindow();
  function addEvent() {
    environment.addEventListener(event, handler);
  }
  function removeEvent() {
    environment.removeEventListener(event, handler);
  }
  React.useEffect(function () {
    addEvent();
    return function () {
      removeEvent();
    };
  }, [event, handler]);
  return null;
}
WindowEvent.displayName = 'WindowEvent';
function _slicedToArray$c(arr, i) {
  return _arrayWithHoles$c(arr) || _iterableToArrayLimit$c(arr, i) || _unsupportedIterableToArray$d(arr, i) || _nonIterableRest$c();
}
function _nonIterableRest$c() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$d(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$d(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$d(o, minLen);
}
function _arrayLikeToArray$d(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$c(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$c(arr) {
  if (Array.isArray(arr)) return arr;
}
var defaultPosition$1 = {
  top: 0,
  left: 0,
  width: 0,
  height: 0
};
function PopoverOverlay(props) {
  var _React$useState = React.useState(defaultPosition$1),
    _React$useState2 = _slicedToArray$c(_React$useState, 2),
    position = _React$useState2[0],
    setPosition = _React$useState2[1];
  var _activatorRef = React.useRef(null);
  var as = props.as,
    id = props.id,
    open = props.open,
    renderPopover = props.renderPopover,
    renderActivator = props.renderActivator,
    portalId = props.portalId;
  function handleMeasurement() {
    setPosition(getPositionFor(_activatorRef.current));
  }
  React.useEffect(function () {
    handleMeasurement();
  }, [open]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WindowEvent, {
    event: "resize",
    handler: handleMeasurement
  }), /*#__PURE__*/React.createElement(Box, {
    as: as,
    // Inline block is required to measure and set height correctly on spans
    display: as === 'span' ? 'inline-block' : null,
    position: "relative"
  }, renderActivator({
    activatorRef: function activatorRef(node) {
      _activatorRef.current = node;
    }
  }), /*#__PURE__*/React.createElement(Portal, {
    containerId: portalId
  }, /*#__PURE__*/React.createElement(Box, Object.assign({}, !open ? {
    'aria-hidden': true
  } : {}, {
    id: id,
    position: "absolute",
    left: "".concat(position.left, "px"),
    top: "".concat(position.top, "px"),
    height: "".concat(position.height, "px"),
    width: "".concat(position.width, "px"),
    zIndex: tokens.zIndex_overlay + 1,
    style: {
      pointerEvents: 'none'
    }
  }), renderPopover()))));
}
PopoverOverlay.displayName = 'PopoverOverlay';
var FOCUSABLE_SELECTOR = 'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),*[tabindex]';
/**
 * Finds the first focusable child within the provided DOM element.
 * @param element The element to query
 * @returns HTMLElement
 */

function findFocusableChild(element) {
  return element.querySelector(FOCUSABLE_SELECTOR);
}
function _slicedToArray$b(arr, i) {
  return _arrayWithHoles$b(arr) || _iterableToArrayLimit$b(arr, i) || _unsupportedIterableToArray$c(arr, i) || _nonIterableRest$b();
}
function _nonIterableRest$b() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$c(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$c(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$c(o, minLen);
}
function _arrayLikeToArray$c(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$b(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$b(arr) {
  if (Array.isArray(arr)) return arr;
}
var Popover = /*#__PURE__*/React.forwardRef(function Popover(props, ref) {
  var as = props.as,
    id = props.id,
    controlledOpen = props.open,
    onClose = props.onClose,
    children = props.children,
    trigger = props.trigger,
    wrapper = props.wrapper,
    portalId = props.portalId,
    _props$closeOnTab = props.closeOnTab,
    closeOnTab = _props$closeOnTab === void 0 ? true : _props$closeOnTab,
    _props$closeOnInsideC = props.closeOnInsideClick,
    closeOnInsideClick = _props$closeOnInsideC === void 0 ? false : _props$closeOnInsideC,
    rest = __rest(props, ["as", "id", "open", "onClose", "children", "trigger", "wrapper", "portalId", "closeOnTab", "closeOnInsideClick"]);
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray$b(_React$useState, 2),
    open = _React$useState2[0],
    setOpen = _React$useState2[1];
  var popoverRef = React.useRef();
  var activatorRef = React.useRef();
  var shouldBeOpen = controlledOpen || open;
  var Wrapper = as || wrapper || 'span';
  useWindowEvent('click', handleClick);
  useWindowEvent('keydown', handleEsc); // Mount hook – sets initial close state if uncontrolled

  React.useEffect(function () {
    // This component becomes "controlled" if the prop 'open' is given a boolean value
    if (controlledOpen === undefined) {
      setOpen(false);
    }
  }, []); // Automatically focuses on content when opening

  React.useEffect(function () {
    if (shouldBeOpen && popoverRef && popoverRef.current) {
      var contentToFocus = findFocusableChild(popoverRef.current) || popoverRef.current; // Honestly not sure why this doesn't work without a timeout

      setTimeout(function () {
        contentToFocus.focus();
      }, 10);
    }
  }, [shouldBeOpen]); // Handles `aria-haspopup` and `aria-expanded` attributes on the activator

  React.useEffect(function () {
    if (activatorRef && activatorRef.current) {
      var activatorElem = findFocusableChild(activatorRef.current) || activatorRef.current;
      activatorElem.setAttribute('aria-haspopup', 'true');
      activatorElem.setAttribute('aria-expanded', Boolean(shouldBeOpen).toString());
    }
  }, [trigger, activatorRef, open, controlledOpen]); // Toggles uncontrolled open state

  function handleUncontrolledToggle() {
    setOpen(!open);
  } // Focus on activator element
  // This is only called for when closing via keyboard

  function focusOnActivator() {
    if (activatorRef && activatorRef.current) {
      var activatorToFocus = findFocusableChild(activatorRef.current) || activatorRef.current;
      activatorToFocus.focus();
    }
  } // Toggles uncontrolled popovers on clicking outside, and calls `onClose` for controlled popovers

  function handleClick(e) {
    var isInside = popoverRef.current && popoverRef.current.contains(e.target);
    var isOutside = popoverRef.current && !popoverRef.current.contains(e.target) && activatorRef.current && !activatorRef.current.contains(e.target);
    if (isOutside && shouldBeOpen || isInside && closeOnInsideClick && shouldBeOpen) {
      if (onClose) {
        onClose(e);
      }
      if (open) {
        handleUncontrolledToggle();
      }
    }
  } // Toggles uncontrolled popovers on escape keydown, and calls `onClose` for controlled popovers

  function handleEsc(e) {
    if (onClose && shouldBeOpen) {
      onKey('escape', function () {
        onClose(e);
        focusOnActivator();
      })(e);
    }
    if (onClose && shouldBeOpen && closeOnTab) {
      onKey('tab', function () {
        onClose(e);
        focusOnActivator();
      })(e);
    } // Uncontrolled

    if (open) {
      onKey('escape', function () {
        handleUncontrolledToggle();
        focusOnActivator();
      })(e);
    } // Uncontrolled

    if (closeOnTab && open) {
      onKey('tab', function () {
        handleUncontrolledToggle();
        focusOnActivator();
      })(e);
    }
  } // Toggles uncontrolled popovers

  function handleTrigger() {
    if (open !== null) {
      handleUncontrolledToggle();
    }
  }
  function handleActivatorKey(e) {
    if (open === false) {
      onKeys(['arrowUp', 'arrowDown'], function () {
        // Stop arrow keys from scrolling the page
        e.preventDefault();
        handleUncontrolledToggle();
      })(e);
    }
  } // Renders popover content

  function renderPopover() {
    return /*#__PURE__*/React.createElement(Content$3, Object.assign({
      open: shouldBeOpen,
      ref: combineRefs(ref, popoverRef)
    }, rest), children);
  } // Renders popover trigger

  function renderActivator(_ref) {
    var forwardedRef = _ref.activatorRef;
    return /*#__PURE__*/React.createElement(Box, {
      as: Wrapper,
      // Inline block is required to measure and set height correctly
      display: Wrapper === 'span' ? 'inline-block' : null,
      position: "relative",
      onClick: handleTrigger,
      onKeyDown: handleActivatorKey,
      ref: combineRefs(forwardedRef, activatorRef)
    }, trigger);
  }
  return /*#__PURE__*/React.createElement(PopoverOverlay, {
    as: Wrapper,
    id: id,
    open: shouldBeOpen,
    renderActivator: renderActivator,
    renderPopover: renderPopover,
    portalId: portalId
  });
});
Popover.displayName = 'Popover';
function _slicedToArray$a(arr, i) {
  return _arrayWithHoles$a(arr) || _iterableToArrayLimit$a(arr, i) || _unsupportedIterableToArray$b(arr, i) || _nonIterableRest$a();
}
function _nonIterableRest$a() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$b(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$b(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$b(o, minLen);
}
function _arrayLikeToArray$b(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$a(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$a(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject4$8() {
  var data = _taggedTemplateLiteral$F(["\n  ", "\n  list-style-type: none;\n  padding: 0;\n  margin: 0;\n"]);
  _templateObject4$8 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$b() {
  var data = _taggedTemplateLiteral$F(["\n  text-align: left;\n  font-size: ", ";\n  font-weight: ", ";\n  background: ", ";\n  padding-right: ", ";\n  border: ", ";\n  &:hover {\n    background: ", ";\n  }\n  ", "\n"]);
  _templateObject3$b = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$n() {
  var data = _taggedTemplateLiteral$F(["\n  ", "\n"]);
  _templateObject2$n = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$F() {
  var data = _taggedTemplateLiteral$F(["\n  ", "\n"]);
  _templateObject$F = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$F(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$7 = compose(margin, maxWidth);
var StyledWrapper$6 = styled(Box)(_templateObject$F(), system$7);
var StyledChevron$1 = styled(KeyboardArrowDown)(_templateObject2$n(), chevron$1);
var StyledButton$2 = styled(Button$1)(_templateObject3$b(), function (props) {
  return props.theme.fontSizes['200'];
}, function (props) {
  return props.theme.fontWeights['medium'];
}, function (props) {
  return props.disabled ? props.theme.colors.gray['200'] : '';
}, function (props) {
  return props.theme.space[650];
}, function (props) {
  return props.$hasError ? "1px solid ".concat(props.theme.colors.red[700]) : "".concat(props.theme.borders[400]);
}, function (props) {
  return props.disabled ? props.theme.colors.gray['200'] : 'transparent';
}, focusOutline());
var ListBoxButton = /*#__PURE__*/React.forwardRef(function ListBoxButton(props, userRef) {
  return /*#__PURE__*/React.createElement(StyledButton$2, Object.assign({}, props, {
    ref: userRef
  }), props.children);
});
var StyledList = styled(Box)(_templateObject4$8(), maxHeight);
var ListBox = /*#__PURE__*/React.forwardRef(function ListBox(props, userRef) {
  var children = props.children,
    dataId = props['data-id'],
    dataSensitive = props['data-sensitive'],
    dataTrack = props['data-track'],
    placeholder = props.placeholder,
    disabled = props.disabled,
    id = props.id,
    label = props.label,
    labelHidden = props.labelHidden,
    error = props.error,
    errorInLabel = props.errorInLabel,
    required = props.required,
    optional = props.optional,
    helpText = props.helpText,
    defaultValue = props.defaultValue,
    value = props.value,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    name = props.name,
    rest = __rest(props, ["children", 'data-id', 'data-sensitive', 'data-track', "placeholder", "disabled", "id", "label", "labelHidden", "error", "errorInLabel", "required", "optional", "helpText", "defaultValue", "value", "onChange", "onFocus", "onBlur", "name"]);
  var systemProps = pick(rest, system$7.propNames);
  var maxHeightProps = pick(rest, maxHeight.propNames);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray$a(_React$useState, 2),
    open = _React$useState2[0],
    setOpen = _React$useState2[1];
  var _React$useState3 = React.useState(value ? value : defaultValue != null ? defaultValue : placeholder),
    _React$useState4 = _slicedToArray$a(_React$useState3, 2),
    currentValue = _React$useState4[0],
    setCurrentValue = _React$useState4[1];
  var buttonRef = React.useRef();
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId; // Sets the value for controlled inputs

  React.useEffect(function () {
    // Check if this component is controlled
    if (typeof value !== 'undefined') {
      setCurrentValue(value);
    }
  }, [value]);
  function togglePopover(event) {
    event.stopPropagation();
    setOpen(!open);
  }
  function handleClose() {
    setOpen(false);
  }
  function onSelect(value) {
    if (onChange) {
      // This is a fake event, because buttons do not inherently have a change event
      // onChange does not work on hidden inputs
      onChange({
        currentTarget: {
          value: value
        }
      });
    }
    setOpen(false);
    setCurrentValue(value); // Returns focus to the button when closing the popover

    buttonRef.current.focus(); // Calls user's blur method

    if (onBlur) {
      onBlur(null);
    }
  }
  var contentFromValue = React.useMemo(function () {
    var options = getChild('ListBox.Option', children);
    var activeOption = options.find(function (option) {
      return option.props.value === currentValue;
    });
    if (activeOption && activeOption.props.label) {
      return activeOption.props.label;
    }
    return activeOption ? activeOption.props.children : currentValue;
  }, [currentValue]);
  var labelMarkup = label && /*#__PURE__*/React.createElement(Label$1, {
    id: "".concat(id, "Label"),
    htmlFor: "".concat(id, "LabelButton"),
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), error && errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    wrapper: "span"
  }, error), optional && /*#__PURE__*/React.createElement(OptionalLabel, {
    "float": true
  }));
  var helpMarkup = helpText ? /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId
  }, helpText) : null;
  var options = React.useMemo(function () {
    return getChild('ListBox.Option', children);
  }, [children]);
  var _useOptionConstructor = useOptionConstructor({
      options: options,
      value: currentValue,
      onSelect: onSelect,
      open: open,
      placeholder: placeholder
    }),
    optionsMarkup = _useOptionConstructor.optionsMarkup,
    focusContainerProps = _useOptionConstructor.focusContainerProps; // Opens the popover when hitting up or down when focused and closed

  function activatorKeyDown(e) {
    if (!open) {
      onKeys(['arrowDown', 'arrowUp'], function () {
        e.preventDefault();
        togglePopover(e);
      })(e);
    }
  }
  return /*#__PURE__*/React.createElement(StyledWrapper$6, Object.assign({
    tabIndex: -1
  }, systemProps, focusContainerProps), labelMarkup, /*#__PURE__*/React.createElement(Popover, {
    id: "listbox-popover",
    p: "0",
    onClose: handleClose,
    open: open,
    as: "div",
    width: "100%",
    minWidth: "5rem",
    trigger: /*#__PURE__*/React.createElement(Box, {
      position: "relative"
    }, /*#__PURE__*/React.createElement(ListBoxButton, Object.assign({
      id: "".concat(id, "LabelButton"),
      "data-id": "open-listbox",
      fullWidth: true,
      variant: "mutedOutline",
      "aria-invalid": !!error,
      "aria-haspopup": "listbox",
      "aria-pressed": open,
      "aria-expanded": open,
      onClick: togglePopover,
      onFocus: onFocus,
      onKeyDown: activatorKeyDown,
      disabled: disabled,
      "$hasError": !!error
    }, describedBy, {
      ref: combineRefs(buttonRef, userRef)
    }), contentFromValue || placeholder), /*#__PURE__*/React.createElement(StyledChevron$1, {
      size: 24,
      "$disabled": disabled
    }))
  }, /*#__PURE__*/React.createElement(StyledList, Object.assign({
    as: "ul",
    "aria-activedescendant": currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString(),
    width: "100%",
    role: "listbox",
    "aria-labelledby": id,
    maxHeight: "400px"
  }, maxHeightProps, {
    overflowY: "scroll"
  }), optionsMarkup)), helpMarkup, error && !errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId
  }, error), /*#__PURE__*/React.createElement("input", {
    id: id,
    type: "hidden",
    name: name,
    value: currentValue,
    "data-id": dataId,
    "data-sensitive": dataSensitive,
    "data-track": dataTrack,
    required: required
  }));
});
ListBox.displayName = 'ListBox';
ListBox.Option = Option$1;
var ModalContext = /*#__PURE__*/React.createContext({});
var base$1 = function base(props) {
  var overlayHex = "".concat(tokens.color_gray_1000, "70");
  return "\n    position: fixed;\n    z-index: ".concat(tokens.zIndex_overlay, ";\n    top: 0;\n    left: 0;\n    width: 100vw;\n    height: 100vh;\n    min-height: 100%;\n    max-height: 100vh;\n    pointer-events: ").concat(props.$open ? 'auto' : 'none', ";\n    overflow-y: auto;\n    will-change: opacity;\n    background-color: ").concat(props.$open ? overlayHex : 'transparent', ";\n    transition: background-color ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ";\n  ");
};
var wrapper$1 = function wrapper() {
  return "\n  width: 100%;\n  min-height: 100%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n";
};
var focusLock = function focusLock(props) {
  return "\n  width: 100%;\n  max-width: ".concat(props.maxWidth, ";\n");
};
var content = function content() {
  return "\n  position: relative;\n  width: 100%;\n  outline: none;\n  transition: transform ".concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ",\n              visibility ").concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ",\n              opacity ").concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_out, ";\n");
};
var contentAnimation = function contentAnimation(props) {
  var commonExitStyles = "\n    transform: translateY(".concat(tokens.spacing_200, ");\n    opacity: 0;\n    transition: transform ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ",\n                visibility ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ",\n                opacity ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in, ";\n  ");
  switch (props.$state) {
    case 'entered':
      return "\n        pointer-events: auto;\n        transform: translateY(0);\n        visibility: visible;\n        opacity: 1;\n      ";
    case 'exiting':
      return "\n        ".concat(commonExitStyles, "\n      ");
    case 'exited':
      return "\n        ".concat(commonExitStyles, "\n        pointer-events: none;\n      ");
  }
};
var closeButton = function closeButton() {
  return "\n  color: ".concat(tokens.color_gray_700, ";\n  padding: 0;\n  position: absolute;\n  right: ").concat(tokens.spacing_200, ";\n  top: ").concat(tokens.spacing_200, ";\n  width: ").concat(tokens.sizing_650, ";\n  z-index: calc(").concat(tokens.zIndex_overlay, " + 1);\n");
};
function _templateObject$E() {
  var data = _taggedTemplateLiteral$E(["\n  color: ", ";\n"]);
  _templateObject$E = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$E(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledButton$1 = styled(Button$1)(_templateObject$E(), tokens.color_gray_700);
var Header$2 = /*#__PURE__*/React.forwardRef(function Header(props, ref) {
  var children = props.children,
    _props$showCloseButto = props.showCloseButton,
    showCloseButton = _props$showCloseButto === void 0 ? true : _props$showCloseButto;
  var _React$useContext = React.useContext(ModalContext),
    onClose = _React$useContext.onClose;
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "modal-header",
    bg: "white",
    pl: "500",
    pr: "500",
    pt: "500",
    ref: ref
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "h2",
    fontSize: "500",
    lineHeight: "500"
  }, children)), showCloseButton && /*#__PURE__*/React.createElement(StyledButton$1, {
    flat: true,
    size: "small",
    width: tokens.spacing_600,
    px: "0",
    onClick: onClose,
    "data-id": "modal-close"
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Close"), /*#__PURE__*/React.createElement(Close, {
    size: 25
  }))));
});
Header$2.displayName = 'Modal.Header';
var Content = /*#__PURE__*/React.forwardRef(function Content(props, ref) {
  var children = props.children,
    _props$restrictHeight = props.restrictHeight,
    restrictHeight = _props$restrictHeight === void 0 ? true : _props$restrictHeight,
    rest = __rest(props, ["children", "restrictHeight"]);
  var systemProps = pick(rest, padding.propNames);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    "data-id": "modal-content",
    p: "500"
  }, systemProps, {
    maxHeight: restrictHeight ? '60vh' : null,
    overflowY: restrictHeight ? 'auto' : null,
    ref: ref
  }), children);
});
Content.displayName = 'Modal.Content';
var header = function header() {
  return "\n  display: flex;\n";
};
var headerText = function headerText() {
  return "\n  flex: 1 0 0;\n  padding-right: ".concat(tokens.spacing_100, ";\n  font-size: ").concat(tokens.fontSize_400, ";\n  font-weight: ").concat(tokens.fontWeight_semibold, ";\n  line-height: ").concat(tokens.lineHeight_400, ";\n  color: ").concat(tokens.color_gray_900, ";\n\n  text-rendering: optimizeLegibility;\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n");
};
var panel = function panel() {
  return "\n  position: relative;\n  background: ".concat(tokens.color_white, ";\n  border-radius: ").concat(tokens.borderRadius_100, ";\n  padding: ").concat(tokens.spacing_0, ";\n");
};
var panelInner = function panelInner(props) {
  var borderRadius = tokens.borderRadius_100;
  if (props.accent) {
    borderRadius = "0 0 ".concat(tokens.borderRadius_100, " ").concat(tokens.borderRadius_100);
  }
  return "\n    border-radius: ".concat(borderRadius, ";\n    border-top-width: ").concat(props.accent ? '0' : '1px', ";\n  ");
};
var accent = function accent(props) {
  var color;
  switch (props.accentColor) {
    case 'orange':
      color = tokens.color_brand_orange;
      break;
    case 'green':
      color = tokens.color_green_700;
      break;
    case 'yellow':
      color = tokens.color_yellow_400;
      break;
    case 'red':
      color = tokens.color_red_700;
      break;
    case 'gray':
      color = tokens.color_gray_600;
      break;
    case 'blue':
    case 'navy':
    case 'purple':
    default:
      color = tokens.color_blue_700;
  }
  return "\n    height: ".concat(tokens.spacing_100, ";\n    border-radius: ").concat(tokens.borderRadius_100, " ").concat(tokens.borderRadius_100, " 0 0;\n    background-color: ").concat(color, ";\n  ");
};
var body = function body() {
  return "\n  display: flex;\n  border-bottom: ".concat(tokens.borderWidth_100, " solid ").concat(tokens.color_gray_400, ";\n  &:last-of-type {\n    border-bottom: none;\n  }\n");
};
var sectionContent = function sectionContent() {
  return "\n  flex: 1 0 0;\n";
};
var actions = function actions() {
  return "\n  flex: 0;\n  white-space: nowrap;\n";
};

/**
 * Context is created here to pass system prop padding
 * to Panel.Header and Panel.Section
 */

var PanelPaddingContext$1 = /*#__PURE__*/React.createContext({});
function ownKeys$3(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$3(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) {
      _defineProperty$4(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$4(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$3() {
  _extends$3 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3.apply(this, arguments);
}
function _objectWithoutProperties$4(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$4(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$4(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _templateObject3$a() {
  var data = _taggedTemplateLiteral$D(["\n  ", "\n"]);
  _templateObject3$a = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$m() {
  var data = _taggedTemplateLiteral$D(["\n  ", "\n"]);
  _templateObject2$m = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$D() {
  var data = _taggedTemplateLiteral$D(["\n  ", "\n  ", "\n"]);
  _templateObject$D = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$D(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var SectionOuter = styled('div')(_templateObject$D(), body, padding);
var SectionContent = styled('div')(_templateObject2$m(), sectionContent);
var Actions$1 = styled('div')(_templateObject3$a(), actions);
var actionOverrides$1 = {
  flat: true,
  size: 'small'
};
function Section$1(props) {
  var actions = props.actions,
    children = props.children,
    className = props.className,
    rest = _objectWithoutProperties$4(props, ["actions", "children", "className"]);
  var paddingContext = React.useContext(PanelPaddingContext$1);
  var actionMarkup = actions && actions.length ? /*#__PURE__*/React.createElement(Actions$1, null, buttonsFrom(actions, actionOverrides$1)) : null;
  return /*#__PURE__*/React.createElement(SectionOuter, _extends$3({
    className: className
  }, paddingContext, rest), /*#__PURE__*/React.createElement(SectionContent, null, children), actionMarkup);
}
Section$1.displayName = 'Panel.LEGACY.Section';
Section$1.propTypes = _objectSpread$3({
  /**
   * Actions that build buttons. Most button props will work in here.
   * e.g. { content: 'button label', onClick: callback() }
   */
  actions: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.node.isRequired
  })),
  /**
   * Panel Content
   */
  children: PropTypes.node
}, createPropTypes(padding.propNames));
function _objectWithoutProperties$3(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$3(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$3(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _templateObject$C() {
  var data = _taggedTemplateLiteral$C(["\n  ", "\n"]);
  _templateObject$C = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$C(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var OuterWrapper = styled('div')(_templateObject$C(), margin);
function Footer$1(props) {
  var left = props.left,
    right = props.right,
    rest = _objectWithoutProperties$3(props, ["left", "right"]);
  return /*#__PURE__*/React.createElement(OuterWrapper, rest, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    textAlign: "left"
  }, left), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    textAlign: "right"
  }, right)));
}
Footer$1.displayName = 'Panel.LEGACY.Footer';
Footer$1.propTypes = {
  left: PropTypes.node,
  right: PropTypes.node
};
function ownKeys$2(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) {
      _defineProperty$3(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$3(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$2() {
  _extends$2 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2.apply(this, arguments);
}
function _objectWithoutProperties$2(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$2(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$2(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _templateObject3$9() {
  var data = _taggedTemplateLiteral$B(["\n  ", "\n"]);
  _templateObject3$9 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$l() {
  var data = _taggedTemplateLiteral$B(["\n  ", "\n"]);
  _templateObject2$l = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$B() {
  var data = _taggedTemplateLiteral$B(["\n  ", "\n  ", "\n"]);
  _templateObject$B = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$B(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var HeaderOuter = styled('div')(_templateObject$B(), header, padding);
var HeaderText = styled('div')(_templateObject2$l(), headerText);
var Actions = styled('div')(_templateObject3$9(), actions);
var actionOverrides = {
  flat: true,
  size: 'small'
};
var Header$1 = function Header(_ref) {
  var title = _ref.title,
    actions = _ref.actions;
  _ref.p;
  var rest = _objectWithoutProperties$2(_ref, ["title", "actions", "p"]);
  var paddingContext = React.useContext(PanelPaddingContext$1);
  var actionMarkup = actions && actions.length ? /*#__PURE__*/React.createElement(Actions, null, buttonsFrom(actions, actionOverrides)) : null;
  return /*#__PURE__*/React.createElement(HeaderOuter, _extends$2({}, paddingContext, rest, {
    pb: "0"
  }), /*#__PURE__*/React.createElement(HeaderText, null, title), actionMarkup);
};
Header$1.displayName = 'Panel.LEGACY.Header';
Header$1.propTypes = _objectSpread$2({
  title: PropTypes.node
}, createPropTypes(padding.propNames));
function _templateObject$A() {
  var data = _taggedTemplateLiteral$A(["\n  ", "\n"]);
  _templateObject$A = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$A(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var AccentOuter = styled('div')(_templateObject$A(), accent);
var Accent$1 = function Accent(_ref) {
  var accentColor = _ref.accentColor;
  return /*#__PURE__*/React.createElement(AccentOuter, {
    accentColor: accentColor
  });
};
Accent$1.displayName = 'Panel.Accent';
Accent$1.propTypes = {
  accentColor: PropTypes.oneOf(['orange', 'blue', 'red', 'yellow', 'green', 'purple', 'navy', 'gray'])
};
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty$2(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$2(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$1(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _templateObject2$k() {
  var data = _taggedTemplateLiteral$z(["\n  ", "\n  ", "\n"]);
  _templateObject2$k = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$z() {
  var data = _taggedTemplateLiteral$z(["\n  ", "\n  ", "\n"]);
  _templateObject$z = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$z(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var systemOuter$1 = compose(margin, padding, width, height);
var systemInner$1 = compose(border, height);
var PanelOuter = styled('div')(_templateObject$z(), systemOuter$1, panel);
var PanelInner = styled('div')(_templateObject2$k(), systemInner$1, panelInner);
var Panel$1 = /*#__PURE__*/React.forwardRef(function Panel(props, ref) {
  var title = props.title,
    accent = props.accent,
    sectioned = props.sectioned,
    actions = props.actions,
    children = props.children,
    className = props.className,
    rest = _objectWithoutProperties$1(props, ["title", "accent", "sectioned", "actions", "children", "className"]);
  var accentColor = accent === true ? 'blue' : accent;
  var contentMarkup = sectioned ? /*#__PURE__*/React.createElement(Section$1, null, children) : children; // Pick out `p` and `padding` so we only pass one value down
  // `context` is passed to handle directional padding values like `px` or `pr`

  var _pick = pick$1(rest),
    _pick$p = _pick.p,
    contextP = _pick$p === void 0 ? '500' : _pick$p,
    contextPadding = _pick.padding,
    context = _objectWithoutProperties$1(_pick, ["p", "padding"]); // Picks border, width, and height for outer container

  var systemOuterProps = pick(rest, systemOuter$1.propNames); // Picks border for inner container, its width and height should always be 100%

  var _pickNames = pick(rest, border.propNames),
    _pickNames$border = _pickNames.border,
    borderProp = _pickNames$border === void 0 ? '400' : _pickNames$border,
    systemInnerProps = _objectWithoutProperties$1(_pickNames, ["border"]);
  var innerHeight = !!systemOuterProps.height ? '100%' : null;
  return /*#__PURE__*/React.createElement(PanelOuter, _extends$1({
    className: className
  }, rest, {
    ref: ref,
    tabIndex: "-1"
  }, systemOuterProps), accentColor && /*#__PURE__*/React.createElement(Accent$1, {
    accentColor: accentColor
  }), /*#__PURE__*/React.createElement(PanelInner, _extends$1({
    accent: accent,
    border: borderProp,
    height: innerHeight
  }, systemInnerProps), /*#__PURE__*/React.createElement(PanelPaddingContext$1.Provider, {
    value: _objectSpread$1({
      p: contextP || contextPadding
    }, context)
  }, title && /*#__PURE__*/React.createElement(Header$1, {
    title: title,
    actions: actions
  }), contentMarkup)));
});
Panel$1.displayName = 'Panel.LEGACY';
Panel$1.Header = Header$1;
Panel$1.Section = Section$1;
Panel$1.Footer = Footer$1;
Panel$1.Accent = Accent$1;
Panel$1.propTypes = _objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1({
  title: PropTypes.node,
  className: PropTypes.string,
  accent: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['orange', 'blue', 'red', 'yellow', 'green', 'purple', 'navy', 'gray'])]),
  sectioned: PropTypes.bool,
  actions: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.node.isRequired
  })),
  children: PropTypes.node
}, createPropTypes(border.propNames)), createPropTypes(height.propNames)), createPropTypes(margin.propNames)), createPropTypes(padding.propNames)), createPropTypes(width.propNames));
function Accent(_ref) {
  var accentColor = _ref.accentColor;
  var color = React.useMemo(function () {
    switch (accentColor) {
      case 'orange':
        return 'brand.orange';
      case 'green':
        return 'green.700';
      case 'yellow':
        return 'yellow.400';
      case 'red':
        return 'red.700';
      case 'gray':
        return 'gray.600';
      case 'blue':
      default:
        return 'blue.700';
    }
  }, [accentColor]);
  return /*#__PURE__*/React.createElement(Box, {
    bg: color,
    borderTopLeftRadius: "200",
    borderTopRightRadius: "200",
    position: "absolute",
    height: "100",
    top: "-1px",
    left: "-1px",
    right: "-1px"
  });
}
Accent.displayName = 'Panel.Accent';
var Action = /*#__PURE__*/React.forwardRef(function Action(props, userRef) {
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    color: "blue"
  }, props, {
    variant: "text",
    size: "small",
    ref: userRef
  }));
});
Action.displayName = 'Panel.Action';

/**
 * Context is created here to pass system prop padding
 * to Panel.Header and Panel.Section
 */

var PanelPaddingContext = /*#__PURE__*/React.createContext({});
/**
 * Context is created here to pass appearance prop
 * to Panel.Header, Panel.SubHeader, and Panel.Section
 */

var PanelAppearanceContext = /*#__PURE__*/React.createContext({});
var Header = /*#__PURE__*/React.forwardRef(function Header(props, userRef) {
  var _props$as = props.as,
    as = _props$as === void 0 ? 'h3' : _props$as,
    appearance = props.appearance,
    borderBottom = props.borderBottom,
    children = props.children,
    className = props.className;
  var actions = getChild('Panel.Action', children);
  var title = excludeChild(['Panel.Action'], children);
  var paddingContext = React.useContext(PanelPaddingContext);
  var appearanceContext = appearance || React.useContext(PanelAppearanceContext);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    borderBottom: borderBottom ? '300' : 'none',
    className: className
  }, paddingContext, {
    // The array is a hack to override responsive padding context
    // First null to inherit padding context
    pb: borderBottom ? null : [0, null, 0],
    ref: userRef,
    tabIndex: -1,
    color: appearanceContext === 'inverted' ? 'white' : '',
    bg: appearanceContext === 'inverted' ? 'gray.900' : ''
  }), /*#__PURE__*/React.createElement(Columns, {
    collapseBelow: "xs",
    space: "300",
    alignY: "top",
    align: "right"
  }, /*#__PURE__*/React.createElement(Column$1, null, /*#__PURE__*/React.createElement(Text, {
    as: as,
    fontSize: "400",
    lineHeight: "400",
    fontWeight: "semibold"
  }, title)), actions.length ? /*#__PURE__*/React.createElement(Column$1, {
    width: "content"
  }, /*#__PURE__*/React.createElement(Button$1.Group, null, actions)) : null));
});
Header.displayName = 'Panel.Header';
var SubHeader = /*#__PURE__*/React.forwardRef(function SubHeader(props, userRef) {
  var _props$as = props.as,
    as = _props$as === void 0 ? 'h4' : _props$as,
    children = props.children,
    className = props.className,
    appearance = props.appearance;
  var paddingContext = React.useContext(PanelPaddingContext);
  var appearanceContext = appearance || React.useContext(PanelAppearanceContext);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    as: as
  }, paddingContext, {
    className: className,
    pb: [0, null, 0],
    fontSize: "300",
    fontWeight: "normal",
    lineHeight: "300",
    color: appearanceContext === 'inverted' ? 'gray.300' : 'gray.700',
    bg: appearanceContext === 'inverted' ? 'gray.900' : '',
    tabIndex: -1,
    ref: userRef
  }), children);
});
SubHeader.displayName = 'Panel.SubHeader';
function _templateObject$y() {
  var data = _taggedTemplateLiteral$y(["\n  &:last-child {\n    border-bottom: none;\n  }\n"]);
  _templateObject$y = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$y(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledSection = styled(Box)(_templateObject$y());
var Section = /*#__PURE__*/React.forwardRef(function Section(props, userRef) {
  var children = props.children,
    className = props.className,
    appearance = props.appearance,
    rest = __rest(props, ["children", "className", "appearance"]);
  var actions = getChild('Panel.Action', children);
  var content = excludeChild(['Panel.Action'], children);
  var paddingContext = React.useContext(PanelPaddingContext);
  var appearanceContext = appearance || React.useContext(PanelAppearanceContext);
  var systemProps = pick(rest, padding.propNames); // Checks if 'padding', and overwrite 'p' instead of using the 'padding' key

  var paddingProps = React.useMemo(function () {
    if (Object.keys(systemProps).includes('padding')) {
      var _padding = systemProps.padding,
        _rest = __rest(systemProps, ["padding"]);
      return Object.assign(Object.assign(Object.assign({}, paddingContext), {
        p: _padding
      }), _rest);
    }
    return Object.assign(Object.assign({}, paddingContext), systemProps);
  }, [paddingContext, systemProps]);
  return /*#__PURE__*/React.createElement(StyledSection, Object.assign({
    borderBottom: "300",
    className: className,
    ref: userRef,
    tabIndex: -1
  }, paddingProps, {
    bg: appearanceContext === 'inverted' ? 'gray.900' : '',
    color: appearanceContext === 'inverted' ? 'white' : ''
  }), /*#__PURE__*/React.createElement(Columns, {
    collapseBelow: "xs",
    space: "300",
    alignY: "top",
    align: "right"
  }, /*#__PURE__*/React.createElement(Column$1, null, content), actions && actions.length ? /*#__PURE__*/React.createElement(Column$1, {
    width: "content"
  }, /*#__PURE__*/React.createElement(Button$1.Group, null, actions)) : null));
});
Section.displayName = 'Panel.Section';
var systemOuter = compose(margin, width, height);
var systemInner = compose(border, height);
var Panel = /*#__PURE__*/React.forwardRef(function Panel(props, userRef) {
  var accent = props.accent,
    appearance = props.appearance,
    children = props.children,
    className = props.className,
    rest = __rest(props, ["accent", "appearance", "children", "className"]);
  var outerSystemProps = pick(rest, systemOuter.propNames);
  var innerSystemProps = pick(rest, systemInner.propNames);
  var innerHeight = !!outerSystemProps.height ? '100%' : null; // Pick out `p` and `padding` so we only pass one value down
  // `context` is passed to handle directional padding values like `px` or `pr`

  var _a = pick(rest, padding.propNames),
    contextP = _a.p,
    contextPadding = _a.padding,
    context = __rest(_a, ["p", "padding"]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({}, outerSystemProps, {
    ref: userRef,
    tabIndex: -1,
    "data-id": props['data-id']
  }), /*#__PURE__*/React.createElement(Box, Object.assign({
    border: "300",
    borderRadius: "100",
    className: className,
    position: "relative"
  }, innerSystemProps, {
    height: innerHeight,
    bg: appearance === 'inverted' ? 'gray.900' : ''
  }), accent && /*#__PURE__*/React.createElement(Accent, {
    accentColor: accent
  }), /*#__PURE__*/React.createElement(PanelPaddingContext.Provider, {
    value: Object.assign({
      p: contextP || contextPadding || [400, null, 450]
    }, context)
  }, /*#__PURE__*/React.createElement(PanelAppearanceContext.Provider, {
    value: appearance
  }, children))));
});
Panel.displayName = 'Panel';
Panel.LEGACY = Panel$1;
Panel.Header = Header;
Panel.SubHeader = SubHeader;
Panel.Action = Action;
Panel.Section = Section;
var buttonProps = {
  0: {
    color: 'blue',
    key: 'modal-primary'
  },
  1: {
    color: 'blue',
    variant: 'outline',
    key: 'modal-secondary'
  },
  2: {
    color: 'blue',
    variant: 'text',
    key: 'modal-tertiary'
  }
};
function getButtonProps(index, element) {
  return Object.assign(Object.assign({}, buttonProps[index]), element.props);
}
var Footer = /*#__PURE__*/React.forwardRef(function Footer(_ref, ref) {
  var children = _ref.children;
  var buttons = React.Children.toArray(children);
  return /*#__PURE__*/React.createElement(Panel, {
    borderLeft: "none",
    borderRight: "none",
    borderBottom: "none",
    ref: ref
  }, /*#__PURE__*/React.createElement(Panel.Section, null, buttons && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "300"
  }, buttons[0] && /*#__PURE__*/React.cloneElement(buttons[0], getButtonProps(0, buttons[0])), buttons[1] && /*#__PURE__*/React.cloneElement(buttons[1], getButtonProps(1, buttons[1]))), buttons[2] && /*#__PURE__*/React.createElement(Box, null, buttons[2] && /*#__PURE__*/React.cloneElement(buttons[2], getButtonProps(2, buttons[2]))))));
});
Footer.displayName = 'Modal.Footer';
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty$1(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _templateObject5$6() {
  var data = _taggedTemplateLiteral$x(["\n  ", "\n  ", "\n"]);
  _templateObject5$6 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$7() {
  var data = _taggedTemplateLiteral$x(["\n  ", "\n  ", "\n"]);
  _templateObject4$7 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$8() {
  var data = _taggedTemplateLiteral$x(["\n  ", "\n"]);
  _templateObject3$8 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$j() {
  var data = _taggedTemplateLiteral$x(["\n  ", "\n"]);
  _templateObject2$j = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$x() {
  var data = _taggedTemplateLiteral$x(["\n  ", "\n  ", "\n"]);
  _templateObject$x = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$x(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledBase$1 = styled('div')(_templateObject$x(), base$1, padding);
var StyledWrapper$5 = styled('div')(_templateObject2$j(), wrapper$1);
var StyledCloseButton = styled(Button$1)(_templateObject3$8(), closeButton);
var StyledFocusLock$1 = styled(FocusLock)(_templateObject4$7(), focusLock, maxWidth);
var StyledContent$2 = styled('div')(_templateObject5$6(), content, contentAnimation);
var Modal$1 = /*#__PURE__*/React.forwardRef(function Modal(props, userRef) {
  var onClose = props.onClose,
    children = props.children,
    portalId = props.portalId,
    className = props.className,
    showCloseButton = props.showCloseButton,
    maxWidth = props.maxWidth,
    open = props.open,
    rest = _objectWithoutProperties(props, ["onClose", "children", "portalId", "className", "showCloseButton", "maxWidth", "open"]);
  var container = useRef();
  var content = useRef();
  var handleKeydown = function handleKeydown(e) {
    if (open && onClose) {
      onKey('escape', onClose)(e);
    }
  };
  var handleOutsideClick = function handleOutsideClick(e) {
    var isOutside = content && !content.current.contains(e.target) && container && container.current.contains(e.target);
    if (open && isOutside && onClose) {
      onClose(e);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScrollLock, {
    isActive: open
  }), /*#__PURE__*/React.createElement(Portal, {
    containerId: portalId
  }, /*#__PURE__*/React.createElement(TouchScrollable, null, /*#__PURE__*/React.createElement(StyledBase$1, _extends({
    $open: open
  }, rest, {
    className: className,
    onClose: onClose,
    role: "dialog",
    "aria-modal": "true"
  }), /*#__PURE__*/React.createElement(Box, {
    p: ['400', null, '700'],
    size: "100%",
    ref: container
  }, /*#__PURE__*/React.createElement(StyledWrapper$5, null, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "center",
    ref: content,
    width: "100%",
    maxWidth: maxWidth
  }, /*#__PURE__*/React.createElement(ModalContent, {
    open: open,
    ref: userRef
  }, /*#__PURE__*/React.createElement(WindowEvent, {
    event: "keydown",
    handler: handleKeydown
  }), /*#__PURE__*/React.createElement(WindowEvent, {
    event: "click",
    handler: handleOutsideClick
  }), showCloseButton && /*#__PURE__*/React.createElement(StyledCloseButton, {
    flat: true,
    onClick: onClose,
    "data-id": "modal-close"
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Close"), /*#__PURE__*/React.createElement(Close, {
    size: 24
  })), children))))))));
});
var ModalContent = /*#__PURE__*/React.forwardRef(function ModalContent(props, userRef) {
  var open = props.open,
    children = props.children;
  return /*#__PURE__*/React.createElement(StyledFocusLock$1, {
    disabled: !open || isInIframe()
  }, /*#__PURE__*/React.createElement(Transition, {
    mountOnEnter: true,
    unmountOnExit: true,
    "in": open,
    timeout: {
      enter: secondsToMS(tokens.motionDuration_medium),
      exit: secondsToMS(tokens.motionDuration_fast)
    }
  }, function (state) {
    return /*#__PURE__*/React.createElement(StyledContent$2, {
      $state: state,
      tabIndex: "-1",
      ref: userRef,
      "data-id": "modal-content-wrapper"
    }, children);
  }));
});
Modal$1.propTypes = _objectSpread(_objectSpread({
  /**
   * Controlled open state of the modal
   */
  open: PropTypes.bool,
  /**
   * An optional function that is called on key down 'Escape' and on click outside modal content
   */
  onClose: PropTypes.func,
  /**
   * Modal content
   */
  children: PropTypes.node,
  showCloseButton: PropTypes.bool,
  maxWidth: PropTypes.string,
  /**
   * Controls the target container ID for the rendering React portal
   */
  portalId: PropTypes.string
}, createPropTypes(padding.propNames)), createPropTypes(maxWidth.propNames));
Modal$1.defaultProps = {
  maxWidth: '1200'
};
function _templateObject4$6() {
  var data = _taggedTemplateLiteral$w(["\n  ", "\n  ", "\n"]);
  _templateObject4$6 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$7() {
  var data = _taggedTemplateLiteral$w(["\n  ", "\n"]);
  _templateObject3$7 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$i() {
  var data = _taggedTemplateLiteral$w(["\n  ", "\n"]);
  _templateObject2$i = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$w() {
  var data = _taggedTemplateLiteral$w(["\n  ", "\n"]);
  _templateObject$w = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$w(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledBase = styled('div')(_templateObject$w(), base$1);
var StyledWrapper$4 = styled('div')(_templateObject2$i(), wrapper$1);
var StyledFocusLock = styled(FocusLock)(_templateObject3$7(), focusLock);
var StyledContent$1 = styled(Box)(_templateObject4$6(), content, contentAnimation);
var Modal = /*#__PURE__*/React.forwardRef(function Modal(props, userRef) {
  var children = props.children,
    className = props.className,
    _props$closeOnEscape = props.closeOnEscape,
    closeOnEscape = _props$closeOnEscape === void 0 ? true : _props$closeOnEscape,
    _props$closeOnOutside = props.closeOnOutsideClick,
    closeOnOutsideClick = _props$closeOnOutside === void 0 ? true : _props$closeOnOutside,
    onClose = props.onClose,
    open = props.open,
    portalId = props.portalId,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? '1200' : _props$maxWidth;
  var overlayRef = useRef();
  var contentRef = useRef();
  var transitionRef = useRef(); // Calls onClose when clicking outside modal content

  function handleOutsideClick(e) {
    if (!closeOnOutsideClick) {
      return;
    }
    var isOutside = overlayRef.current && overlayRef.current.contains(e.target) && contentRef.current && !contentRef.current.contains(e.target);
    if (isOutside && open && onClose) {
      onClose();
    }
  } // Calls onClose when receiving a escape keydown event

  function handleEscape(e) {
    if (closeOnEscape && open && onClose) {
      onKey('escape', onClose)(e);
    }
  }
  useWindowEvent('keydown', handleEscape);
  useWindowEvent('click', handleOutsideClick); // Focuses container when opening

  React.useLayoutEffect(function () {
    if (open) {
      contentRef.current.focus();
    }
  }, [open]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScrollLock, {
    isActive: open
  }), /*#__PURE__*/React.createElement(Portal, {
    containerId: portalId
  }, /*#__PURE__*/React.createElement(TouchScrollable, null, /*#__PURE__*/React.createElement(StyledBase, {
    "$open": open,
    className: className,
    role: "dialog",
    "aria-modal": "true"
  }, /*#__PURE__*/React.createElement(Box, {
    size: "100%",
    ref: overlayRef,
    "data-id": "modal-overlay"
  }, /*#__PURE__*/React.createElement(StyledWrapper$4, null, /*#__PURE__*/React.createElement(StyledFocusLock, {
    disabled: !open || isInIframe(),
    returnFocus: true
  }, /*#__PURE__*/React.createElement(Transition, {
    mountOnEnter: true,
    unmountOnExit: true,
    "in": open,
    timeout: {
      enter: secondsToMS(tokens.motionDuration_medium),
      exit: secondsToMS(tokens.motionDuration_fast)
    },
    nodeRef: transitionRef
  }, function (state) {
    return /*#__PURE__*/React.createElement("div", {
      ref: transitionRef
    }, /*#__PURE__*/React.createElement(StyledContent$1, {
      "$state": state,
      tabIndex: -1,
      ref: userRef,
      display: "flex",
      justifyContent: "center",
      "data-id": "modal-content-wrapper",
      p: ['400', null, '700']
    }, /*#__PURE__*/React.createElement(Box, {
      ref: contentRef,
      width: "100%",
      maxWidth: maxWidth,
      bg: "white",
      tabIndex: -1,
      "data-id": "modal-content-panel"
    }, /*#__PURE__*/React.createElement(ModalContext.Provider, {
      value: {
        onClose: onClose,
        open: open
      }
    }, children))));
  }))))))));
});
Modal.displayName = 'Modal';
Modal.Header = Header$2;
Modal.Content = Content;
Modal.Footer = Footer;
Modal.LEGACY = Modal$1;
function _slicedToArray$9(arr, i) {
  return _arrayWithHoles$9(arr) || _iterableToArrayLimit$9(arr, i) || _unsupportedIterableToArray$a(arr, i) || _nonIterableRest$9();
}
function _nonIterableRest$9() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$a(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$a(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$a(o, minLen);
}
function _arrayLikeToArray$a(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$9(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$9(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject$v() {
  var data = _taggedTemplateLiteral$v(["\n  text-decoration: none;\n"]);
  _templateObject$v = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$v(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledLink$1 = styled(UnstyledLink)(_templateObject$v());
function Breadcrumb(_a) {
  var content = _a.content,
    rest = __rest(_a, ["content"]);
  return /*#__PURE__*/React.createElement(StyledLink$1, Object.assign({}, rest, {
    fontSize: "200",
    lineHeight: "200",
    fontWeight: "medium"
  }), /*#__PURE__*/React.createElement(Box, {
    as: "span",
    display: "inline-flex",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(ChevronLeft, {
    size: "20"
  }), content));
}
function Subtitle$1(_ref) {
  var subtitle = _ref.subtitle;
  if (!subtitle) {
    return null;
  }
  if (typeof subtitle === 'string') {
    return /*#__PURE__*/React.createElement(Box, {
      as: "h2",
      fontSize: ['300', null, null, '500'],
      lineHeight: ['300', null, null, '500'],
      fontWeight: "normal"
    }, subtitle);
  }
  return /*#__PURE__*/React.createElement("div", null, subtitle);
}
function SecondaryActions(_ref2) {
  var actions = _ref2.actions,
    hasPrimaryAction = _ref2.hasPrimaryAction;
  var visibleActions = React.useMemo(function () {
    return filterByVisible(actions);
  }, [actions]);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray$9(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  if (!visibleActions.length) {
    return null;
  }
  if (visibleActions.length === 1) {
    var action = visibleActions[0];
    return /*#__PURE__*/React.createElement(Button$1, Object.assign({}, action, {
      variant: "outline",
      color: "blue",
      mr: hasPrimaryAction ? ['0', null, '300'] : ' 0',
      ml: hasPrimaryAction ? ['300', null, '0'] : '0'
    }), action.content);
  }
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    mr: hasPrimaryAction ? ['0', null, '300'] : ' 0',
    ml: hasPrimaryAction ? ['300', null, '0'] : '0'
  }, /*#__PURE__*/React.createElement(Popover, {
    id: "page-secondary-actions",
    position: ['bottomRight', null, 'bottomLeft'],
    onClose: function onClose() {
      return setIsOpen(false);
    },
    open: isOpen,
    trigger: /*#__PURE__*/React.createElement(Button$1, {
      "aria-controls": "page-secondary-actions",
      "aria-expanded": isOpen,
      color: "blue",
      onClick: function onClick() {
        return setIsOpen(!isOpen);
      },
      variant: "outline",
      p: "0",
      width: "2.5rem" // Forces a square
    }, /*#__PURE__*/React.createElement(MoreHoriz, null), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "More Options"))
  }, /*#__PURE__*/React.createElement(ActionList, null, visibleActions.map(function (action, i) {
    return /*#__PURE__*/React.createElement(ActionList.Action, Object.assign({
      key: i
    }, action));
  }))));
}
function PrimaryAction(_ref3) {
  var area = _ref3.area,
    action = _ref3.action;
  if (!action && !area) {
    return null;
  }
  if (area) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, area);
  }
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    color: "blue",
    variant: "filled"
  }, action), action.content);
}
function Page(props) {
  var title = props.title,
    subtitle = props.subtitle,
    primaryAction = props.primaryAction,
    primaryArea = props.primaryArea,
    secondaryActions = props.secondaryActions,
    breadcrumbAction = props.breadcrumbAction,
    _props$empty = props.empty,
    empty = _props$empty === void 0 ? {} : _props$empty,
    children = props.children;
  var show = empty.show,
    content = empty.content,
    emptyProps = __rest(empty, ["show", "content"]);
  if (show) {
    return /*#__PURE__*/(
      // @ts-ignore
      React.createElement(EmptyState.LEGACY, Object.assign({
        title: title,
        primaryAction: primaryAction
      }, emptyProps), content)
    );
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
    mt: ['500', null, '700'],
    mb: ['300', null, '450']
  }, breadcrumbAction && /*#__PURE__*/React.createElement(Box, {
    mb: "600"
  }, /*#__PURE__*/React.createElement(Breadcrumb, Object.assign({}, breadcrumbAction))), /*#__PURE__*/React.createElement(Box, {
    display: [null, 'block', 'flex'],
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1"
  }, title && /*#__PURE__*/React.createElement(Box, {
    as: "h1",
    fontWeight: "semibold",
    fontSize: ['500', null, '600'],
    lineHeight: ['500', null, '600']
  }, title), /*#__PURE__*/React.createElement(Subtitle$1, {
    subtitle: subtitle
  })), /*#__PURE__*/React.createElement(Box, {
    flex: "0",
    display: "flex",
    flexDirection: ['row-reverse', null, 'row'],
    justifyContent: "flex-end",
    mt: ['400', null, '0'],
    mb: ['400', null, '0']
  }, /*#__PURE__*/React.createElement(SecondaryActions, {
    actions: secondaryActions,
    hasPrimaryAction: !!primaryAction || !!primaryArea
  }), /*#__PURE__*/React.createElement(PrimaryAction, {
    area: primaryArea,
    action: primaryAction
  })))), children);
}
Page.displayName = 'Page';
var Next = /*#__PURE__*/React.forwardRef(function Next(props, userRef) {
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    size: "small",
    width: "600"
  }, props, {
    ml: props.ml || props.marginRight || 200,
    ref: userRef
  }), /*#__PURE__*/React.createElement(ArrowForward, {
    size: 16
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Next"));
});
Next.displayName = 'Pager.Next';
var Previous = /*#__PURE__*/React.forwardRef(function Previous(props, userRef) {
  return /*#__PURE__*/React.createElement(Button$1, Object.assign({
    size: "small",
    width: "600"
  }, props, {
    mr: props.mr || props.marginRight || 200,
    ref: userRef
  }), /*#__PURE__*/React.createElement(ArrowBack, {
    size: 16
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Previous"));
});
Previous.displayName = 'Pager.Previous';
function _templateObject$u() {
  var data = _taggedTemplateLiteral$u(["\n  ", "\n  display: inline-block;\n"]);
  _templateObject$u = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$u(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledPager = styled('div')(_templateObject$u(), margin);
function Pager(props) {
  var children = props.children,
    id = props.id,
    dataId = props['data-id'],
    rest = __rest(props, ["children", "id", 'data-id']);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(StyledPager, Object.assign({
    id: id,
    "data-id": dataId
  }, systemProps), children);
}
Pager.Next = Next;
Pager.Previous = Previous;
Pager.displayName = 'Pager';
function _slicedToArray$8(arr, i) {
  return _arrayWithHoles$8(arr) || _iterableToArrayLimit$8(arr, i) || _unsupportedIterableToArray$9(arr, i) || _nonIterableRest$8();
}
function _nonIterableRest$8() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$9(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$9(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$9(o, minLen);
}
function _arrayLikeToArray$9(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$8(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$8(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject2$h() {
  var data = _taggedTemplateLiteral$t(["\n  ", "\n"]);
  _templateObject2$h = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$t() {
  var data = _taggedTemplateLiteral$t(["\n  fill: ", ";\n"]);
  _templateObject$t = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$t(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledMoreHoriz = styled(MoreHoriz)(_templateObject$t(), tokens.color_gray_500);
var Wrapper$3 = styled.div(_templateObject2$h(), margin);
/**
 * Pagination component
 * @see https://design.sparkpost.com/components/pagination
 */

function Pagination(props) {
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray$8(_React$useState, 2),
    index = _React$useState2[0],
    setIndex = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray$8(_React$useState3, 2),
    hasPrevious = _React$useState4[0],
    setHasPrevious = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray$8(_React$useState5, 2),
    hasNext = _React$useState6[0],
    setHasNext = _React$useState6[1];
  var breakpoint = useBreakpoint();
  var _props$currentPage = props.currentPage,
    currentPage = _props$currentPage === void 0 ? 1 : _props$currentPage,
    pages = props.pages,
    pageRange = props.pageRange,
    onChange = props.onChange,
    marginsHidden = props.marginsHidden,
    rest = __rest(props, ["currentPage", "pages", "pageRange", "onChange", "marginsHidden"]);
  var systemProps = pick$1(rest);
  var calculatedPageRange = React.useMemo(function () {
    return ['default', 'xs', 'sm'].includes(breakpoint) ? 3 : pageRange;
  }, [breakpoint]);
  function handlePageChange(index) {
    setIndex(index);
    setHasPrevious(index > 0);
    setHasNext(index < pages - 1);
    onChange && onChange(index);
  }
  function handleNext() {
    handlePageChange(index + 1);
  }
  function handlePrevious() {
    handlePageChange(index - 1);
  }
  function _getStart() {
    var start = 0;
    var half = Math.floor(calculatedPageRange / 2);
    if (index > half) {
      start = index - half;
    }
    if (index + half + 1 > pages) {
      start = pages - calculatedPageRange;
    }
    return start;
  }
  React.useEffect(function () {
    handlePageChange(currentPage - 1);
  }, [pages, calculatedPageRange, currentPage]);
  var start = _getStart();
  var buttonMarkup = React.useMemo(function () {
    function _createButtons(start, end) {
      var buttons = [];
      for (var currentIndex = start; currentIndex < end; currentIndex++) {
        buttons.push({
          content: "".concat(currentIndex + 1),
          onClick: handlePageChange.bind(this, currentIndex),
          variant: currentIndex === index ? 'filled' : 'text',
          size: 'small',
          color: currentIndex === index ? 'blue' : 'gray',
          mx: '100',
          width: currentIndex + 1 < 100 ? tokens.spacing_600 : 'auto'
        });
      }
      return buttonsFrom(buttons);
    }
    if (pages <= calculatedPageRange) {
      return _createButtons(0, pages);
    } else {
      return _createButtons(start, start + calculatedPageRange);
    }
  }, [index, pages, calculatedPageRange]);
  var firstButton = !marginsHidden && start > 1 ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button$1, {
    variant: "text",
    width: tokens.spacing_600,
    size: "small",
    onClick: function onClick() {
      return handlePageChange(0);
    }
  }, "1"), /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    pl: 200,
    pr: 200
  }, /*#__PURE__*/React.createElement(StyledMoreHoriz, {
    size: 24
  }))) : null;
  var lastButton = !marginsHidden && start + calculatedPageRange < pages ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    pl: 200,
    pr: 200
  }, /*#__PURE__*/React.createElement(StyledMoreHoriz, {
    size: 24
  })), /*#__PURE__*/React.createElement(Button$1, {
    variant: "text",
    size: "small",
    width: pages + 1 < 100 ? tokens.spacing_600 : 'auto',
    onClick: function onClick() {
      return handlePageChange(pages - 1);
    }
  }, pages)) : null;
  return /*#__PURE__*/React.createElement(Wrapper$3, Object.assign({}, systemProps), /*#__PURE__*/React.createElement(Box, {
    display: "inline-flex",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Pager.Previous, {
    variant: "text",
    onClick: handlePrevious,
    disabled: !hasPrevious
  }), firstButton, /*#__PURE__*/React.createElement(Box, {
    display: "inline-block"
  }, buttonMarkup), lastButton, /*#__PURE__*/React.createElement(Pager.Next, {
    variant: "text",
    onClick: handleNext,
    disabled: !hasNext
  })));
}
Pagination.displayName = 'Pagination';
function get(object, str) {
  var parts = str.split('.');
  var result = parts.reduce(function (o, key) {
    if (!o || !o[key]) {
      return undefined;
    }
    return o[key];
  }, object);
  return result;
}
function getTheme(str) {
  return function (_ref) {
    var theme = _ref.theme;
    return get(theme, str);
  };
}
function _templateObject5$5() {
  var data = _taggedTemplateLiteral$s(["\n  position: absolute;\n  bottom: ", ";\n  left: ", ";\n  border-radius: ", ";\n  background: ", ";\n  width: ", ";\n  height: ", ";\n"]);
  _templateObject5$5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$5() {
  var data = _taggedTemplateLiteral$s(["\n  border-radius: ", ";\n  border: 2px solid transparent;\n  width: ", ";\n  height: ", ";\n\n  ", "\n"]);
  _templateObject4$5 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$6() {
  var data = _taggedTemplateLiteral$s(["\n  text-decoration: none;\n\n  font-weight: ", ";\n\n  &,\n  &:visited {\n    color: ", ";\n  }\n\n  &:hover {\n    color: ", ";\n  }\n\n  ", "\n"]);
  _templateObject3$6 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$g() {
  var data = _taggedTemplateLiteral$s(["\n  position: relative;\n  ", ":first-child & {\n    display: none;\n  }\n"]);
  _templateObject2$g = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$s() {
  var data = _taggedTemplateLiteral$s([""]);
  _templateObject$s = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$s(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledWrapper$3 = styled(Box)(_templateObject$s());
var StyledConnector = styled.div(_templateObject2$g(), StyledWrapper$3);
var StyledLink = styled(UnstyledLink)(_templateObject3$6(), function (_ref) {
  var $selected = _ref.$selected;
  return $selected ? getTheme('fontWeights.semibold') : getTheme('fontWeights.normal');
}, function (_ref2) {
  var $selected = _ref2.$selected,
    $completed = _ref2.$completed;
  return $selected || $completed ? getTheme('colors.gray.900') : getTheme('colors.gray.700');
}, getTheme('colors.blue.700'), function (_ref3) {
  var disabled = _ref3.disabled;
  if (disabled) {
    return "\n        cursor: not-allowed;\n        &:hover {\n          color: inherit;\n        }\n      ";
  }
});
var StyledCircle = styled.div(_templateObject4$5(), getTheme('radii.circle'), getTheme('sizes.450'), getTheme('sizes.450'), function (_ref4) {
  var $completed = _ref4.$completed,
    $selected = _ref4.$selected,
    $disabled = _ref4.$disabled,
    theme = _ref4.theme;
  var color = get(theme, 'colors.gray.700');
  var bg = 'transparent;';
  if ($completed) {
    bg = get(theme, 'colors.blue.700');
  }
  if ($selected || $completed) {
    color = get(theme, 'colors.blue.700');
  }
  if ($disabled) {
    color = get(theme, 'colors.gray.600');
    if ($completed) {
      bg = get(theme, 'colors.gray.600');
    }
  }
  return "\n      background: ".concat(bg, ";\n      border-color: ").concat(color, ";\n    ");
});
var StyledInnerCircle = styled.div(_templateObject5$5(), getTheme('sizes.100'), getTheme('sizes.100'), getTheme('radii.circle'), function (_ref5) {
  var $disabled = _ref5.$disabled;
  return $disabled ? getTheme('colors.gray.700') : getTheme('colors.blue.700');
}, getTheme('sizes.300'), getTheme('sizes.300'));
function Indicator(props) {
  var disabled = props.disabled,
    selected = props.selected,
    completed = props.completed;
  var connectorColor = React.useMemo(function () {
    if (disabled) {
      return 'gray.600';
    }
    if (completed || selected) {
      return 'blue.700';
    }
    return 'gray.700';
  }, []);
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(StyledConnector, {
    "$disabled": disabled
  }, /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    left: "9px",
    width: "2px",
    height: "550",
    bg: connectorColor
  })), /*#__PURE__*/React.createElement(StyledCircle, {
    "$completed": completed,
    "$selected": selected,
    "$disabled": disabled
  }), selected && /*#__PURE__*/React.createElement(StyledInnerCircle, {
    "$disabled": disabled
  }));
}
var Step = /*#__PURE__*/React.forwardRef(function Step(props, userRef) {
  var children = props.children,
    selected = props.selected,
    completed = props.completed,
    disabled = props.disabled,
    rest = __rest(props, ["children", "selected", "completed", "disabled"]);
  return /*#__PURE__*/React.createElement(StyledWrapper$3, {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gridGap: "300"
  }, /*#__PURE__*/React.createElement(Indicator, {
    completed: completed,
    selected: selected,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Box, {
    height: "450",
    alignSelf: "end"
  }, /*#__PURE__*/React.createElement(StyledLink, Object.assign({
    "$selected": selected,
    "$completed": completed,
    disabled: disabled
  }, rest, {
    ref: userRef
  }), children)));
});
Step.displayName = 'Progress.Step';
function _templateObject$r() {
  var data = _taggedTemplateLiteral$r(["\n  ", "\n"]);
  _templateObject$r = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$r(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$6 = compose(margin);
var Wrapper$2 = styled.div(_templateObject$r(), system$6);
var Progress = /*#__PURE__*/React.forwardRef(function Progress(props, userRef) {
  var children = props.children,
    id = props.id,
    dataId = props['data-id'],
    rest = __rest(props, ["children", "id", 'data-id']);
  var systemProps = pick(rest, system$6.propNames);
  return /*#__PURE__*/React.createElement(Wrapper$2, Object.assign({
    id: id,
    "data-id": dataId
  }, systemProps, {
    ref: userRef
  }), children);
});
Progress.displayName = 'Progress';
Progress.Step = Step;
var outerBase = function outerBase() {
  return "\n  position: relative;\n  background-color: ".concat(tokens.color_gray_300, ";\n  border-radius: ").concat(tokens.borderRadius_100, ";\n  width: 100%;\n");
};
var innerBase = function innerBase() {
  return "\n  background-color: ".concat(tokens.color_blue_700, ";\n  border-radius: ").concat(tokens.borderRadius_100, " ").concat(tokens.borderRadius_0, " ").concat(tokens.borderRadius_0, " ").concat(tokens.borderRadius_100, ";\n");
};
var visualSize = function visualSize(props) {
  switch (props.$visualSize) {
    case 'small':
      return "\n        height: 0.25rem;\n      ";
    case 'normal':
    default:
      return "\n        height: 0.75rem;\n      ";
  }
};
var calculatedWidth = function calculatedWidth(props) {
  var percentage = props.$completed;
  if (percentage > 100) {
    percentage = 100;
  } else if (percentage < 1) {
    percentage = 0;
  }
  return "width: ".concat(percentage, "%");
};
function _templateObject2$f() {
  var data = _taggedTemplateLiteral$q(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject2$f = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$q() {
  var data = _taggedTemplateLiteral$q(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$q = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$q(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$5 = compose(margin);
var StyledProgressBarOuter = styled(Box)(_templateObject$q(), outerBase, visualSize, system$5);
var StyledProgressBarInner = styled(Box)(_templateObject2$f(), innerBase, visualSize, calculatedWidth);
var ProgressBar = /*#__PURE__*/React.forwardRef(function ProgressBar(props, userRef) {
  var _props$completed = props.completed,
    completed = _props$completed === void 0 ? 0 : _props$completed,
    label = props.label,
    _props$size = props.size,
    size = _props$size === void 0 ? 'normal' : _props$size,
    valueText = props.valueText,
    id = props.id,
    dataId = props['data-id'],
    rest = __rest(props, ["completed", "label", "size", "valueText", "id", 'data-id']);
  var systemProps = pick(rest, system$5.propNames);
  return /*#__PURE__*/React.createElement(StyledProgressBarOuter, Object.assign({
    "$visualSize": size,
    role: "progressbar",
    "aria-label": label,
    "aria-valuenow": completed,
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    "aria-valuetext": valueText
  }, systemProps, {
    "data-id": dataId,
    id: id,
    ref: userRef
  }), /*#__PURE__*/React.createElement(StyledProgressBarInner, {
    "$completed": completed,
    "$visualSize": size
  }));
});
ProgressBar.displayName = 'ProgressBar';
var Title = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  var children = props.children;
  return /*#__PURE__*/React.createElement(Text, {
    as: "span",
    fontSize: "200",
    lineHeight: "200",
    fontWeight: "medium",
    color: "gray.900",
    ref: userRef
  }, children);
});
Title.displayName = 'QueryCard.Title';
var Operator = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  var children = props.children;
  return /*#__PURE__*/React.createElement(Text, {
    as: "span",
    fontSize: "200",
    lineHeight: "200",
    fontWeight: "normal",
    color: "gray.700",
    ref: userRef
  }, children);
});
Operator.displayName = 'QueryCard.Operator';
var Legend = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  var _props$color = props.color,
    color = _props$color === void 0 ? 'blue.700' : _props$color;
  return /*#__PURE__*/React.createElement(Box, {
    display: "inline-block",
    borderRadius: "circle",
    size: "400",
    bg: color,
    ref: userRef
  });
});
Legend.displayName = 'QueryCard.Legend';
var Subtitle = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  var children = props.children;
  return /*#__PURE__*/React.createElement(Text, {
    fontSize: "200",
    lineHeight: "200",
    fontWeight: "medium",
    color: "gray.700",
    ref: userRef
  }, children);
});
Subtitle.displayName = 'QueryCard.Subtitle';
function _templateObject$p() {
  var data = _taggedTemplateLiteral$p(["\n  & > div:not(:first-child) {\n    margin-top: -1px;\n    border-top-right-radius: 0;\n    border-top-left-radius: 0;\n  }\n\n  & > div:first-child:not(:only-child) {\n    border-bottom-right-radius: 0;\n    border-bottom-left-radius: 0;\n  }\n"]);
  _templateObject$p = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$p(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledWrapper$2 = styled.div(_templateObject$p());
var Group$2 = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  return /*#__PURE__*/React.createElement(StyledWrapper$2, {
    ref: userRef
  }, props.children);
});
Group$2.displayName = 'QueryCard.Group';
var QueryCard = /*#__PURE__*/React.forwardRef(function QueryCard(props, userRef) {
  var children = props.children,
    id = props.id,
    onClose = props.onClose;
  var hasLegend = Boolean(getChild('QueryCard.Legend', children).length);
  return /*#__PURE__*/React.createElement(Box, {
    id: id,
    ref: userRef,
    borderRadius: "100",
    border: "400",
    p: "200",
    position: "relative"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "flex-start"
  }, hasLegend && /*#__PURE__*/React.createElement(Box, {
    flex: "0",
    pr: "200"
  }, getChild('QueryCard.Legend', children)), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    mt: "-2px"
  }, getChild('QueryCard.Title', children), " ", getChild('QueryCard.Operator', children), /*#__PURE__*/React.createElement(Box, null, getChild('QueryCard.Subtitle', children)))), /*#__PURE__*/React.createElement(Box, null, onClose && /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    top: "100",
    right: "100"
  }, /*#__PURE__*/React.createElement(Button$1, {
    size: "small",
    variant: "text",
    onClick: onClose,
    px: "200"
  }, /*#__PURE__*/React.createElement(Button$1.Icon, {
    as: Close
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Close")))));
});
QueryCard.displayName = 'QueryCard';
QueryCard.Title = Title;
QueryCard.Operator = Operator;
QueryCard.Legend = Legend;
QueryCard.Subtitle = Subtitle;
QueryCard.Group = Group$2;
function _templateObject$o() {
  var data = _taggedTemplateLiteral$o(["\n  border: none;\n  padding: 0;\n  ", "\n"]);
  _templateObject$o = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$o(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledGroup = styled('fieldset')(_templateObject$o(), margin);
function Group$1(props) {
  var dataId = props['data-id'],
    children = props.children,
    className = props.className,
    label = props.label,
    labelHidden = props.labelHidden,
    required = props.required,
    optional = props.optional,
    rest = __rest(props, ['data-id', "children", "className", "label", "labelHidden", "required", "optional"]);
  var systemProps = pick$1(rest);
  return /*#__PURE__*/React.createElement(StyledGroup, Object.assign({}, systemProps, {
    "aria-required": required,
    className: className,
    "data-id": dataId
  }), label && /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement(Label$1, {
    as: "legend",
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label, required && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Required")), optional && /*#__PURE__*/React.createElement(OptionalLabel, null))), /*#__PURE__*/React.createElement(Stack, {
    space: "100"
  }, children));
}
Group$1.displayName = 'Radio.Group';
function _templateObject5$4() {
  var data = _taggedTemplateLiteral$n(["\n  ", "\n\n  & ~ span > * {\n    border-radius: ", ";\n  }\n\n  &:focus ~ span ", ", &:focus ~ span ", " {\n    box-shadow: 0 0 0 0px ", ", 0 0 0 2px ", ";\n  }\n\n  &:checked {\n    & ~ span ", " {\n      opacity: 0;\n    }\n\n    & ~ span ", " {\n      opacity: 1;\n    }\n  }\n\n  &:hover:not(:disabled) {\n    & ~ span ", " {\n      fill: ", ";\n    }\n  }\n\n  &:disabled ~ span ", ", &:disabled ~ span ", " {\n    cursor: not-allowed;\n    fill: ", ";\n  }\n"]);
  _templateObject5$4 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$4() {
  var data = _taggedTemplateLiteral$n(["\n  ", "\n  opacity: 0;\n  fill: ", ";\n"]);
  _templateObject4$4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$5() {
  var data = _taggedTemplateLiteral$n(["\n  ", "\n  opacity: 1;\n  fill: ", ";\n"]);
  _templateObject3$5 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$e() {
  var data = _taggedTemplateLiteral$n(["\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  white-space: nowrap;\n  user-select: none;\n\n  &:hover {\n    cursor: ", ";\n  }\n"]);
  _templateObject2$e = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$n() {
  var data = _taggedTemplateLiteral$n(["\n  ", "\n  position: relative;\n  border: none;\n  padding: 0;\n"]);
  _templateObject$n = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$n(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Wrapper$1 = styled('div')(_templateObject$n(), margin);
var StyledLabel$1 = styled('label')(_templateObject2$e(), function (props) {
  return props.$disabled ? 'not-allowed' : 'pointer';
});
var commonCheckedStyles = "\n  position: absolute;\n  top: 0;\n  left: 0;\n  transition: opacity ".concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", fill ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", box-shadow ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n");
var StyledUnchecked$1 = styled(Box)(_templateObject3$5(), commonCheckedStyles, function (props) {
  return props.$error ? tokens.color_red_700 : tokens.color_gray_700;
});
var StyledChecked$1 = styled(Box)(_templateObject4$4(), commonCheckedStyles, function (props) {
  return props.$error ? tokens.color_red_700 : tokens.color_blue_700;
});
var StyledInput$4 = styled('input')(_templateObject5$4(), visuallyHidden, tokens.borderRadius_circle, StyledChecked$1, StyledUnchecked$1, tokens.color_white, tokens.color_blue_700, StyledUnchecked$1, StyledChecked$1, StyledUnchecked$1, tokens.color_gray_800, StyledChecked$1, StyledUnchecked$1, tokens.color_gray_600);
var Radio = /*#__PURE__*/React.forwardRef(function Radio(props, userRef) {
  var id = props.id,
    name = props.name,
    checked = props.checked,
    label = props.label,
    labelHidden = props.labelHidden,
    disabled = props.disabled,
    error = props.error,
    value = props.value,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    helpText = props.helpText,
    rest = __rest(props, ["id", "name", "checked", "label", "labelHidden", "disabled", "error", "value", "onChange", "onFocus", "onBlur", "helpText"]);
  var componentProps = omit(rest, margin.propNames);
  var systemProps = pick$1(rest);
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId;
  return /*#__PURE__*/React.createElement(Wrapper$1, Object.assign({}, systemProps), /*#__PURE__*/React.createElement(StyledLabel$1, {
    htmlFor: id,
    "$disabled": disabled
  }, /*#__PURE__*/React.createElement(StyledInput$4, Object.assign({
    "aria-invalid": !!error,
    id: id,
    name: name,
    value: value,
    checked: checked,
    disabled: disabled,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    type: "radio",
    ref: userRef
  }, describedBy, componentProps)), /*#__PURE__*/React.createElement(Box, {
    as: "span",
    position: "relative",
    display: "block",
    alignItems: "center",
    size: "1rem" // Matches height of Label
  }, /*#__PURE__*/React.createElement(StyledUnchecked$1, {
    "$error": error,
    size: "1rem",
    as: RadioButtonUnchecked
  }), /*#__PURE__*/React.createElement(StyledChecked$1, {
    "$error": error,
    size: "1rem",
    as: RadioButtonChecked
  })), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    pl: "200"
  }, label && /*#__PURE__*/React.createElement(Label$1, {
    as: "span" // Outer wrapper already includes a label
    ,

    id: id,
    label: label,
    labelHidden: labelHidden,
    fontWeight: "400",
    mb: "0" // TODO Remove once margin 0 is baked into Label
  }))), helpText && /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId,
    ml: "500",
    mt: "0"
  }, helpText), error && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    error: error,
    ml: "500"
  }));
});
Radio.displayName = 'Radio';
Radio.Group = Group$1;
function _templateObject2$d() {
  var data = _taggedTemplateLiteral$m(["\n  ", "\n"]);
  _templateObject2$d = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$m() {
  var data = _taggedTemplateLiteral$m(["\n  border: none;\n  padding: 0;\n  ", "\n"]);
  _templateObject$m = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$m(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Fieldset = styled.fieldset(_templateObject$m(), margin);
var CardWrapper = styled.div(_templateObject2$d(), function (props) {
  if (props.$space === 'compact') {
    return "\n        & > div:not(:first-child) label {\n          margin-top: -1px;\n          border-top-right-radius: 0;\n          border-top-left-radius: 0;\n        }\n\n        & > div:not(:last-child) label {\n          border-bottom-right-radius: 0;\n          border-bottom-left-radius: 0;\n        }\n      ";
  }
  return "";
});
var Group = /*#__PURE__*/React.forwardRef(function Group(props, userRef) {
  var children = props.children,
    collapseBelow = props.collapseBelow,
    helpText = props.helpText,
    id = props.id,
    label = props.label,
    labelHidden = props.labelHidden,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'vertical' : _props$orientation,
    optional = props.optional,
    space = props.space,
    rest = __rest(props, ["children", "collapseBelow", "helpText", "id", "label", "labelHidden", "orientation", "optional", "space"]);
  var items = React.Children.toArray(children);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(Fieldset, Object.assign({
    "data-id": rest['data-id'],
    id: id,
    ref: userRef
  }, systemProps), /*#__PURE__*/React.createElement(Label$1, {
    as: "legend",
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), optional && /*#__PURE__*/React.createElement(OptionalLabel, null)), helpText && /*#__PURE__*/React.createElement(Box, {
    mb: "200"
  }, /*#__PURE__*/React.createElement(HelpText, {
    mt: "0"
  }, helpText)), orientation === 'horizontal' && /*#__PURE__*/React.createElement(Columns, {
    space: "300",
    collapseBelow: collapseBelow
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement(Column$1, {
      key: i
    }, item);
  })), orientation === 'vertical' && /*#__PURE__*/React.createElement(CardWrapper, {
    "$space": space
  }, space !== 'compact' ? /*#__PURE__*/React.createElement(Stack, {
    space: "300"
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  }))), orientation === 'grid' && /*#__PURE__*/React.createElement(Box, {
    display: "grid",
    gridTemplateColumns: ['1fr', null, '1fr 1fr'],
    gridGap: "300"
  }, items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, item);
  })));
});
Group.displayName = 'RadioCard.Group';
function _templateObject5$3() {
  var data = _taggedTemplateLiteral$l(["\n  input:checked ~ ", " & {\n    color: ", ";\n  }\n\n  input:disabled ~ ", " & {\n    color: ", ";\n  }\n"]);
  _templateObject5$3 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$3() {
  var data = _taggedTemplateLiteral$l(["\n  ", "\n  opacity: 0;\n  left: -1rem;\n  fill: ", ";\n\n  input:checked ~ ", " & {\n    opacity: 1;\n  }\n"]);
  _templateObject4$3 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$4() {
  var data = _taggedTemplateLiteral$l(["\n  ", "\n  opacity: 1;\n  fill: ", ";\n\n  input:checked ~ ", " & {\n    opacity: 0;\n  }\n"]);
  _templateObject3$4 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$c() {
  var data = _taggedTemplateLiteral$l(["\n  ", "\n  &:hover {\n    & + ", " {\n      z-index: ", ";\n    }\n  }\n  &:checked {\n    & + ", " {\n      z-index: ", ";\n    }\n  }\n"]);
  _templateObject2$c = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$l() {
  var data = _taggedTemplateLiteral$l(["\n  display: block;\n  position: relative;\n  background: ", ";\n  padding: ", ";\n  border: ", ";\n  border-radius: ", ";\n  cursor: pointer;\n  transition: ", ";\n  transition-property: box-shadow, border;\n  height: 100%;\n  ", "\n\n  &:hover {\n    border: ", " solid\n      ", ";\n  }\n\n  input:checked ~ & {\n    border: ", " solid\n      ", ";\n  }\n\n  input:disabled ~ & {\n    cursor: not-allowed;\n    background: ", ";\n    border: ", " solid\n      ", ";\n    color: ", ";\n  }\n"]);
  _templateObject$l = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$l(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledLabel = styled(Box)(_templateObject$l(), function (props) {
  return props.theme.colors.white;
}, function (props) {
  return props.$size === 'small' ? props.theme.space['200'] : props.theme.space['500'];
}, function (props) {
  return props.theme.borders['400'];
}, function (props) {
  return props.theme.radii['200'];
}, tokens.motionDuration_medium, focusOutline({
  offset: '2px',
  modifier: 'input:focus ~ &'
}), function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['400'] : props.theme.colors.gray['600'];
}, function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['400'] : props.theme.colors.blue['700'];
}, function (props) {
  return props.theme.colors.gray['100'];
}, function (props) {
  return props.theme.borderWidths['100'];
}, function (props) {
  return props.theme.colors.gray['300'];
}, function (props) {
  return props.theme.colors.gray['800'];
});
var StyledInput$3 = styled.input(_templateObject2$c(), visuallyHidden, StyledLabel, tokens.zIndex_default, StyledLabel, tokens.zIndex_above);
var commonCheckStyle = function commonCheckStyle(props) {
  return "\n  position: relative;\n  transition: opacity ".concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", fill ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ", box-shadow ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n  \n  input:disabled ~ ").concat(StyledLabel, " & {\n    fill: ").concat(props.theme.colors.gray['700'], ";\n  }\n");
};
var StyledUnchecked = styled.svg(_templateObject3$4(), function (props) {
  return commonCheckStyle(props);
}, function (props) {
  return props.theme.colors.gray['700'];
}, StyledLabel);
var StyledChecked = styled.svg(_templateObject4$3(), function (props) {
  return commonCheckStyle(props);
}, function (props) {
  return props.theme.colors.blue['700'];
}, StyledLabel);
var StyledHeader = styled(Box)(_templateObject5$3(), StyledLabel, function (props) {
  return props.$size === 'small' ? props.theme.colors.gray['800'] : props.theme.colors.blue['700'];
}, StyledLabel, function (props) {
  return props.theme.colors.gray['800'];
});
var RadioCard = /*#__PURE__*/React.forwardRef(function RadioCard(props, userRef) {
  var id = props.id,
    checked = props.checked,
    children = props.children,
    defaultChecked = props.defaultChecked,
    disabled = props.disabled,
    label = props.label,
    name = props.name,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    _props$size = props.size,
    size = _props$size === void 0 ? 'default' : _props$size,
    value = props.value,
    dataId = props['data-id'],
    dataTrack = props['data-track'],
    rest = __rest(props, ["id", "checked", "children", "defaultChecked", "disabled", "label", "name", "onChange", "onFocus", "onBlur", "size", "value", 'data-id', 'data-track']);
  var isSmall = size === 'small';
  return /*#__PURE__*/React.createElement(Box, {
    "data-id": "radio-card",
    height: "100%"
  }, /*#__PURE__*/React.createElement(StyledInput$3, Object.assign({
    checked: checked,
    "data-id": dataId,
    "data-track": dataTrack,
    disabled: disabled,
    defaultChecked: defaultChecked,
    id: id,
    name: name,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: userRef,
    type: "radio",
    value: value
  }, rest)), /*#__PURE__*/React.createElement(StyledLabel, {
    as: "label",
    htmlFor: id,
    "$size": size
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 auto",
    width: isSmall ? '200' : '400'
  }, /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    top: isSmall ? '100' : '500',
    left: isSmall ? '200' : '500'
  }, /*#__PURE__*/React.createElement(StyledUnchecked, {
    size: "1rem",
    as: RadioButtonUnchecked
  }), /*#__PURE__*/React.createElement(StyledChecked, {
    size: "1rem",
    as: RadioButtonChecked
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    pl: "300"
  }, /*#__PURE__*/React.createElement(StyledHeader, {
    "data-id": "radio-card-header",
    "$size": size,
    fontSize: isSmall ? '200' : '300',
    lineHeight: isSmall ? '200' : '400',
    fontWeight: isSmall ? 'medium' : 'semibold'
  }, label))), children && /*#__PURE__*/React.createElement(Box, {
    "data-id": "radio-card-content",
    pt: isSmall ? '0' : '200',
    pl: isSmall ? '450' : '0',
    fontSize: isSmall ? '200' : '300',
    lineHeight: isSmall ? '200' : '300',
    color: isSmall ? 'gray.700' : 'gray.900'
  }, children)));
});
RadioCard.displayName = 'RadioCard';
RadioCard.Group = Group;
var select = function select(props) {
  return "\n  appearance: none;\n  font-weight: ".concat(tokens.fontWeight_medium, ";\n  font-size: ").concat(tokens.fontSize_200, ";\n  &:hover {\n    cursor: ").concat(props.disabled ? 'not-allowed;' : 'pointer;', "\n  }\n");
};
var chevron = function chevron(props) {
  return "\n  position: absolute;\n  z-index: ".concat(tokens.zIndex_default, ";\n  top: 8%;\n  right: ").concat(tokens.spacing_300, ";\n  height: 100%;\n  fill: ").concat(tokens.color_blue_700, ";\n  user-select: none;\n  pointer-events: none;\n  ").concat(props.$disabled ? 'display: none' : '', "\n");
};
function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}
var Option = function Option(_ref) {
  var option = _ref.option;
  if (_typeof(option) === 'object') {
    var value = option.value,
      _option$label = option.label,
      label = _option$label === void 0 ? value : _option$label,
      rest = __rest(option, ["value", "label"]);
    return /*#__PURE__*/React.createElement("option", Object.assign({
      value: value
    }, rest), label);
  } else if (typeof option === 'string' || typeof option === 'number') {
    return /*#__PURE__*/React.createElement("option", {
      value: option
    }, option);
  }
};
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$8(arr) || _nonIterableSpread();
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$8(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$8(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$8(o, minLen);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$8(arr);
}
function _arrayLikeToArray$8(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function Options(_ref) {
  var options = _ref.options,
    placeholder = _ref.placeholder,
    placeholderValue = _ref.placeholderValue;
  var combined = options;
  if (placeholder) {
    combined = [{
      label: placeholder,
      value: placeholderValue,
      disabled: true
    }].concat(_toConsumableArray(combined));
  }
  if (!combined && !combined.length) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, combined.map(function (option, key) {
    return /*#__PURE__*/React.createElement(Option, {
      option: option,
      key: key
    });
  }));
}
function _templateObject5$2() {
  var data = _taggedTemplateLiteral$k(["\n  ", "\n"]);
  _templateObject5$2 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$2() {
  var data = _taggedTemplateLiteral$k(["\n  ", "\n"]);
  _templateObject4$2 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$3() {
  var data = _taggedTemplateLiteral$k(["\n  ", "\n"]);
  _templateObject3$3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$b() {
  var data = _taggedTemplateLiteral$k(["\n  outline: none;\n"]);
  _templateObject2$b = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$k() {
  var data = _taggedTemplateLiteral$k(["\n  ", "\n"]);
  _templateObject$k = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$k(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledInputWrapper$1 = styled(Box)(_templateObject$k(), focusOutline({
  modifier: '&:focus-within',
  offset: '2px'
}));
var StyledInput$2 = styled(Box)(_templateObject2$b());
var SelectBox = /*#__PURE__*/React.forwardRef(function SelectBox(props, userRef) {
  var hasError = props.hasError,
    disabled = props.disabled,
    rest = __rest(props, ["hasError", "disabled"]);
  return /*#__PURE__*/React.createElement(StyledInputWrapper$1, null, /*#__PURE__*/React.createElement(StyledInput$2, Object.assign({
    "aria-invalid": !!hasError,
    as: "select",
    disabled: disabled,
    width: "100%",
    border: hasError ? "1px solid ".concat(tokens.color_red_700) : '400',
    borderRadius: "100",
    bg: disabled ? 'gray.200' : 'white',
    pl: "400",
    pr: "600",
    lineHeight: "2.5rem",
    height: "2.5rem",
    color: "gray.900"
  }, rest, {
    ref: userRef
  })));
});
var system$4 = compose(margin, maxWidth);
var StyledSelect = styled(SelectBox)(_templateObject3$3(), select);
var StyledChevron = styled(KeyboardArrowDown)(_templateObject4$2(), chevron);
var StyledWrapper$1 = styled('div')(_templateObject5$2(), system$4);
var Select = /*#__PURE__*/React.forwardRef(function Select(props, userRef) {
  var id = props.id,
    options = props.options,
    label = props.label,
    helpText = props.helpText,
    placeholder = props.placeholder,
    placeholderValue = props.placeholderValue,
    disabled = props.disabled,
    required = props.required,
    error = props.error,
    errorInLabel = props.errorInLabel,
    optional = props.optional,
    labelHidden = props.labelHidden,
    rest = __rest(props, ["id", "options", "label", "helpText", "placeholder", "placeholderValue", "disabled", "required", "error", "errorInLabel", "optional", "labelHidden"]);
  var systemProps = pick(rest, system$4.propNames);
  var componentProps = omit$1(rest);
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId;
  var labelMarkup = label && /*#__PURE__*/React.createElement(Label$1, {
    id: id,
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), error && errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    wrapper: "span"
  }, error), optional && /*#__PURE__*/React.createElement(OptionalLabel, {
    "float": true
  }));
  var helpMarkup = helpText ? /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId
  }, helpText) : null;
  return /*#__PURE__*/React.createElement(StyledWrapper$1, Object.assign({}, systemProps), labelMarkup, /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(StyledSelect, Object.assign({
    id: id,
    disabled: disabled,
    required: required,
    hasError: !!error
  }, componentProps, describedBy, {
    ref: userRef
  }), /*#__PURE__*/React.createElement(Options, {
    options: options,
    placeholder: placeholder,
    placeholderValue: placeholderValue
  })), /*#__PURE__*/React.createElement(StyledChevron, {
    size: 24,
    "$disabled": disabled
  })), helpMarkup, error && !errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId
  }, error));
});
Select.displayName = 'Select';
function _templateObject2$a() {
  var data = _taggedTemplateLiteral$j(["\n  border-radius: ", ";\n  position: relative;\n  overflow: hidden;\n  background: ", ";\n  opacity: 0.5;\n  animation: ", " infinite alternate ", " ", ";\n  will-change: opacity;\n"]);
  _templateObject2$a = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$j() {
  var data = _taggedTemplateLiteral$j(["\n  from { opacity: 0.5 }\n  to { opacity: 1; }\n"]);
  _templateObject$j = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$j(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Shimmer = keyframes(_templateObject$j());
var Animator = styled(Box)(_templateObject2$a(), tokens.borderRadius_200, tokens.color_gray_300, tokens.motionDuration_slow, Shimmer, tokens.motionEase_in_out);
var SkeletonHeader = /*#__PURE__*/React.forwardRef(function SkeletonHeader(props, ref) {
  var dataId = props['data-id'],
    _props$looksLike = props.looksLike,
    looksLike = _props$looksLike === void 0 ? 'h3' : _props$looksLike,
    _props$width = props.width,
    width = _props$width === void 0 ? '900' : _props$width;
  var delay = React.useMemo(function () {
    return "".concat(Math.random() / 2, "s");
  }, []);
  var size = React.useMemo(function () {
    switch (looksLike) {
      case 'h1':
        return '700';
      case 'h2':
        return '600';
      case 'h3':
        return '500';
      case 'h4':
        return '400';
      case 'h5':
        return '300';
      case 'h6':
        return '200';
    }
  }, [looksLike]);
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    tabIndex: -1,
    "aria-hidden": "true",
    "data-id": dataId
  }, /*#__PURE__*/React.createElement(Animator, {
    borderRadius: "200",
    height: tokens["lineHeight_".concat(size)],
    maxWidth: width,
    style: {
      animationDelay: delay
    }
  }));
});
SkeletonHeader.displayName = 'Skeleton.Header';
var SkeletonBody = /*#__PURE__*/React.forwardRef(function SkeletonBody(props, ref) {
  var dataId = props['data-id'],
    _props$lines = props.lines,
    lines = _props$lines === void 0 ? 3 : _props$lines;
  var body = React.useMemo(function () {
    var arr = [];
    for (var i = 0; i < lines; i++) {
      arr.push( /*#__PURE__*/React.createElement(Animator, {
        key: i,
        borderRadius: "200",
        style: {
          animationDelay: "".concat(i * 0.3, "s")
        },
        height: "300",
        width: i === lines - 1 ? '70%' : ''
      }));
    }
    return arr;
  }, [lines]);
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    mt: "100",
    tabIndex: -1,
    "aria-hidden": "true",
    "data-id": dataId
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "300"
  }, body));
});
SkeletonBody.displayName = 'Skeleton.Body';
var SkeletonBox = /*#__PURE__*/React.forwardRef(function SkeletonBox(props, ref) {
  var dataId = props['data-id'];
  props.children;
  var rest = __rest(props, ['data-id', "children"]);
  var delay = React.useMemo(function () {
    return "".concat(Math.random() / 2, "s");
  }, []);
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    tabIndex: -1,
    "aria-hidden": "true",
    "data-id": dataId
  }, /*#__PURE__*/React.createElement(Animator, Object.assign({}, rest, {
    style: {
      animationDelay: delay
    }
  })));
});
SkeletonBox.displayName = 'Skeleton.Box'; // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types

function Skeleton() {
  return null;
}
Skeleton.Header = SkeletonHeader;
Skeleton.Box = SkeletonBox;
Skeleton.Body = SkeletonBody;
var slider = function slider(props) {
  var cursor = 'pointer';
  if (props.$disabled) {
    cursor = 'not-allowed';
  }
  return "\n    padding: 1.25rem 0;\n    padding-bottom: ".concat(props.hasTicks ? '2rem' : '1.25rem', ";\n    position: relative;\n    cursor: ").concat(cursor, ";\n  ");
};
var rail = function rail() {
  return "\n  position: absolute;\n  left: ".concat(tokens.spacing_0, ";\n  right: ").concat(tokens.spacing_0, ";\n\n  height: ").concat(tokens.spacing_100, ";\n  background: ").concat(tokens.color_gray_300, ";\n  border-radius: ").concat(tokens.borderRadius_200, ";\n  transition: ").concat(tokens.motionDuration_medium, " background;\n");
};
var railHover = function railHover(props) {
  return "\n  background: ".concat(props.$disabled ? tokens.color_gray_300 : tokens.color_gray_400, ";\n");
};
var track = function track(props) {
  var background = tokens.color_blue_700;
  if (props.$disabled) {
    background = tokens.color_gray_600;
  }
  return "\n    position: absolute;\n    left: ".concat(tokens.spacing_0, ";\n    height: ").concat(tokens.spacing_100, ";\n    background: ").concat(background, ";\n    border-radius: ").concat(tokens.borderRadius_200, ";\n  ");
};
var tick = function tick(props) {
  var background = tokens.color_gray_300;
  if (props.$disabled && props.$included) {
    background = tokens.color_gray_600;
  }
  if (!props.$disabled && props.$included) {
    background = tokens.color_blue_700;
  }
  return "\n    position: absolute;\n    height: ".concat(tokens.spacing_200, ";\n    width: ").concat(tokens.spacing_200, ";\n    border-radius: ").concat(tokens.borderRadius_circle, ";\n    transform: translate(-2px, -2px);\n\n    background: ").concat(background, ";\n    transition: ").concat(tokens.motionDuration_medium, " background;\n\n    user-select: none;\n    white-space: nowrap;\n  ");
};
var tickLabel = function tickLabel() {
  return "\n  position: absolute;\n  top: ".concat(tokens.spacing_400, ";\n\n  color: ").concat(tokens.color_gray_700, ";\n  font-size: ").concat(tokens.fontSize_100, ";\n");
};
var tickLabelPosition = function tickLabelPosition(_ref) {
  var $x = _ref.$x;
  if ($x === '0') {
    return "\n      left: 0;\n   ";
  }
  if ($x === '100') {
    return "\n      right: 0;\n   ";
  }
  return "\n    left: 50%;\n    transform: translate(-50%, 0);\n  ";
};
var sharedStyles = "\n  position: absolute;\n  width: ".concat(tokens.spacing_400, ";\n  height: ").concat(tokens.spacing_400, ";\n  border-radius: ").concat(tokens.borderRadius_circle, ";\n  outline: none;\n");
var handle = function handle(props) {
  var border = "3px solid ".concat(tokens.color_blue_700);
  if (props.$disabled) {
    border = "3px solid ".concat(tokens.color_gray_600);
  }
  return "\n    ".concat(sharedStyles, "\n    transform: translate(-7px, -5px);\n    border: ").concat(border, ";\n    background: ").concat(tokens.color_white, ";\n    &:focus > div, &:active > div, &:hover > div {\n      opacity: 0.3;\n      transition: ").concat(tokens.motionDuration_fast, " opacity;\n    }\n  ");
};
var handleShadow = function handleShadow(props) {
  var boxShadow = "none";
  if (!props.$disabled) {
    boxShadow = "0 0 0 5px ".concat(tokens.color_blue_700);
  }
  return "\n    ".concat(sharedStyles, "\n    transform: translate(-3px, -3px);\n    border: 3px solid transparent;\n    background: transparent;\n    box-shadow: ").concat(boxShadow, ";\n    transition: ").concat(tokens.motionDuration_fast, " opacity;\n    opacity: 0;\n  ");
};
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _slicedToArray$7(arr, i) {
  return _arrayWithHoles$7(arr) || _iterableToArrayLimit$7(arr, i) || _unsupportedIterableToArray$7(arr, i) || _nonIterableRest$7();
}
function _nonIterableRest$7() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$7(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$7(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$7(o, minLen);
}
function _arrayLikeToArray$7(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$7(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$7(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject7$1() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n"]);
  _templateObject7$1 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$1() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n"]);
  _templateObject6$1 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$1() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n  ", "\n"]);
  _templateObject5$1 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$1() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n"]);
  _templateObject4$1 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$2() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n"]);
  _templateObject3$2 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$9() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n  ", ":hover & {\n    ", "\n  }\n"]);
  _templateObject2$9 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$i() {
  var data = _taggedTemplateLiteral$i(["\n  ", "\n  ", "\n"]);
  _templateObject$i = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$i(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$3 = compose(margin);
var StyledSlider = styled('div')(_templateObject$i(), slider, system$3);
var StyledRail = styled('div')(_templateObject2$9(), rail, StyledSlider, railHover);
var StyledTrack = styled('div')(_templateObject3$2(), track);
var StyledTick = styled('div')(_templateObject4$1(), tick);
var StyledTickLabel = styled('div')(_templateObject5$1(), tickLabel, tickLabelPosition);
var StyledHandle = styled('div')(_templateObject6$1(), handle);
var StyledHandleShadow = styled('div')(_templateObject7$1(), handleShadow);
function Slider(props) {
  var defaultValue = props.defaultValue,
    disabled = props.disabled,
    id = props.id,
    _props$max = props.max,
    max = _props$max === void 0 ? 100 : _props$max,
    _props$min = props.min,
    min = _props$min === void 0 ? 0 : _props$min,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    onChange = props.onChange,
    _props$precision = props.precision,
    precision = _props$precision === void 0 ? 0 : _props$precision,
    ticks = props.ticks,
    value = props.value,
    rest = __rest(props, ["defaultValue", "disabled", "id", "max", "min", "onBlur", "onFocus", "onChange", "precision", "ticks", "value"]);
  var systemProps = pick$1(rest);
  var environment = getWindow();
  var _useResizeObserver = useResizeObserver(),
    _useResizeObserver2 = _slicedToArray$7(_useResizeObserver, 2),
    resizeRef = _useResizeObserver2[0],
    contentRect = _useResizeObserver2[1].contentRect;
  var _React$useState = React.useState(value || defaultValue != null ? defaultValue : min),
    _React$useState2 = _slicedToArray$7(_React$useState, 2),
    sliderValue = _React$useState2[0],
    setSliderValue = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray$7(_React$useState3, 2),
    sliderLocation = _React$useState4[0],
    setSliderLocation = _React$useState4[1];
  var _React$useState5 = React.useState({
      width: 0,
      left: 0
    }),
    _React$useState6 = _slicedToArray$7(_React$useState5, 2),
    rect = _React$useState6[0],
    setRect = _React$useState6[1];
  var _React$useState7 = React.useState(''),
    _React$useState8 = _slicedToArray$7(_React$useState7, 2),
    moving = _React$useState8[0],
    setMoving = _React$useState8[1];
  var sliderRef = React.useRef();
  React.useEffect(function () {
    setRect(getRectFor(sliderRef.current));
  }, [contentRect, sliderRef.current]); // Calculates step increments based on precision

  var interval = React.useMemo(function () {
    var interval = 1;
    if (precision > 0) {
      var zeros = '0'.repeat(precision - 1);
      interval = parseFloat("0.".concat(zeros, "1"));
    }
    return interval;
  }, [precision]); // Sets internal value when value is controlled externally

  React.useEffect(function () {
    if (!isNaN(value) && isFinite(value)) {
      setValue(value);
    }
  }, [value]); // Updates slider location when value changes

  React.useEffect(function () {
    if (rect.width) {
      var absoluteProportion = (sliderValue - min) / Math.abs(min - max);
      setSliderLocation(lerp(0, rect.width, absoluteProportion));
      if (onChange) {
        onChange(sliderValue);
      }
    }
  }, [sliderValue, rect.width]); // Updates tick locations when ticks or component size change

  var tickLocations = React.useMemo(function () {
    if (!ticks || !rect.width) {
      return {};
    }
    return Object.keys(ticks).reduce(function (acc, number) {
      var absoluteProportion = (Number(number) - min) / Math.abs(min - max);
      return Object.assign(Object.assign({}, acc), _defineProperty({}, number, {
        position: lerp(0, rect.width, absoluteProportion),
        label: ticks[number]
      }));
    }, {});
  }, [ticks, rect.width]); // Event handlers

  function handleMouseDown(e) {
    if (e.button !== 0) {
      return;
    }
    var mousePosition = e.pageX;
    setPositions(mousePosition);
    setMoving('mouse');
  }
  function handleMouseMove(e) {
    var mousePosition = e.pageX;
    setPositions(mousePosition);
  }
  function handleTouchStart(e) {
    if (isNotTouchEvent(e)) {
      return;
    }
    var position = e.touches[0].pageX;
    setPositions(position);
    setMoving('touch');
  }
  function handleTouchMove(e) {
    if (isNotTouchEvent(e)) {
      return;
    }
    var position = e.touches[0].pageX;
    setPositions(position);
  }
  function handleEnd() {
    setMoving(null);
  }
  function handleKeyDown(e) {
    // preventDefault here prevents the parent window
    // from scrolling when interacting with the slider
    onKeys(['arrowLeft', 'arrowDown'], function () {
      e.preventDefault();
      setValue(sliderValue - interval);
    })(e);
    onKeys(['arrowRight', 'arrowUp'], function () {
      e.preventDefault();
      setValue(sliderValue + interval);
    })(e);
    onKey('home', function () {
      e.preventDefault();
      setValue(min);
    })(e);
    onKey('end', function () {
      e.preventDefault();
      setValue(max);
    })(e);
  } // Sets slider value based on an x position

  function setPositions(xPosition) {
    var clampedPixelOffset = clamp(xPosition - rect.left, 0, rect.width);
    var percentOffset = clampedPixelOffset / rect.width;
    setValue(lerp(min, max, percentOffset));
  } // Sets value to a provided value

  function setValue(newValue) {
    var clampedValue = clamp(newValue, min, max);
    setSliderValue(roundToPlaces(clampedValue, precision));
  } // Binding of mouse/touch drag events

  React.useEffect(function () {
    if (moving === 'mouse') {
      environment.addEventListener('mousemove', handleMouseMove);
      environment.addEventListener('mouseup', handleEnd);
    }
    if (moving === 'touch') {
      environment.addEventListener('touchmove', handleTouchMove);
      environment.addEventListener('touchend', handleEnd);
    }
    return function () {
      if (moving === 'mouse') {
        environment.removeEventListener('mousemove', handleMouseMove);
        environment.removeEventListener('mouseup', handleEnd);
      }
      if (moving === 'touch') {
        environment.removeEventListener('touchmove', handleTouchMove);
        environment.removeEventListener('touchend', handleEnd);
      }
    };
  }, [moving]);
  var tickMarkup = Object.keys(tickLocations).map(function (tick) {
    var _tickLocations$tick = tickLocations[tick],
      label = _tickLocations$tick.label,
      position = _tickLocations$tick.position;
    return /*#__PURE__*/React.createElement(StyledTick, {
      key: tick,
      style: {
        left: position
      },
      "$disabled": disabled,
      "$included": Number(tick) < sliderValue
    }, /*#__PURE__*/React.createElement(StyledTickLabel, {
      "$x": tick
    }, label));
  });
  return /*#__PURE__*/React.createElement(StyledSlider, Object.assign({
    hasTicks: ticks,
    "$disabled": disabled,
    "data-id": "slider-wrapper",
    onTouchStart: disabled ? noop : handleTouchStart,
    onMouseDown: disabled ? noop : handleMouseDown,
    ref: combineRefs(sliderRef, resizeRef)
  }, systemProps), /*#__PURE__*/React.createElement(StyledRail, {
    "$disabled": disabled
  }), tickMarkup, /*#__PURE__*/React.createElement(StyledTrack, {
    "$disabled": disabled,
    style: {
      width: sliderLocation
    }
  }), /*#__PURE__*/React.createElement(StyledHandle, {
    id: id,
    "aria-controls": props['aria-controls'],
    "aria-labelledby": props['aria-labelledby'],
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": sliderValue,
    "aria-disabled": disabled,
    "data-id": props['data-id'],
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyDown: disabled ? noop : handleKeyDown,
    role: "slider",
    "$disabled": disabled,
    style: {
      left: sliderLocation
    },
    tabIndex: 0
  }, /*#__PURE__*/React.createElement(StyledHandleShadow, {
    "$disabled": disabled
  })));
}
Slider.displayName = 'Slider';
var base = function base() {
  return "\n  display: inline-flex;\n  align-items: flex-start;\n  color: ".concat(tokens.color_white, ";\n");
};
var status = function status(props) {
  function makeLinkColorStyles(color) {
    return "\n      a, \n      a:hover, \n      a:visited { \n        color: ".concat(color, " !important; \n      }\n    ");
  }
  var whiteLinks = makeLinkColorStyles(tokens.color_white);
  var greyLinks = makeLinkColorStyles(tokens.color_gray_900);
  switch (props.$status) {
    case 'success':
      return "\n        ".concat(whiteLinks, " \n        background: ").concat(tokens.color_green_800, ";\n      ");
    case 'danger':
    case 'error':
      return "\n        ".concat(whiteLinks, " background: ").concat(tokens.color_red_700, ";\n      ");
    case 'warning':
      return "\n        ".concat(greyLinks, " \n        background: ").concat(tokens.color_yellow_300, ";\n        color: ").concat(tokens.color_gray_900, ";\n      ");
    case 'default':
    default:
      return "\n        ".concat(whiteLinks, " background: ").concat(tokens.color_blue_800, ";\n      ");
  }
};
var dismiss = function dismiss() {
  return "\n  display: inline-flex;\n  transition: background ".concat(tokens.motionDuration_fast, ";\n  &:hover {\n    cursor: pointer;\n  }\n");
};
var dismissStatus = function dismissStatus(props) {
  var color;
  switch (props.$status) {
    case 'success':
      color = tokens.color_green_900;
      break;
    case 'danger':
    case 'error':
      color = tokens.color_red_800;
      break;
    case 'warning':
      color = tokens.color_yellow_400;
      break;
    case 'default':
    default:
      color = tokens.color_blue_900;
      break;
  }
  return "\n    &:hover {\n      background: ".concat(color, ";\n    }\n  ");
};
function _templateObject2$8() {
  var data = _taggedTemplateLiteral$h(["\n  ", "\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject2$8 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$h() {
  var data = _taggedTemplateLiteral$h(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$h = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$h(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledBox = styled(Box)(_templateObject$h(), base, status, margin);
var StyledClose = styled.button(_templateObject2$8(), buttonReset, css$1({
  p: '0.125rem',
  mx: '100'
}), dismiss, dismissStatus);
var Snackbar = /*#__PURE__*/React.forwardRef(function Snackbar(props, userRef) {
  var children = props.children,
    _props$status = props.status,
    status = _props$status === void 0 ? 'default' : _props$status,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? 380 : _props$maxWidth,
    onDismiss = props.onDismiss,
    dataId = props['data-id'],
    rest = __rest(props, ["children", "status", "maxWidth", "onDismiss", 'data-id']);
  var systemProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(StyledBox, Object.assign({
    "$status": status,
    role: "alert",
    p: "300",
    borderRadius: "100",
    ref: userRef,
    tabIndex: -1
  }, systemProps, {
    "data-id": dataId
  }), /*#__PURE__*/React.createElement(Box, {
    mr: "300"
  }, status === 'default' && /*#__PURE__*/React.createElement(Info, {
    size: "20",
    label: "Info"
  }), status === 'success' && /*#__PURE__*/React.createElement(CheckCircle, {
    size: "20",
    label: "Success"
  }), status === 'warning' && /*#__PURE__*/React.createElement(Warning, {
    size: "20",
    label: "Warning"
  }), (status === 'error' || status === 'danger') && /*#__PURE__*/React.createElement(ErrorIcon, {
    size: "20",
    label: "Error"
  })), /*#__PURE__*/React.createElement(Box, {
    fontSize: "200",
    lineHeight: "200",
    py: "100",
    pr: "400",
    maxWidth: maxWidth,
    minWidth: "12.5rem"
  }, children), /*#__PURE__*/React.createElement(StyledClose, {
    "$status": status,
    onClick: onDismiss,
    type: "button"
  }, /*#__PURE__*/React.createElement(Close, {
    size: 24
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Close")));
});
Snackbar.displayName = 'Snackbar';
var table = function table() {
  return "\n  position: relative;\n  text-align: left;\n  width: 100%;\n  padding: 0;\n  border-collapse: collapse;\n";
};
var headerCell = function headerCell(_ref) {
  var theme = _ref.theme;
  return "\n  font-size: ".concat(theme.fontSizes['200'], ";\n  line-height: ").concat(theme.lineHeights['300'], ";\n  font-weight: ").concat(theme.fontWeights.semibold, ";\n");
};
var sticky = function sticky(_ref2) {
  var isScrolled = _ref2.isScrolled,
    $freezeFirstColumn = _ref2.$freezeFirstColumn;
  return "\n    td:first-child, th:first-child {\n      ".concat($freezeFirstColumn ? "\n            position: sticky;\n            left: 0;\n            background: inherit;\n            z-index: 1;\n            &:after {\n              transition: box-shadow ".concat(tokens.motionDuration_medium, " ").concat(tokens.motionEase_in_out, ";\n              ").concat(isScrolled ? "box-shadow: 16px 0 10px -12px inset rgb(44 53 61 / 20%);" : '', "\n              content: \"\";\n              height: 100%;\n              position: absolute;\n              top: 0;\n              right: -15px;\n              width: 15px;\n            }\n          ") : '', "\n      \n    }\n  ");
};
var cell = function cell() {
  return "\n  word-break: break-all;\n";
};
var zebra = function zebra(theme) {
  return "\n  tbody &:nth-of-type(odd) {\n    background: ".concat(theme.colors.gray['100'], ";\n  }\n");
};
var row = function row(_ref3) {
  var theme = _ref3.theme;
  return "\n  background: ".concat(theme.colors.white, ";\n  border: none;\n  thead & {\n    border-bottom:").concat(theme.borders['300'], ";\n  }\n  ").concat(zebra(theme), "\n");
};
var totalsRow = function totalsRow(_ref4) {
  var theme = _ref4.theme;
  return "\n  &:not(:last-child) {\n    border-bottom: ".concat(theme.borders['500'], ";\n  }\n  &:not(:first-child) {\n    border-top: ").concat(theme.borders['500'], ";\n  }\n  td {\n    font-weight: ").concat(theme.fontWeights.semibold, ";\n  }\n  ").concat(zebra(theme), "\n");
};
var verticalAlignment = system$g({
  $alignY: {
    property: 'verticalAlign',
    defaultScale: {
      center: 'middle',
      top: 'top',
      bottom: 'bottom'
    }
  }
});
var horizontalAlignment = system$g({
  $align: {
    property: 'textAlign',
    defaultScale: {
      center: 'center',
      left: 'left',
      right: 'right'
    }
  }
});
var wrapper = function wrapper(_ref5) {
  var $freezeFirstColumn = _ref5.$freezeFirstColumn;
  return "\n   ".concat($freezeFirstColumn ? 'overflow: auto;' : '', "\n");
};
var TablePaddingContext = /*#__PURE__*/React.createContext({});
function _templateObject$g() {
  var data = _taggedTemplateLiteral$g(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$g = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$g(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var cellSystem = compose(padding, fontSize$1, width);
var StyledCell = styled(Box)(_templateObject$g(), cell, cellSystem, horizontalAlignment);
var Cell = /*#__PURE__*/React.forwardRef(function Cell(_a, userRef) {
  var align = _a.align,
    value = _a.value,
    children = _a.children,
    className = _a.className,
    colSpan = _a.colSpan,
    role = _a.role,
    rowSpan = _a.rowSpan,
    style = _a.style,
    width = _a.width,
    rest = __rest(_a, ["align", "value", "children", "className", "colSpan", "role", "rowSpan", "style", "width"]);
  var paddingContext = React.useContext(TablePaddingContext);
  var paddingProps = pick(rest, padding.propNames);
  return /*#__PURE__*/React.createElement(StyledCell, Object.assign({
    as: "td"
  }, paddingContext, paddingProps, {
    "$align": align,
    width: width,
    fontSize: ['200', null, '300'],
    className: className,
    colSpan: colSpan,
    ref: userRef,
    role: role,
    rowSpan: rowSpan,
    style: style
  }), value || children);
});
Cell.displayName = 'Table.Cell';
function _templateObject$f() {
  var data = _taggedTemplateLiteral$f(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$f = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$f(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var headerSystem = compose(padding, width);
var StyledHeaderCell = styled(Box)(_templateObject$f(), headerCell, headerSystem, horizontalAlignment);
var HeaderCell = /*#__PURE__*/React.forwardRef(function HeaderCell(_ref, userRef) {
  var align = _ref.align,
    ariaSort = _ref['aria-sort'],
    value = _ref.value,
    children = _ref.children,
    className = _ref.className,
    colSpan = _ref.colSpan,
    role = _ref.role,
    rowSpan = _ref.rowSpan,
    scope = _ref.scope,
    style = _ref.style,
    width = _ref.width;
  return /*#__PURE__*/React.createElement(StyledHeaderCell, {
    as: "th",
    p: "450",
    "$align": align,
    "aria-sort": ariaSort,
    width: width,
    className: className,
    colSpan: colSpan,
    ref: userRef,
    role: role,
    rowSpan: rowSpan,
    scope: scope,
    style: style
  }, value || children);
});
HeaderCell.displayName = 'Table.HeaderCell';
function _templateObject$e() {
  var data = _taggedTemplateLiteral$e(["\n  ", "\n  thead & th {\n    vertical-align: bottom;\n  }\n  td {\n    ", "\n  }\n"]);
  _templateObject$e = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$e(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledRow = styled.tr(_templateObject$e(), row, verticalAlignment);
var Row = /*#__PURE__*/React.forwardRef(function Row(_ref, userRef) {
  var _ref$alignY = _ref.alignY,
    alignY = _ref$alignY === void 0 ? 'center' : _ref$alignY,
    rowData = _ref.rowData,
    children = _ref.children,
    className = _ref.className,
    onClick = _ref.onClick;
  return /*#__PURE__*/React.createElement(StyledRow, {
    "$alignY": alignY,
    className: className,
    onClick: onClick,
    ref: userRef
  }, rowData ? rowData.map(function (value, i) {
    return /*#__PURE__*/React.createElement(Cell, {
      value: value,
      key: "Cell-".concat(i)
    });
  }) : children);
});
Row.displayName = 'Table.Row';
function _templateObject$d() {
  var data = _taggedTemplateLiteral$d(["\n  ", "\n"]);
  _templateObject$d = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$d(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledTotalsRow = styled.tr(_templateObject$d(), totalsRow);
var TotalsRow = /*#__PURE__*/React.forwardRef(function TotalsRow(_ref, userRef) {
  var rowData = _ref.rowData,
    children = _ref.children,
    className = _ref.className,
    onClick = _ref.onClick;
  return /*#__PURE__*/React.createElement(StyledTotalsRow, {
    className: className,
    ref: userRef,
    tabIndex: -1,
    onClick: onClick
  }, rowData ? rowData.map(function (value, i) {
    return /*#__PURE__*/React.createElement(Cell, {
      value: value,
      key: "Cell-".concat(i)
    });
  }) : children);
});
TotalsRow.displayName = 'Table.TotalsRow';
function _templateObject3$1() {
  var data = _taggedTemplateLiteral$c(["\n  display: inline-block;\n  position: relative;\n  height: 18px;\n  width: 18px;\n\n  svg {\n    position: absolute;\n  }\n\n  svg:first-child {\n    top: 0px;\n    left: 0px;\n  }\n\n  svg:last-child {\n    bottom: -4px;\n    right: 0px;\n  }\n"]);
  _templateObject3$1 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$7() {
  var data = _taggedTemplateLiteral$c(["\n  display: inline-block;\n  align-self: end;\n  margin-left: ", ";\n  color: ", ";\n  transition: ", ";\n\n  ", "\n\n  ", ":hover & {\n    color: ", ";\n  }\n"]);
  _templateObject2$7 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$c() {
  var data = _taggedTemplateLiteral$c(["\n  ", "\n  ", "\n  display: inline-flex;\n  text-align: left;\n  cursor: pointer;\n\n  ", "\n\n  ", "\n\n  &:hover {\n    ", "\n  }\n"]);
  _templateObject$c = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$c(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Button = styled.button(_templateObject$c(), buttonReset, focusOutline(), function (_ref) {
  var theme = _ref.theme;
  return "\n    padding: ".concat(theme.space[100], " ").concat(theme.space[200], ";\n    margin-left: -").concat(theme.space[200], ";\n    margin-right: -").concat(theme.space[200], ";\n    margin-top: -").concat(theme.space[100], ";\n    margin-bottom: -").concat(theme.space[100], ";\n    border: 1px solid transparent;\n    border-radius: ").concat(theme.radii[200], ";\n    transition: ").concat(theme.motion.duration.fast, ";\n  ");
}, function (_ref2) {
  var $sorted = _ref2.$sorted,
    theme = _ref2.theme;
  return $sorted ? "\n    background: ".concat(theme.colors.blue[200], ";\n    color: ").concat(theme.colors.blue[700], ";\n  ") : '';
}, function (_ref3) {
  var theme = _ref3.theme;
  return "\n      background: ".concat(theme.colors.blue[200], ";\n      color: ").concat(theme.colors.blue[700], ";\n      border: 1px solid ").concat(theme.colors.blue[700], ";\n    ");
});
var IconWrapper = styled.span(_templateObject2$7(), function (_ref4) {
  var theme = _ref4.theme;
  return theme.space[100];
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.colors.gray[700];
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.motion.duration.fast;
}, function (_ref7) {
  var $sorted = _ref7.$sorted,
    theme = _ref7.theme;
  return $sorted ? "color: ".concat(theme.colors.blue[700], ";") : '';
}, Button, function (_ref8) {
  var theme = _ref8.theme;
  return theme.colors.blue[700];
});
var UnsortedWrapper = styled.span(_templateObject3$1());
function Unsorted() {
  return /*#__PURE__*/React.createElement(UnsortedWrapper, null, /*#__PURE__*/React.createElement(ArrowUpward, {
    size: 14
  }), /*#__PURE__*/React.createElement(ArrowDownward, {
    size: 14
  }));
}
var SortButton = /*#__PURE__*/React.forwardRef(function SortButton(props, userRef) {
  var children = props.children,
    direction = props.direction,
    onClick = props.onClick,
    onFocus = props.onFocus,
    onBlur = props.onBlur;
  var Icon = React.useMemo(function () {
    if (direction === 'asc') {
      return ArrowUpward;
    }
    if (direction === 'desc') {
      return ArrowDownward;
    }
    return Unsorted;
  }, [direction]);
  var sorted = React.useMemo(function () {
    return ['asc', 'desc'].includes(direction);
  }, [direction]);
  return /*#__PURE__*/React.createElement(Button, {
    onClick: onClick,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: userRef,
    type: "button",
    "$sorted": sorted
  }, /*#__PURE__*/React.createElement("span", null, children), /*#__PURE__*/React.createElement(IconWrapper, {
    "$sorted": sorted
  }, /*#__PURE__*/React.createElement(Icon, {
    size: 16
  })));
});
SortButton.displayName = 'Table.SortButton';
function _slicedToArray$6(arr, i) {
  return _arrayWithHoles$6(arr) || _iterableToArrayLimit$6(arr, i) || _unsupportedIterableToArray$6(arr, i) || _nonIterableRest$6();
}
function _nonIterableRest$6() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$6(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$6(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$6(o, minLen);
}
function _arrayLikeToArray$6(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$6(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$6(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject2$6() {
  var data = _taggedTemplateLiteral$b(["\n  ", "\n  ", "\n"]);
  _templateObject2$6 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$b() {
  var data = _taggedTemplateLiteral$b(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject$b = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$b(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledTable = styled('table')(_templateObject$b(), table, padding, sticky);
var Wrapper = styled(Box)(_templateObject2$6(), wrapper, margin);
function Table(props) {
  var readOnly = props['aria-readonly'],
    children = props.children,
    data = props.data,
    dataId = props['data-id'],
    freezeFirstColumn = props.freezeFirstColumn,
    id = props.id,
    role = props.role,
    title = props.title,
    rest = __rest(props, ['aria-readonly', "children", "data", 'data-id', "freezeFirstColumn", "id", "role", "title"]);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray$6(_React$useState, 2),
    isScrolled = _React$useState2[0],
    setIsScrolled = _React$useState2[1];
  var handleScroll = React.useCallback(function (e) {
    setIsScrolled(e.target.scrollLeft > 10);
  }, [freezeFirstColumn]);
  var dataMarkup = data ? /*#__PURE__*/React.createElement("tbody", null, data.map(function (rowData, i) {
    return /*#__PURE__*/React.createElement(Row, {
      rowData: rowData,
      key: "Row-".concat(i)
    });
  })) : children;
  var _a = pick(rest, padding.propNames),
    _a$px = _a.px,
    px = _a$px === void 0 ? '450' : _a$px,
    _a$py = _a.py,
    py = _a$py === void 0 ? '400' : _a$py,
    paddingProps = __rest(_a, ["px", "py"]);
  var marginProps = pick(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(Wrapper, Object.assign({
    "$freezeFirstColumn": freezeFirstColumn,
    onScroll: freezeFirstColumn ? handleScroll : null
  }, marginProps), /*#__PURE__*/React.createElement(StyledTable, {
    "aria-readonly": readOnly,
    "data-id": dataId,
    "$freezeFirstColumn": freezeFirstColumn,
    id: id,
    isScrolled: isScrolled,
    role: role
  }, /*#__PURE__*/React.createElement(TablePaddingContext.Provider, {
    value: Object.assign({
      px: px,
      py: py
    }, paddingProps)
  }, title && /*#__PURE__*/React.createElement("caption", null, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, title)), dataMarkup)));
}
Table.Cell = Cell;
Table.HeaderCell = HeaderCell;
Table.Row = Row;
Table.TotalsRow = TotalsRow;
Table.SortButton = SortButton;
Table.displayName = 'Table';
var tabStyles = function tabStyles(_ref) {
  var $selected = _ref.$selected,
    $fitted = _ref.$fitted;
  return "\n  ".concat(buttonReset, "\n  \n  ").concat(focusOutline({
    offset: '0px',
    modifier: '&'
  }), "\n  &:focus:not(:focus-visible):after {\n    box-shadow: none;\n  }\n  ").concat(focusOutline({
    offset: '0px',
    modifier: '&:focus-visible'
  }), "\n\n  display: inline-block;\n  cursor: pointer;\n  position: relative;\n  flex: ").concat($fitted ? '1' : '0', ";\n  text-decoration: none;\n  outline: none;\n  text-align: center;\n\n  padding: 0 ").concat(tokens.spacing_400, ";\n  margin-left: ").concat($fitted ? '0' : tokens.sizing_200, ";\n\n  &:first-child {\n    margin-left: 0;\n  }\n\n  font-size: ").concat(tokens.fontSize_300, ";\n  font-weight: ").concat(tokens.fontWeight_medium, ";\n  line-height: ").concat(tokens.sizing_650, ";\n  white-space: nowrap;\n\n  background: ").concat($selected ? tokens.color_blue_200 : 'transparent', ";\n  transition: ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n\n  &, &:visited {\n    color: ").concat($selected ? tokens.color_blue_700 : tokens.color_gray_700, ";\n  }\n\n  &:before {\n    display: block;\n    position: absolute;\n    content: '';\n    bottom: 0px;\n    left: 0;\n    right: 0;\n  }\n\n  &:before {\n    height: 1px;\n    background: ").concat($selected ? tokens.color_blue_700 : 'transparent', ";\n    transition: ").concat(tokens.motionDuration_fast, " ").concat(tokens.motionEase_in_out, ";\n  }\n  \n  &:hover {\n    ").concat(!$selected ? "\n      color: ".concat(tokens.color_gray_900, ";\n      background: ").concat(tokens.color_gray_200, ";\n    ") : '', ";\n    &:before {\n      ").concat(!$selected ? "background: ".concat(tokens.color_gray_700, ";") : '', "\n    }\n  }\n  \n  &:after {\n    top: 2px;\n  }\n");
};
var overflowTabs = function overflowTabs(_ref2) {
  var isOverflowing = _ref2.isOverflowing;
  return "\n  position: absolute;\n  left: 0;\n  top: 0;\n  right:0;\n  display: flex;\n  visibility: ".concat(isOverflowing ? 'hidden' : 'visible', ";\n  pointer-events: ").concat(isOverflowing ? 'none' : 'auto', ";\n  overflow: ").concat(isOverflowing ? 'hidden' : 'unset', ";\n");
};
var containerStyles = function containerStyles() {
  return "\n  position: relative;\n  height: ".concat(tokens.sizing_650, ";\n");
};
function _templateObject$a() {
  var data = _taggedTemplateLiteral$a(["\n  ", "\n"]);
  _templateObject$a = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$a(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
// See: https://github.com/styled-components/styled-components/commit/e02109e626ed117b76f220d0b9b926129655262d
// Or when UnstyledLink is updated to use system props

var LinkWrapper = /*#__PURE__*/React.forwardRef(function LinkWrapper(props, ref) {
  props.selected;
  props.fitted;
  var rest = __rest(props, ["selected", "fitted"]);
  return /*#__PURE__*/React.createElement(UnstyledLink, Object.assign({
    ref: ref
  }, rest));
});
var StyledTab = styled(LinkWrapper)(_templateObject$a(), tabStyles);
var Tab$1 = /*#__PURE__*/React.forwardRef(function Tab(props, ref) {
  var as = props.as,
    index = props.index,
    content = props.content,
    selected = props.selected,
    fitted = props.fitted,
    component = props.component,
    Component = props.Component,
    tabIndex = props.tabIndex,
    type = props.type,
    rest = __rest(props, ["as", "index", "content", "selected", "fitted", "component", "Component", "tabIndex", "type"]);
  function handleClick(event) {
    var index = props.index,
      onClick = props.onClick;
    onClick(event, index);
  } // Buttons ensure focusability
  // Links will be focusable with an href

  var wrapper = as || component || Component || 'button';
  return /*#__PURE__*/React.createElement(StyledTab, Object.assign({
    "aria-selected": selected === index,
    as: wrapper,
    "$selected": selected === index,
    "$fitted": fitted,
    ref: ref
  }, rest, {
    onClick: handleClick,
    role: "tab",
    tabIndex: tabIndex,
    // Safari overwrites the button reset styles
    // if the element is an 'a' with this attribute
    type: wrapper === 'button' ? 'button' : type
  }), content);
});
Tab$1.displayName = 'Tab';
function _slicedToArray$5(arr, i) {
  return _arrayWithHoles$5(arr) || _iterableToArrayLimit$5(arr, i) || _unsupportedIterableToArray$5(arr, i) || _nonIterableRest$5();
}
function _nonIterableRest$5() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$5(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$5(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen);
}
function _arrayLikeToArray$5(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$5(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$5(arr) {
  if (Array.isArray(arr)) return arr;
}
function useTabConstructor$1(_ref) {
  var tabs = _ref.tabs,
    fitted = _ref.fitted,
    _ref$selected = _ref.selected,
    selected = _ref$selected === void 0 ? 0 : _ref$selected,
    handleClick = _ref.handleClick,
    onSelect = _ref.onSelect,
    keyboardActivation = _ref.keyboardActivation,
    disableResponsiveBehavior = _ref.disableResponsiveBehavior;
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray$5(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1];
  var focusContainerRef = React.useRef(null);
  var tabRefs = React.useRef({
    current: new Array(tabs.length)
  }); // Handles keydown events on the tab focus container

  var onFocusContainerKeyDown = function onFocusContainerKeyDown(e) {
    var isWithin = focusContainerRef.current && focusContainerRef.current.contains(e.currentTarget);
    if (!isWithin) {
      return;
    }
    onKey('arrowRight', function () {
      e.preventDefault();
      if (focused === tabs.length - 1) {
        setFocused(0);
      } else {
        setFocused(focused + 1);
      }
    })(e);
    onKey('arrowLeft', function () {
      e.preventDefault();
      if (focused === 0) {
        setFocused(tabs.length - 1);
      } else {
        setFocused(focused - 1);
      }
    })(e);
    onKeys(['home', 'pageDown'], function () {
      e.preventDefault();
      setFocused(0);
    })(e);
    onKeys(['end', 'pageUp'], function () {
      e.preventDefault();
      setFocused(tabs.length - 1);
    })(e);
  };
  function onFocusContainerBlur(e) {
    if (focusContainerRef.current && !focusContainerRef.current.contains(e.relatedTarget)) {
      setFocused(selected);
    }
  } // Preps array of tab item refs

  React.useEffect(function () {
    tabRefs.current = new Array(tabs.length);
    return function () {
      tabRefs.current = [];
    };
  }, [tabs]); // Focuses on tab items when focused index changes
  // Optionally calls onSelect

  React.useLayoutEffect(function () {
    // Does not continue when focus is reset, when moving focus outside the container
    if (focusContainerRef.current && !focusContainerRef.current.contains(document.activeElement)) {
      return;
    }
    if (tabRefs.current[focused]) {
      tabRefs.current[focused].focus();
      if (keyboardActivation === 'auto' && onSelect) {
        onSelect(focused);
      }
    }
  }, [focused]); // Constructs Tabs with refs

  var tabMarkup = tabs.map(function (tab, i) {
    return /*#__PURE__*/React.createElement(Tab$1, Object.assign({
      ref: function ref(n) {
        return tabRefs.current[i] = n;
      },
      key: i,
      index: i,
      fitted: fitted,
      selected: selected
    }, tab, {
      onClick: handleClick,
      tabIndex: focused === i ? 0 : -1
    }));
  }); // Constructs ActionList actions from tabs

  var tabActions = React.useMemo(function () {
    if (disableResponsiveBehavior) {
      return;
    }
    return tabs.map(function (tab, i) {
      var selected = tab.selected,
        rest = __rest(tab, ["selected"]);
      return Object.assign(Object.assign({
        is: 'button'
      }, rest), {
        onClick: function onClick(e) {
          return handleClick(e, i);
        },
        visible: i !== selected
      });
    });
  }, [selected, tabs]);
  return {
    tabMarkup: tabMarkup,
    tabActions: tabActions,
    focusContainerProps: {
      onBlur: onFocusContainerBlur,
      onKeyDown: onFocusContainerKeyDown,
      ref: focusContainerRef
    }
  };
}
function _slicedToArray$4(arr, i) {
  return _arrayWithHoles$4(arr) || _iterableToArrayLimit$4(arr, i) || _unsupportedIterableToArray$4(arr, i) || _nonIterableRest$4();
}
function _nonIterableRest$4() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$4(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$4(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen);
}
function _arrayLikeToArray$4(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$4(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$4(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject2$5() {
  var data = _taggedTemplateLiteral$9(["\n  ", "\n  ", "\n  ", "\n"]);
  _templateObject2$5 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$9() {
  var data = _taggedTemplateLiteral$9(["\n  ", "\n"]);
  _templateObject$9 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$9(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var OverflowTabContainer = styled('div')(_templateObject$9(), overflowTabs);
var Container$2 = styled('div')(_templateObject2$5(), margin, borderBottom, containerStyles);
var Tabs = /*#__PURE__*/React.forwardRef(function Tabs(props, userRef) {
  var disableResponsiveBehavior = props.disableResponsiveBehavior,
    _props$keyboardActiva = props.keyboardActivation,
    keyboardActivation = _props$keyboardActiva === void 0 ? 'auto' : _props$keyboardActiva,
    tabs = props.tabs,
    selected = props.selected,
    onSelect = props.onSelect,
    fitted = props.fitted,
    rest = __rest(props, ["disableResponsiveBehavior", "keyboardActivation", "tabs", "selected", "onSelect", "fitted"]);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray$4(_React$useState, 2),
    isOverflowing = _React$useState2[0],
    setIsOverflowing = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray$4(_React$useState3, 2),
    popoverOpen = _React$useState4[0],
    setPopoverOpen = _React$useState4[1];
  var wrapperRef = React.useRef();
  var overflowRef = React.useRef();
  var _useResizeObserver = useResizeObserver(),
    _useResizeObserver2 = _slicedToArray$4(_useResizeObserver, 2),
    resizeRef = _useResizeObserver2[0],
    contentRect = _useResizeObserver2[1].contentRect;
  function handleClick(event, index) {
    var onClick = tabs[index].onClick;
    if (onClick) {
      onClick(event, index);
    }
    if (onSelect && selected !== index) {
      onSelect(index);
    }
    if (isOverflowing) {
      setPopoverOpen(false);
    }
  }
  React.useLayoutEffect(function () {
    if (!disableResponsiveBehavior) {
      var wrapperPosition = getPositionFor(wrapperRef.current);
      var overflowPosition = getPositionFor(overflowRef.current); // Compares tab container width with its position relative to a measurement pixel

      if (wrapperPosition.width < overflowPosition.left - wrapperPosition.left) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }
  }, [wrapperRef, overflowRef, contentRect, tabs]); // Constructs the tabs, their props and handles tab keyboard navigation

  var _useTabConstructor = useTabConstructor$1({
      tabs: tabs,
      fitted: fitted,
      handleClick: handleClick,
      selected: selected,
      onSelect: onSelect,
      keyboardActivation: keyboardActivation,
      disableResponsiveBehavior: disableResponsiveBehavior
    }),
    tabMarkup = _useTabConstructor.tabMarkup,
    tabActions = _useTabConstructor.tabActions,
    focusContainerProps = _useTabConstructor.focusContainerProps;
  var assignWrapperRefs = function assignWrapperRefs(node) {
    if (wrapperRef) {
      wrapperRef.current = node;
    }
    if (userRef) {
      userRef.current = node;
    }
    if (resizeRef) {
      resizeRef(node);
    }
  };
  return /*#__PURE__*/React.createElement(Container$2, Object.assign({
    borderBottom: "400"
  }, pick$1(rest), {
    ref: assignWrapperRefs,
    tabIndex: "-1"
  }), /*#__PURE__*/React.createElement(Box, {
    "aria-hidden": isOverflowing
  }, /*#__PURE__*/React.createElement(OverflowTabContainer, {
    "aria-orientation": "horizontal",
    isOverflowing: isOverflowing,
    role: "tablist"
  }, /*#__PURE__*/React.createElement(Box, Object.assign({
    position: "absolute",
    left: "0",
    top: "0",
    right: "0",
    display: "flex"
  }, focusContainerProps), tabMarkup, /*#__PURE__*/React.createElement(Box, {
    display: "inline-block",
    width: "1px",
    height: "1px",
    ref: overflowRef
  })))), isOverflowing && !disableResponsiveBehavior && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "0"
  }, /*#__PURE__*/React.createElement(Tab$1, Object.assign({
    index: selected,
    selected: selected
  }, tabs[selected]))), /*#__PURE__*/React.createElement(Box, {
    flex: "0"
  }, /*#__PURE__*/React.createElement(Popover, {
    id: "tab-options",
    left: true,
    bottom: true,
    open: popoverOpen,
    onClose: function onClose() {
      return setPopoverOpen(false);
    },
    trigger: /*#__PURE__*/React.createElement(Button$1, {
      "aria-controls": "tab-options",
      "aria-expanded": popoverOpen,
      "data-id": "tab-options-button",
      variant: "text",
      onClick: function onClick() {
        return setPopoverOpen(!popoverOpen);
      }
    }, /*#__PURE__*/React.createElement(MoreHoriz, {
      size: 20
    }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "More Options"))
  }, /*#__PURE__*/React.createElement(ActionList, null, tabActions.map(function (action, i) {
    return /*#__PURE__*/React.createElement(ActionList.Action, Object.assign({
      key: i
    }, action));
  }))))));
});
Tabs.displayName = 'Tabs';
function _templateObject$8() {
  var data = _taggedTemplateLiteral$8(["\n  z-index: ", ";\n  &:focus-within {\n    position: relative;\n    z-index: ", ";\n  }\n"]);
  _templateObject$8 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$8(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
// above side elements until it side elements are focused

var FocusHandler = styled(Box)(_templateObject$8(), function (props) {
  return tokens.zIndex_default + props.$zIncrement;
}, tokens.zIndex_above);
function Connect(props) {
  var left = props.left,
    right = props.right,
    children = props.children;
  var leftMarkup = left ? /*#__PURE__*/React.createElement(FocusHandler, {
    flex: "0 0 auto",
    mr: "-1px",
    "$zIncrement": 0
  }, left) : null;
  var rightMarkup = right ? /*#__PURE__*/React.createElement(FocusHandler, {
    flex: "0 0 auto",
    ml: "-1px",
    "$zIncrement": 0
  }, right) : null;
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, leftMarkup, /*#__PURE__*/React.createElement(FocusHandler, {
    flex: "1",
    "$zIncrement": 1
  }, children), rightMarkup);
}
Connect.displayName = 'Connect';
function _slicedToArray$3(arr, i) {
  return _arrayWithHoles$3(arr) || _iterableToArrayLimit$3(arr, i) || _unsupportedIterableToArray$3(arr, i) || _nonIterableRest$3();
}
function _nonIterableRest$3() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$3(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$3(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen);
}
function _arrayLikeToArray$3(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$3(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$3(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject7() {
  var data = _taggedTemplateLiteral$7(["\n  color: ", ";\n  display: block;\n"]);
  _templateObject7 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6() {
  var data = _taggedTemplateLiteral$7(["\n  color: ", ";\n  display: block;\n"]);
  _templateObject6 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5() {
  var data = _taggedTemplateLiteral$7(["\n  padding: 0 6px;\n  position: absolute;\n  right: 0;\n  top: 0;\n  height: 100%;\n  border-left: ", ";\n  display: flex;\n  flex-direction: column;\n"]);
  _templateObject5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4() {
  var data = _taggedTemplateLiteral$7(["\n  ", "\n  cursor: pointer;\n"]);
  _templateObject4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3() {
  var data = _taggedTemplateLiteral$7(["\n  outline: none;\n  &[type='number'],\n  &[type='number']::-webkit-inner-spin-button,\n  &[type='number']::-webkit-outer-spin-button {\n    appearance: none;\n    margin: 0;\n  }\n"]);
  _templateObject3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$4() {
  var data = _taggedTemplateLiteral$7(["\n  ", "\n"]);
  _templateObject2$4 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$7() {
  var data = _taggedTemplateLiteral$7(["\n  ", "\n"]);
  _templateObject$7 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$7(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$2 = compose(margin, maxWidth);
var StyledWrapper = styled('div')(_templateObject$7(), system$2);
var StyledInputWrapper = styled(Box)(_templateObject2$4(), focusOutline({
  modifier: '&:focus-within',
  offset: '2px'
}));
var StyledInput$1 = styled(Box)(_templateObject3());
var StyledButton = styled.button(_templateObject4(), buttonReset);
var StyledNumberControls = styled(Box)(_templateObject5(), function (props) {
  return props.theme.borders['400'];
});
var StyledArrowUp = styled(KeyboardArrowUp)(_templateObject6(), function (props) {
  return props.theme.colors.blue['700'];
});
var StyledArrowDown = styled(KeyboardArrowDown)(_templateObject7(), function (props) {
  return props.theme.colors.blue['700'];
});
var FieldBox = /*#__PURE__*/React.forwardRef(function FieldBox(props, userRef) {
  var hasError = props.hasError,
    disabled = props.disabled,
    height = props.height,
    type = props.type,
    lineHeight = props.lineHeight,
    required = props.required,
    py = props.py,
    onChange = props.onChange,
    rest = __rest(props, ["hasError", "disabled", "height", "type", "lineHeight", "required", "py", "onChange"]);
  var inputRef = useRef(null);
  var increment = function increment() {
    inputRef.current.stepUp();
    onChange({
      target: inputRef.current
    });
  };
  var decrement = function decrement() {
    inputRef.current.stepDown();
    onChange({
      target: inputRef.current
    });
  };
  return /*#__PURE__*/React.createElement(StyledInputWrapper, null, /*#__PURE__*/React.createElement(StyledInput$1, Object.assign({
    "aria-invalid": !!hasError,
    as: "input",
    px: "400",
    onChange: onChange
  }, rest, {
    type: type,
    disabled: disabled,
    width: "100%",
    border: hasError ? "1px solid ".concat(tokens.color_red_700) : '400',
    borderRadius: "100",
    bg: disabled ? 'gray.200' : 'white',
    color: "gray.900",
    height: height,
    lineHeight: lineHeight,
    py: py,
    required: required,
    ref: combineRefs(inputRef, userRef)
  })), type == 'number' && !disabled && /*#__PURE__*/React.createElement(StyledNumberControls, {
    "$disabled": disabled
  }, /*#__PURE__*/React.createElement(StyledButton, {
    onClick: increment,
    as: "button",
    type: "button",
    "aria-hidden": true,
    disabled: disabled,
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(StyledArrowUp, {
    size: 20
  })), /*#__PURE__*/React.createElement(StyledButton, {
    onClick: decrement,
    as: "button",
    type: "button",
    "aria-hidden": true,
    disabled: disabled,
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(StyledArrowDown, {
    size: 20
  }))));
});
var PrefixOrSuffix = /*#__PURE__*/React.forwardRef(function PrefixOrSuffix(props, ref) {
  var content = props.content,
    className = props.className,
    rest = __rest(props, ["content", "className"]);
  if (!content) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    as: "span",
    position: "absolute",
    zIndex: "default",
    top: "0",
    lineHeight: "2.5rem",
    height: "2.5rem",
    className: className,
    ref: ref
  }, rest), content);
});
var TextField = /*#__PURE__*/React.forwardRef(function TextField(props, userRef) {
  var align = props.align,
    autoFocus = props.autoFocus,
    connectLeft = props.connectLeft,
    connectRight = props.connectRight,
    disabled = props.disabled,
    error = props.error,
    errorInLabel = props.errorInLabel,
    helpText = props.helpText,
    id = props.id,
    label = props.label,
    labelHidden = props.labelHidden,
    multiline = props.multiline,
    name = props.name,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    placeholder = props.placeholder,
    prefix = props.prefix,
    prefixClassname = props.prefixClassname,
    readOnly = props.readOnly,
    required = props.required,
    _props$resize = props.resize,
    resize = _props$resize === void 0 ? 'both' : _props$resize,
    _props$type = props.type,
    type = _props$type === void 0 ? 'text' : _props$type,
    suffix = props.suffix,
    suffixClassname = props.suffixClassname,
    style = props.style,
    optional = props.optional,
    rest = __rest(props, ["align", "autoFocus", "connectLeft", "connectRight", "disabled", "error", "errorInLabel", "helpText", "id", "label", "labelHidden", "multiline", "name", "onChange", "onFocus", "onBlur", "placeholder", "prefix", "prefixClassname", "readOnly", "required", "resize", "type", "suffix", "suffixClassname", "style", "optional"]);
  var componentProps = omit$1(rest);
  var systemProps = pick(rest, system$2.propNames);
  var _useResizeObserver = useResizeObserver(),
    _useResizeObserver2 = _slicedToArray$3(_useResizeObserver, 2),
    prefixRef = _useResizeObserver2[0],
    prefixEntry = _useResizeObserver2[1];
  var _useResizeObserver3 = useResizeObserver(),
    _useResizeObserver4 = _slicedToArray$3(_useResizeObserver3, 2),
    suffixRef = _useResizeObserver4[0],
    suffixEntry = _useResizeObserver4[1];
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray$3(_React$useState, 2),
    prefixPadding = _React$useState2[0],
    setPrefixPadding = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray$3(_React$useState3, 2),
    suffixPadding = _React$useState4[0],
    setSuffixPadding = _React$useState4[1];
  var _useInputDescribedBy = useInputDescribedBy({
      id: id,
      hasHelpText: !!helpText,
      hasError: !!error
    }),
    describedBy = _useInputDescribedBy.describedBy,
    errorId = _useInputDescribedBy.errorId,
    helpTextId = _useInputDescribedBy.helpTextId;
  React.useLayoutEffect(function () {
    if (prefix && prefixEntry.contentRect.width) {
      setPrefixPadding(roundToBaseline(prefixEntry.contentRect.width));
    }
  }, [prefix, prefixEntry]);
  React.useLayoutEffect(function () {
    if (suffix && suffixEntry.contentRect.width) {
      setSuffixPadding(roundToBaseline(suffixEntry.contentRect.width));
    }
  }, [suffix, suffixEntry]);
  var inputProps = Object.assign(Object.assign(Object.assign(Object.assign({}, componentProps), {
    as: multiline ? 'textarea' : 'input',
    height: multiline ? 'auto' : '2.5rem',
    lineHeight: multiline ? '300' : '2.5rem',
    py: multiline ? '300' : '0',
    hasError: !!error,
    name: name,
    id: id,
    type: type,
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    autoFocus: autoFocus,
    placeholder: placeholder,
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: onChange,
    pl: "calc(".concat(prefixPadding, "px + ").concat(tokens.spacing_400, ")"),
    pr: "calc(".concat(suffixPadding, "px + ").concat(tokens.spacing_400, ")"),
    textAlign: align
  }), describedBy), {
    style: Object.assign(Object.assign({
      cursor: disabled ? 'not-allowed' : 'auto'
    }, multiline ? {
      resize: resize
    } : {}), style)
  });
  return /*#__PURE__*/React.createElement(StyledWrapper, Object.assign({}, systemProps), label && /*#__PURE__*/React.createElement(Label$1, {
    id: id,
    labelHidden: labelHidden
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    pr: "200"
  }, label), error && errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId,
    wrapper: "span"
  }, error), optional && /*#__PURE__*/React.createElement(OptionalLabel, {
    "float": true
  })), /*#__PURE__*/React.createElement(Connect, {
    left: connectLeft,
    right: connectRight
  }, /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(PrefixOrSuffix, {
    content: prefix,
    className: prefixClassname,
    ref: prefixRef,
    left: "300"
  }), /*#__PURE__*/React.createElement(FieldBox, Object.assign({
    ref: userRef
  }, inputProps)), /*#__PURE__*/React.createElement(PrefixOrSuffix, {
    content: suffix,
    className: suffixClassname,
    ref: suffixRef,
    right: "300"
  }))), helpText && /*#__PURE__*/React.createElement(HelpText, {
    id: helpTextId
  }, helpText), error && !errorInLabel && /*#__PURE__*/React.createElement(ErrorLabel, {
    id: errorId
  }, error));
});
TextField.displayName = 'TextField';
function _templateObject2$3() {
  var data = _taggedTemplateLiteral$6(["\n  width: 24px;\n  height: 24px;\n\n  ", ":focus & {\n    ", "\n  }\n\n  ", ":focus:not(:focus-visible) &:after {\n    box-shadow: none;\n  }\n\n  ", ":focus-visible & {\n    ", "\n  }\n"]);
  _templateObject2$3 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$6() {
  var data = _taggedTemplateLiteral$6(["\n  ", "\n  position: relative;\n  cursor: pointer;\n  width: 100%;\n  outline: none;\n\n  svg circle {\n    transition: ", ";\n  }\n\n  svg [data-id='mb-tab-line'] {\n    stroke: ", ";\n  }\n\n  svg [data-id='mb-tab-outer'] {\n    fill: ", ";\n    stroke: ", ";\n  }\n\n  svg [data-id='mb-tab-inner'] {\n    fill: ", ";\n  }\n\n  &:hover {\n    svg [data-id='mb-tab-outer'] {\n      stroke: ", ";\n    }\n    svg [data-id='mb-tab-inner'] {\n      fill: ", ";\n    }\n  }\n\n  &:last-child {\n    svg line {\n      display: none;\n    }\n  }\n"]);
  _templateObject$6 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$6(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Container$1 = styled.button(_templateObject$6(), buttonReset, function (_ref) {
  var theme = _ref.theme;
  return theme.motion.duration.fast;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.gray[700];
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.colors.white;
}, function (props) {
  return props.$selected ? props.theme.colors.blue[700] : props.theme.colors.gray[700];
}, function (props) {
  return props.$selected ? props.theme.colors.blue[700] : 'transparent';
}, function (_ref4) {
  var theme = _ref4.theme,
    $selected = _ref4.$selected;
  return !$selected ? theme.colors.gray[800] : theme.colors.blue[700];
}, function (_ref5) {
  var theme = _ref5.theme,
    $selected = _ref5.$selected;
  return !$selected ? theme.colors.gray[400] : theme.colors.blue[700];
});
var FocusSvg = styled.div(_templateObject2$3(), Container$1, focusOutline({
  modifier: '&',
  offset: '1px',
  radius: '50%'
}), Container$1, Container$1, focusOutline({
  modifier: '&',
  offset: '1px',
  radius: '50%'
}));
var Tab = /*#__PURE__*/React.forwardRef(function Tab(props, userRef) {
  var _props$as = props.as,
    as = _props$as === void 0 ? 'button' : _props$as,
    ariaControls = props['aria-controls'],
    children = props.children,
    dataId = props['data-id'],
    id = props.id,
    handleParentSelect = props.handleParentSelect,
    onClick = props.onClick,
    selected = props.selected,
    rest = __rest(props, ["as", 'aria-controls', "children", 'data-id', "id", "handleParentSelect", "onClick", "selected"]);
  function handleClick(e) {
    if (onClick) {
      onClick(e);
    }
    handleParentSelect();
  }
  return /*#__PURE__*/React.createElement(Container$1, Object.assign({
    "aria-controls": ariaControls,
    "aria-selected": selected,
    as: as,
    "data-id": dataId,
    id: id,
    onClick: handleClick,
    ref: userRef,
    role: "tab",
    "$selected": selected,
    tabIndex: selected ? 0 : -1,
    type: "button"
  }, rest), /*#__PURE__*/React.createElement(Box, {
    display: "grid",
    gridTemplateColumns: "30px 1fr",
    gridGap: "200"
  }, /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    height: "100%",
    gridColumn: "0/1"
  }, /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    width: "2px",
    height: "100%",
    top: "2px",
    left: "35%",
    bg: "red",
    zIndex: "below",
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement("svg", {
    width: "5px",
    height: "200px"
  }, /*#__PURE__*/React.createElement("line", {
    "data-id": "mb-tab-line",
    x1: "1",
    y1: "0",
    x2: "1",
    y2: "100",
    stroke: "black",
    strokeWidth: "1",
    strokeDasharray: "3 2"
  }))), /*#__PURE__*/React.createElement(FocusSvg, null, /*#__PURE__*/React.createElement("svg", {
    width: "25px",
    height: "25px"
  }, /*#__PURE__*/React.createElement("circle", {
    "data-id": "mb-tab-outer",
    cx: "12",
    cy: "12",
    r: "11",
    fill: "transparent",
    strokeWidth: "1"
  }), /*#__PURE__*/React.createElement("circle", {
    "data-id": "mb-tab-inner",
    cx: "12",
    cy: "12",
    r: "5"
  })))), /*#__PURE__*/React.createElement(Box, {
    textAlign: "left",
    minHeight: "800",
    pb: "300"
  }, children)));
});
function _slicedToArray$2(arr, i) {
  return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$2(arr, i) || _nonIterableRest$2();
}
function _nonIterableRest$2() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$2(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$2(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen);
}
function _arrayLikeToArray$2(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$2(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$2(arr) {
  if (Array.isArray(arr)) return arr;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any

function useTabConstructor(_ref) {
  var tabs = _ref.tabs,
    initialIndex = _ref.initialIndex;
  var tabRefs = React.useRef([]);
  var focusRef = React.useRef();
  var _React$useState = React.useState(initialIndex || 0),
    _React$useState2 = _slicedToArray$2(_React$useState, 2),
    selectedIndex = _React$useState2[0],
    setSelectedIndex = _React$useState2[1]; // Preps array of tab item refs

  React.useEffect(function () {
    tabRefs.current = new Array(tabs.length);
    return function () {
      tabRefs.current = [];
    };
  }, [tabs]);
  var onFocusContainerKeyDown = function onFocusContainerKeyDown(e) {
    var isWithin = focusRef.current && focusRef.current.contains(e.currentTarget);
    if (!isWithin) {
      return;
    }
    onKey('arrowDown', function () {
      e.preventDefault();
      if (selectedIndex === tabs.length - 1) {
        setSelectedIndex(0);
      } else {
        setSelectedIndex(selectedIndex + 1);
      }
    })(e);
    onKey('arrowUp', function () {
      e.preventDefault();
      if (selectedIndex === 0) {
        setSelectedIndex(tabs.length - 1);
      } else {
        setSelectedIndex(selectedIndex - 1);
      }
    })(e);
    onKeys(['home', 'pageDown'], function () {
      e.preventDefault();
      setSelectedIndex(0);
    })(e);
    onKeys(['end', 'pageUp'], function () {
      e.preventDefault();
      setSelectedIndex(tabs.length - 1);
    })(e);
  }; // Focuses on tab items when focused index changes
  // Optionally calls onSelect

  React.useLayoutEffect(function () {
    // Does not continue when focus is reset, when moving focus outside the container
    if (focusRef.current && !focusRef.current.contains(document.activeElement)) {
      return;
    }
    if (tabRefs.current[selectedIndex]) {
      tabRefs.current[selectedIndex].focus();
    }
  }, [selectedIndex]);
  var constructedItems = React.useMemo(function () {
    return tabs.map(function (tab, i) {
      return Object.assign(Object.assign({}, tab), {
        ref: function ref(n) {
          return tabRefs.current[i] = n;
        },
        props: Object.assign(Object.assign({}, tab.props), {
          handleParentSelect: function handleParentSelect() {
            return setSelectedIndex(i);
          },
          selected: selectedIndex === i
        })
      });
    });
  }, [tabs, selectedIndex]);
  return {
    tabs: constructedItems,
    focusContainerProps: {
      onKeyDown: onFocusContainerKeyDown,
      ref: focusRef
    }
  };
}
function _templateObject$5() {
  var data = _taggedTemplateLiteral$5(["\n  ", "\n"]);
  _templateObject$5 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$5(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var Container = styled.div(_templateObject$5(), margin);
var TimelineTabs = /*#__PURE__*/React.forwardRef(function TimelineTabs(props, userRef) {
  var children = props.children,
    id = props.id,
    initialIndex = props.initialIndex,
    rest = __rest(props, ["children", "id", "initialIndex"]);
  var systemProps = pick(rest, margin.propNames);
  var tabs = React.Children.toArray(children);
  var _useTabConstructor = useTabConstructor({
      initialIndex: initialIndex,
      tabs: tabs
    }),
    constructedItems = _useTabConstructor.tabs,
    focusContainerProps = _useTabConstructor.focusContainerProps;
  return /*#__PURE__*/React.createElement(Container, Object.assign({
    ref: userRef,
    "data-id": rest['data-id'],
    id: id
  }, systemProps), /*#__PURE__*/React.createElement("div", Object.assign({
    role: "tablist",
    "aria-orientation": "vertical"
  }, focusContainerProps), constructedItems));
});
TimelineTabs.Tab = Tab;
function _templateObject2$2() {
  var data = _taggedTemplateLiteral$4(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n\n  background: ", ";\n  border-radius: ", ";\n  transition: ", ";\n"]);
  _templateObject2$2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$4() {
  var data = _taggedTemplateLiteral$4(["\n  pointer-events: none;\n  position: absolute;\n  top: 1px;\n  left: 1px;\n  bottom: 1px;\n  width: calc(", " / 2);\n\n  background: ", ";\n  transition: transform ", " ", ";\n  transform: translate(", ", 0);\n\n  border-radius: ", ";\n  box-shadow: ", ";\n"]);
  _templateObject$4 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$4(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var toggleWidth = '2.25rem';
var toggle = function toggle(props) {
  return "\n  display: inline-block;\n  position: relative;\n  height: 1.25rem;\n  width: ".concat(toggleWidth, ";\n\n  opacity: ").concat(props.disabled ? '0.6' : '1', ";\n  cursor: ").concat(props.disabled ? 'not-allowed' : 'pointer', ";\n");
};
var StyledIndicator = styled('span')(_templateObject$4(), toggleWidth, tokens.color_white, tokens.motionDuration_fast, tokens.motionEase_in_out, function (props) {
  return props.$checked ? tokens.spacing_400 : '0';
}, tokens.borderRadius_circle, tokens.boxShadow_100);
var StyledOutline = styled('span')(_templateObject2$2(), tokens.color_gray_700, tokens.borderRadius_pill, tokens.motionDuration_fast);
var input = function input() {
  return "\n  &:focus ~ ".concat(StyledOutline, ",\n  &:active ~ ").concat(StyledOutline, " {\n    box-shadow: 0 0 0 2px white, 0 0 0 4px ").concat(tokens.color_blue_600, ";\n  }\n\n  &:checked {\n    & ~ ").concat(StyledIndicator, " {\n      transform: translate(").concat(tokens.spacing_400, ", 0);\n    }\n\n    & ~ ").concat(StyledOutline, " {\n      background: ").concat(tokens.color_green_800, ";\n    }\n  }\n");
};
function _templateObject2$1() {
  var data = _taggedTemplateLiteral$3(["\n  ", "\n  ", "\n"]);
  _templateObject2$1 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$3() {
  var data = _taggedTemplateLiteral$3(["\n  ", "\n  ", "\n"]);
  _templateObject$3 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$3(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var StyledToggle = styled('label')(_templateObject$3(), toggle, margin);
var StyledInput = styled('input')(_templateObject2$1(), visuallyHidden, input);
function Toggle(props) {
  var id = props.id,
    value = props.value,
    checked = props.checked,
    defaultChecked = props.defaultChecked,
    disabled = props.disabled,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    label = props.label,
    required = props.required,
    ariaDescribedBy = props['aria-describedby'],
    dataId = props['data-id'],
    rest = __rest(props, ["id", "value", "checked", "defaultChecked", "disabled", "onChange", "onFocus", "onBlur", "label", "required", 'aria-describedby', 'data-id']);
  var systemProps = pick$1(rest);
  var componentProps = omit(rest, margin.propNames);
  return /*#__PURE__*/React.createElement(StyledToggle, Object.assign({
    htmlFor: id,
    disabled: disabled
  }, systemProps), label && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, label), /*#__PURE__*/React.createElement(StyledInput, Object.assign({
    id: id,
    value: value,
    defaultChecked: defaultChecked,
    checked: checked,
    disabled: disabled,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    required: required,
    type: "checkbox",
    "aria-describedby": ariaDescribedBy,
    "data-id": dataId
  }, componentProps)), /*#__PURE__*/React.createElement(StyledOutline, null), /*#__PURE__*/React.createElement(StyledIndicator, {
    "$checked": checked
  }));
}
Toggle.displayName = 'Toggle';

/* eslint-disable max-lines */
// https://styled-system.com/table

var theme = {
  // Example: <Box bg="blue.100" color={gray.1000} />
  colors: {
    gray: {
      100: tokens.color_gray_100,
      200: tokens.color_gray_200,
      300: tokens.color_gray_300,
      400: tokens.color_gray_400,
      500: tokens.color_gray_500,
      600: tokens.color_gray_600,
      700: tokens.color_gray_700,
      800: tokens.color_gray_800,
      900: tokens.color_gray_900,
      1000: tokens.color_gray_1000
    },
    blue: {
      100: tokens.color_blue_100,
      200: tokens.color_blue_200,
      300: tokens.color_blue_300,
      400: tokens.color_blue_400,
      500: tokens.color_blue_500,
      600: tokens.color_blue_600,
      700: tokens.color_blue_700,
      800: tokens.color_blue_800,
      900: tokens.color_blue_900,
      1000: tokens.color_blue_1000
    },
    red: {
      100: tokens.color_red_100,
      200: tokens.color_red_200,
      300: tokens.color_red_300,
      400: tokens.color_red_400,
      500: tokens.color_red_500,
      600: tokens.color_red_600,
      700: tokens.color_red_700,
      800: tokens.color_red_800,
      900: tokens.color_red_900,
      1000: tokens.color_red_1000
    },
    yellow: {
      100: tokens.color_yellow_100,
      200: tokens.color_yellow_200,
      300: tokens.color_yellow_300,
      400: tokens.color_yellow_400,
      500: tokens.color_yellow_500,
      600: tokens.color_yellow_600,
      700: tokens.color_yellow_700,
      800: tokens.color_yellow_800,
      900: tokens.color_yellow_900,
      1000: tokens.color_yellow_1000
    },
    green: {
      100: tokens.color_green_100,
      200: tokens.color_green_200,
      300: tokens.color_green_300,
      400: tokens.color_green_400,
      500: tokens.color_green_500,
      600: tokens.color_green_600,
      700: tokens.color_green_700,
      800: tokens.color_green_800,
      900: tokens.color_green_900,
      1000: tokens.color_green_1000
    },
    teal: {
      100: tokens.color_teal_100,
      200: tokens.color_teal_200,
      300: tokens.color_teal_300,
      400: tokens.color_teal_400,
      500: tokens.color_teal_500,
      600: tokens.color_teal_600,
      700: tokens.color_teal_700,
      800: tokens.color_teal_800,
      900: tokens.color_teal_900,
      1000: tokens.color_teal_1000
    },
    purple: {
      100: tokens.color_purple_100,
      200: tokens.color_purple_200,
      300: tokens.color_purple_300,
      400: tokens.color_purple_400,
      500: tokens.color_purple_500,
      600: tokens.color_purple_600,
      700: tokens.color_purple_700,
      800: tokens.color_purple_800,
      900: tokens.color_purple_900,
      1000: tokens.color_purple_1000
    },
    magenta: {
      100: tokens.color_magenta_100,
      200: tokens.color_magenta_200,
      300: tokens.color_magenta_300,
      400: tokens.color_magenta_400,
      500: tokens.color_magenta_500,
      600: tokens.color_magenta_600,
      700: tokens.color_magenta_700,
      800: tokens.color_magenta_800,
      900: tokens.color_magenta_900,
      1000: tokens.color_magenta_1000
    },
    brand: {
      orange: tokens.color_brand_orange,
      gray: tokens.color_brand_gray,
      blue: tokens.color_brand_blue
    },
    white: tokens.color_white
  },
  // Example: <Box marginBottom="400" padding={400} />
  space: {
    0: tokens.spacing_0,
    100: tokens.spacing_100,
    200: tokens.spacing_200,
    300: tokens.spacing_300,
    400: tokens.spacing_400,
    450: tokens.spacing_450,
    500: tokens.spacing_500,
    550: tokens.spacing_550,
    600: tokens.spacing_600,
    650: tokens.spacing_650,
    700: tokens.spacing_700,
    750: tokens.spacing_750,
    800: tokens.spacing_800
  },
  // Example: <Box width="1200" height={1/2} />
  sizes: {
    0: tokens.sizing_0,
    100: tokens.sizing_100,
    200: tokens.sizing_200,
    300: tokens.sizing_300,
    400: tokens.sizing_400,
    450: tokens.sizing_450,
    500: tokens.sizing_500,
    550: tokens.sizing_550,
    600: tokens.sizing_600,
    650: tokens.sizing_650,
    700: tokens.sizing_700,
    750: tokens.sizing_750,
    800: tokens.sizing_800,
    900: tokens.sizing_900,
    1000: tokens.sizing_1000,
    1100: tokens.sizing_1100,
    1150: tokens.sizing_1150,
    1200: tokens.sizing_1200,
    1300: tokens.sizing_1300,
    1350: tokens.sizing_1350,
    1400: tokens.sizing_1400,
    full: '100%'
  },
  // Example: <Box borderRadius="pill" />
  radii: {
    0: tokens.borderRadius_0,
    100: tokens.borderRadius_100,
    200: tokens.borderRadius_200,
    pill: tokens.borderRadius_pill,
    circle: tokens.borderRadius_circle
  },
  // Example: <Box border={500} />
  borders: {
    300: "".concat(tokens.borderWidth_100, " solid ").concat(tokens.color_gray_300),
    400: "".concat(tokens.borderWidth_100, " solid ").concat(tokens.color_gray_400),
    500: "".concat(tokens.borderWidth_200, " solid ").concat(tokens.color_gray_300)
  },
  // Example: <Box borderWidth={100} />
  borderWidths: {
    0: tokens.borderWidth_0,
    100: tokens.borderWidth_100,
    200: tokens.borderWidth_200
  },
  // Example: <Box boxShadow={200} />
  shadows: {
    0: tokens.boxShadow_0,
    100: tokens.boxShadow_100,
    200: tokens.boxShadow_200,
    300: tokens.boxShadow_300,
    400: tokens.boxShadow_400
  },
  // Example:
  // <Box
  //   fontFamily="monospace"
  //   fontSize="100"
  //   lineHeight="100"
  //   fontWeight="medium"
  // />
  fonts: {
    sans: tokens.fontFamily_sans,
    monospace: tokens.fontFamily_monospace
  },
  fontSizes: {
    50: tokens.fontSize_50,
    100: tokens.fontSize_100,
    200: tokens.fontSize_200,
    300: tokens.fontSize_300,
    400: tokens.fontSize_400,
    500: tokens.fontSize_500,
    600: tokens.fontSize_600,
    700: tokens.fontSize_700,
    800: tokens.fontSize_800
  },
  lineHeights: {
    100: tokens.lineHeight_100,
    200: tokens.lineHeight_200,
    300: tokens.lineHeight_300,
    400: tokens.lineHeight_400,
    500: tokens.lineHeight_500,
    600: tokens.lineHeight_600,
    700: tokens.lineHeight_700,
    800: tokens.lineHeight_800
  },
  fontWeights: {
    light: tokens.fontWeight_light,
    normal: tokens.fontWeight_normal,
    medium: tokens.fontWeight_medium,
    semibold: tokens.fontWeight_semibold
  },
  breakpoints: [tokens.mediaQuery_xs, tokens.mediaQuery_sm, tokens.mediaQuery_md, tokens.mediaQuery_lg, tokens.mediaQuery_xl],
  zIndices: {
    below: tokens.zIndex_below,
    "default": tokens.zIndex_default,
    above: tokens.zIndex_above,
    overlay: tokens.zIndex_overlay
  },
  motion: {
    duration: {
      fast: tokens.motionDuration_fast,
      medium: tokens.motionDuration_medium,
      slow: tokens.motionDuration_slow
    },
    ease: {
      "in": tokens.motionEase_in,
      out: tokens.motionEase_out,
      inOut: tokens.motionEase_in_out
    }
  }
};
var global = "\n  html {\n    position: relative;\n    box-sizing: border-box;\n    font-size: ".concat(tokens.fontSize_root, ";\n  }\n\n  body {\n    min-height: 100%;\n    margin: 0;\n    padding: 0;\n\n    color: ").concat(tokens.color_gray_900, ";\n    font-family: ").concat(tokens.fontFamily_sans, ";\n    font-size: ").concat(tokens.fontSize_300, ";\n    line-height: ").concat(tokens.lineHeight_300, ";\n    font-weight: ").concat(tokens.fontWeight_normal, ";\n  }\n\n  * {\n    box-sizing: inherit;\n  }\n\n  h1 {\n    font-size: ").concat(tokens.fontSize_700, ";\n    line-height: ").concat(tokens.lineHeight_700, ";\n  }\n\n  h2 {\n    font-size: ").concat(tokens.fontSize_600, ";\n    line-height: ").concat(tokens.lineHeight_600, ";\n  }\n\n  h3 {\n    font-size: ").concat(tokens.fontSize_500, ";\n    line-height: ").concat(tokens.lineHeight_500, ";\n  }\n\n  h4 {\n    font-size: ").concat(tokens.fontSize_400, ";\n    line-height: ").concat(tokens.lineHeight_400, ";\n  }\n\n  h5 {\n    font-size: ").concat(tokens.fontSize_300, ";\n    line-height: ").concat(tokens.lineHeight_300, ";\n  }\n\n  h6 {\n    font-size: ").concat(tokens.fontSize_200, ";\n    line-height: ").concat(tokens.lineHeight_200, ";\n  }\n\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6,\n  strong,\n  b {\n    font-weight: ").concat(tokens.fontWeight_semibold, ";\n  }\n\n  small {\n    font-size: ").concat(tokens.fontSize_200, ";\n    line-height: ").concat(tokens.lineHeight_200, ";\n  }\n\n  p,\n  ul,\n  ol {\n    font-size: ").concat(tokens.fontSize_300, ";\n    line-height: ").concat(tokens.lineHeight_300, ";\n  }\n\n  code,\n  pre {\n    font-family: ").concat(tokens.fontFamily_monospace, ";\n    font-size: ").concat(tokens.fontSize_100, ";\n    line-height: ").concat(tokens.lineHeight_100, ";\n  }\n\n  ul,\n  ol,\n  dd,\n  figure,\n  pre,\n  table,\n  fieldset,\n  hr {\n    margin-top: 0;\n  }\n\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6,\n  p,\n  ul,\n  ol,\n  pre,\n  table,\n  fieldset {\n    margin: 0;\n  }\n\n  a,\n  a:visited {\n    color: ").concat(tokens.color_blue_700, ";\n    text-decoration: underline;\n    cursor: pointer;\n  }\n  \n  a:hover,\n  a:active,\n  a:focus {\n    color: ").concat(tokens.color_blue_800, "    \n  }\n\n  ul,\n  ol {\n    padding-left: ").concat(tokens.spacing_500, ";\n  }\n\n  hr {\n    margin: ").concat(tokens.spacing_500, " 0;\n    background: ").concat(tokens.color_gray_400, ";\n    border: none;\n    height: 1px;\n  }\n\n  ::selection {\n    color: ").concat(tokens.color_gray_1000, ";\n    background: ").concat(tokens.color_blue_300, ";\n    text-shadow: none;\n  }\n\n  [tabindex=\"-1\"]:focus {\n    outline: none;\n  }\n");
function _templateObject$2() {
  var data = _taggedTemplateLiteral$2(["\n  ", "\n  ", "\n"]);
  _templateObject$2 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$2(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var GlobalStyle = createGlobalStyle(_templateObject$2(), normalize, global);
/**
 * See https://styled-components.com/docs/api#stylesheetmanager
 * Target provides an alternate DOM node to inject styles info
 *
 * Example:
 * In your app's <head>:
 * <style id="styled-components-target"></style>
 *
 * When rendering ThemeProvider:
 * <ThemeProvider target={document.getElementById('styled-components-target')} />
 * */

function Manager(_ref) {
  var target = _ref.target,
    children = _ref.children;
  if (!target) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  return /*#__PURE__*/React.createElement(StyleSheetManager, {
    target: target
  }, children);
}
Manager.displayName = 'MatchboxStyleSheetManager';
/**
 * Provides context for styled-system
 *
 * - https://www.styled-components.com/docs/advanced#theming
 * - https://styled-system.com/theme-specification
 *
 * @prop theme Overrides matchbox's theme
 */

function Theme(props) {
  return /*#__PURE__*/React.createElement(Manager, {
    target: props.target
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: Object.assign(Object.assign({}, theme), props.theme)
  }, !props.skipGlobalStyles && /*#__PURE__*/React.createElement(GlobalStyle, null), props.children));
}
Theme.displayName = 'MatchboxThemeProvider';
function _slicedToArray$1(arr, i) {
  return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1();
}
function _nonIterableRest$1() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$1(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
function _arrayLikeToArray$1(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit$1(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles$1(arr) {
  if (Array.isArray(arr)) return arr;
}
var defaultPosition = {
  top: 0,
  left: 0,
  width: 0,
  height: 0
};
var defaultDirection = {
  top: null,
  bottom: null,
  left: null,
  right: null
};
function TooltipOverlay(props) {
  var _React$useState = React.useState(defaultPosition),
    _React$useState2 = _slicedToArray$1(_React$useState, 2),
    position = _React$useState2[0],
    setPosition = _React$useState2[1];
  var _React$useState3 = React.useState(defaultDirection),
    _React$useState4 = _slicedToArray$1(_React$useState3, 2),
    preferredDirection = _React$useState4[0],
    setPreferredDirection = _React$useState4[1];
  var activatorRef = React.useRef(null);
  var renderA11yContent = props.renderA11yContent,
    renderTooltip = props.renderTooltip,
    renderActivator = props.renderActivator,
    hideTooltip = props.hideTooltip,
    visible = props.visible,
    portalId = props.portalId;
  function handleMeasurement() {
    if (activatorRef.current && visible) {
      setPosition(getPositionFor(activatorRef.current));
      setPreferredDirection(getPreferredDirectionFor(activatorRef.current));
    }
  } // Tooltips are fixed to the window and do not reposition on scroll
  // This hides them since scrolling does not automatically trigger `mouseOut`

  function handleScroll() {
    if (visible) {
      hideTooltip();
    }
  }
  React.useLayoutEffect(function () {
    handleMeasurement();
  }, [renderTooltip, renderActivator, activatorRef, visible]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WindowEvent, {
    event: "scroll",
    handler: handleScroll
  }), renderActivator({
    activatorRef: activatorRef
  }), renderA11yContent(), /*#__PURE__*/React.createElement(Portal, {
    containerId: portalId
  }, /*#__PURE__*/React.createElement(Box, {
    "aria-hidden": "true",
    position: "absolute",
    top: "0",
    left: "0",
    style: {
      pointerEvents: 'none'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    top: "".concat(position.top, "px"),
    left: "".concat(position.left, "px"),
    height: "".concat(position.height, "px"),
    width: "".concat(position.width, "px"),
    zIndex: "overlay"
  }, renderTooltip({
    preferredDirection: preferredDirection
  })))));
}
TooltipOverlay.displayName = 'TooltipOverlay';
var visibility = function visibility(props) {
  var $visible = props.$visible,
    $state = props.$state;
  return "\n      pointer-events: none;\n      opacity: ".concat($visible && $state === 'entered' ? '1' : '0', ";\n      visibility: ").concat($visible && $state === 'entered' ? 'visible' : 'hidden', ";\n      transform: scale(").concat($visible && $state === 'entered' ? '1' : '0.96', ");\n      transition:  ").concat(tokens.motionDuration_fast, " ").concat($visible && $state === 'entered' ? tokens.motionEase_out : tokens.motionEase_in, ";");
};
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _templateObject2() {
  var data = _taggedTemplateLiteral$1(["\n  ", "\n"]);
  _templateObject2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1() {
  var data = _taggedTemplateLiteral$1(["\n  ", "\n"]);
  _templateObject$1 = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral$1(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system$1 = compose(border, color, layout, padding, typography, shadow);
var StyledTooltipContainer = styled(Box)(_templateObject$1(), visibility);
var StyledContent = styled('div')(_templateObject2(), system$1);
function Tooltip(props) {
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    show = _React$useState2[0],
    setshow = _React$useState2[1];
  function handleShow() {
    setshow(true);
  }
  function handleHide() {
    setshow(false);
  }
  function renderA11yContent() {
    return /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement("span", {
      role: "tooltip",
      "aria-hidden": !show,
      id: props.id
    }, props.content));
  }
  function renderTooltip(_ref) {
    var preferredDirection = _ref.preferredDirection;
    var content = props.content,
      dark = props.dark,
      top = props.top,
      left = props.left;
    props.right;
    props.bottom;
    var _props$forcePosition = props.forcePosition,
      forcePosition = _props$forcePosition === void 0 ? false : _props$forcePosition,
      disabled = props.disabled,
      _props$width = props.width,
      width = _props$width === void 0 ? '13rem' : _props$width,
      rest = __rest(props, ["content", "dark", "top", "left", "right", "bottom", "forcePosition", "disabled", "width"]);
    var transitionRef = React.useRef();
    var systemProps = pick$1(rest);
    var positionTop = preferredDirection.top || top && !forcePosition;
    var positionLeft = preferredDirection.left || left && !forcePosition;
    var contentStyles = Object.assign(Object.assign({}, systemProps), dark ? {
      bg: 'gray.1000'
    } : {});
    return /*#__PURE__*/React.createElement(Transition, {
      mountOnEnter: true,
      unmountOnExit: true,
      "in": show,
      timeout: {
        enter: secondsToMS(tokens.motionDuration_medium),
        exit: 0
      },
      nodeRef: transitionRef
    }, function (state) {
      return /*#__PURE__*/React.createElement(StyledTooltipContainer, {
        display: "block",
        position: "absolute",
        top: positionTop ? 'auto' : '100%',
        bottom: positionTop ? '100%' : 'auto',
        left: positionLeft ? 'auto' : '0',
        right: positionLeft ? '0' : 'auto',
        // Tooltip gap to activator
        mt: positionTop ? '0' : '100',
        mb: positionTop ? '100' : '0',
        "$visible": !disabled && show,
        "$state": state,
        ref: transitionRef
      }, /*#__PURE__*/React.createElement(StyledContent, Object.assign({
        position: "relative",
        fontSize: "100",
        lineHeight: "100",
        p: "200",
        borderRadius: "100",
        bg: "gray.1000",
        color: "white",
        textAlign: "left",
        width: width,
        zIndex: "default"
      }, contentStyles), /*#__PURE__*/React.createElement(Box, {
        overflow: "hidden",
        maxHeight: "11rem"
      }, content)));
    });
  }
  function renderActivator(_ref2) {
    var activatorRef = _ref2.activatorRef;
    var _props$as = props.as,
      as = _props$as === void 0 ? 'span' : _props$as;
    return /*#__PURE__*/React.createElement(Box, {
      as: as,
      // Inline block is required to measure and set height
      display: as === 'span' ? 'inline-block' : null,
      onFocus: handleShow,
      onBlur: handleHide,
      onMouseOver: handleShow,
      onMouseOut: handleHide,
      ref: activatorRef
    }, props.children);
  }
  return /*#__PURE__*/React.createElement(TooltipOverlay, {
    hideTooltip: handleHide,
    portalId: props.portalId,
    renderA11yContent: renderA11yContent,
    renderTooltip: renderTooltip,
    renderActivator: renderActivator,
    visible: !props.disabled && show
  });
}
Tooltip.displayName = 'Tooltip';
var Source = /*#__PURE__*/React.forwardRef(function Source(props, userRef) {
  var src = props.src,
    type = props.type,
    className = props.className;
  return /*#__PURE__*/React.createElement("source", {
    className: className,
    src: src,
    ref: userRef,
    type: type
  });
});
Source.displayName = 'Video.Source';
function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  ", "\n"]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}
function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var system = compose(layout, margin);
var StyledFigure = styled.figure(_templateObject(), system);
var Video = /*#__PURE__*/React.forwardRef(function Video(props, userRef) {
  var _props$autoPlay = props.autoPlay,
    autoPlay = _props$autoPlay === void 0 ? true : _props$autoPlay,
    role = props.role,
    children = props.children,
    _props$controls = props.controls,
    controls = _props$controls === void 0 ? false : _props$controls,
    _props$loop = props.loop,
    loop = _props$loop === void 0 ? false : _props$loop,
    _props$muted = props.muted,
    muted = _props$muted === void 0 ? true : _props$muted,
    rest = __rest(props, ["autoPlay", "role", "children", "controls", "loop", "muted"]);
  var systemProps = pick(rest, system.propNames);
  return /*#__PURE__*/React.createElement(StyledFigure, Object.assign({
    role: role,
    ref: userRef
  }, systemProps), /*#__PURE__*/React.createElement("video", {
    width: "100%",
    autoPlay: autoPlay,
    controls: controls,
    loop: loop,
    muted: muted
  }, children));
});
Video.displayName = 'Video';
Video.Source = Source;
var index = {
  buttonReset: buttonReset,
  focusOutline: focusOutline,
  visuallyHidden: visuallyHidden
};
export { ActionList, Animator, Badge, Banner, Box, Breadcrumb$1 as Breadcrumb, Button$1 as Button, Checkbox, CheckboxCard, CodeBlock, Column$1 as Column, Columns, ComboBox, ComboBoxMenu, ComboBoxTextField, DatePicker, Drawer, EmptyState, ErrorLabel as Error, ErrorLabel, Expandable, Grid, HelpText, Inline, InlineCode, KeyboardKey, Label$1 as Label, LabelValue, Layout, ListBox, Modal, OptionalLabel, Page, Pager, Pagination, Panel, Picture, Popover, Portal, Progress, ProgressBar, QueryCard, Radio, RadioCard, ScreenReaderOnly, Select, Skeleton, Slider, Snackbar, Spinner, Stack, StyledButton$3 as StyledButton, StyledProgressBarInner, StyledProgressBarOuter, StyledSlider, Table, Tabs, Tag, Text, TextField, Theme as ThemeProvider, TimelineTabs, Toggle, Tooltip, UnstyledLink, Video, WindowEvent, Wrapper$4 as Wrapper, buttonFrom, buttonsFrom, getLoaderColor, linkFrom, index as styles, useBreakpoint, useCopyToClipboard, useDrawer, useInView, useModal, usePrefersColorScheme, usePrefersReducedMotion, useResizeObserver, useTabs, useWindowEvent };